import { useState, useEffect, useCallback, useRef } from 'react';
import SowResourcingTab from './SowResourcingTab';
import { useApp } from '../../context';
import api from '../../hooks/useApi';
import { parseRoles, canAccess } from '../../utils/rbac';
import { formatMoney, formatDate } from '../../utils/format';
import { fmtDateTimeLong, fmtDateOnly } from '../../utils/dates.js';
import { MILESTONE_STATUS_TRANSITIONS, ALL_MILESTONE_STATUSES } from '../../utils/milestoneStatus';
import { DEFAULT_MILESTONE_SORT, sortMilestones, nextMilestoneSort } from '../../utils/milestoneSort.js';
import { isCoupaMilestone } from '../../utils/coupa.js';
import { milestoneTableClipboard } from '../../utils/milestoneTableCopy.js';
import { contactOptions, contactOptionText } from '../../utils/clientContacts.js';
import NonCommercialContactSelect from './NonCommercialContactSelect';
import Tag from '../shared/Tag';
import CopyButton from '../shared/CopyButton';
import Modal from '../shared/Modal';
import FileUpload from '../shared/FileUpload';
import DocumentLink from '../shared/DocumentLink';
import SharePointFiles from '../shared/SharePointFiles';
import ActionsPanel from '../shared/ActionsPanel';
import { useToast } from '../ui/Toast';
import { QuickAddHardwareRow, QuickAddResourceRow } from './QuickAddLines';
import ProjectPlanTab from './plan/ProjectPlanTab';
import SowHistoryTab from './SowHistoryTab';
import ProjectCostTable from './ProjectCostTable';
import { downloadCallOffExport } from './callOffExportSheet';
import ProductSupplyNote from '../procurement/ProductSupplyNote';
import SurveysPanel from '../delivery/SurveysPanel';

const BASE_TABS = ['milestones', 'tasks', 'resources', 'hardware', 'costs', 'plan', 'risks', 'history', 'commentary', 'documents', 'qa-review'];
const CCN_STATUS_LABELS = { draft: 'Draft', pending_approval: 'Pending Approval', approved: 'Approved', rejected: 'Rejected' };
const CCN_STATUS_COLORS = { draft: 'bg-gray-100 text-gray-600', pending_approval: 'bg-amber-100 text-amber-700', approved: 'bg-emerald-100 text-emerald-700', rejected: 'bg-red-100 text-red-700' };
const IS_REAUG = (type) => ['Resource Augmentation', 'ReAug'].includes(type);


// ─── SharePoint sync ─────────────────────────────────────────────────────────
//
// SOW documents live in two places: here, and the SOW's SharePoint folder under
// "01 Statement of Work", as laid out by the template folder. This panel shows whether the
// two agree and lets you close the gap in either direction.
//
// It never syncs on its own. Pushing writes real files into a shared,
// client-facing library, and pulling attaches a document to a client record;
// both are things somebody should choose to do.
function SharePointSyncPanel({ sowId, canEdit = false }) {
  const [state, setState]   = useState(null);
  const [busy, setBusy]     = useState(false);
  const [picked, setPicked] = useState([]);
  const [msg, setMsg]       = useState(null);

  const load = useCallback(async () => {
    setState(await api.get(`sows/${sowId}/sharepoint-sync`).catch(() => ({ error: 'Could not check SharePoint' })));
  }, [sowId]);
  useEffect(() => { load(); }, [load]);

  const pushAll = async () => {
    setBusy(true);
    try {
      const r = await api.post(`sows/${sowId}/sharepoint-sync/push`, {});
      // Copied and linked are different things and are counted separately: linking
      // moved no bytes, which is the point of distinguishing them.
      const parts = [];
      if (r.pushed) parts.push(`copied ${r.pushed}`);
      if (r.linked) parts.push(`linked ${r.linked} already there`);
      const did = parts.join(', ') || 'nothing to do';
      setMsg(r.failed?.length
        ? { kind: 'warn', text: `${did}. ${r.failed.length} failed: ${r.failed.map(f => f.error).join('; ')}` }
        : { kind: 'ok', text: `${did[0].toUpperCase()}${did.slice(1)}.` });
      await load();
    } catch (e) { setMsg({ kind: 'error', text: e.message || 'Copy failed' }); }
    finally { setBusy(false); }
  };

  const pullPicked = async () => {
    if (!picked.length) return;
    setBusy(true);
    try {
      const items = state.in_sharepoint_only.filter(f => picked.includes(f.id))
        .map(f => ({ drive_item_id: f.id, file_name: f.name, web_url: f.web_url, document_type: 'sow' }));
      const r = await api.post(`sows/${sowId}/sharepoint-sync/pull`, { items });
      setMsg({ kind: 'ok', text: `Attached ${r.pulled} file${r.pulled === 1 ? '' : 's'}.` });
      setPicked([]);
      await load();
    } catch (e) { setMsg({ kind: 'error', text: e.message || 'Pull failed' }); }
    finally { setBusy(false); }
  };

  if (!state) return null;

  // The server decides what state each document is in; this only renders it. The
  // panel used to derive "not synced" itself from "has no SharePoint item id", which
  // is why a document sitting in the folder under the same name appeared both as not
  // yet in SharePoint AND as in SharePoint but not here. Two rules for one question
  // is how a screen contradicts itself.
  const docs = state.documents || [];
  const notSynced = docs.filter(d => d.sharepoint_state === 'missing_in_sharepoint');
  const sameName  = docs.filter(d => d.sharepoint_state === 'same_name_not_linked');
  const broken    = docs.filter(d => d.sharepoint_state === 'link_broken');
  const spOnly = state.in_sharepoint_only || [];
  const inStep = !notSynced.length && !sameName.length && !broken.length && !spOnly.length && !state.error;

  return (
    <div className="mb-4 rounded-lg border border-gray-200 bg-white p-3">
      <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
        <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500">SharePoint folder</span>
        {state.folder_url && (
          <a href={state.folder_url} target="_blank" rel="noreferrer"
            className="text-[10px] font-semibold text-navy hover:text-gold">Open in SharePoint →</a>
        )}
      </div>

      {msg && (
        <div className={`mb-2 rounded px-2 py-1.5 text-[10px] ${
          msg.kind === 'ok' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200'
            : msg.kind === 'warn' ? 'bg-amber-50 text-amber-800 border border-amber-200'
            : 'bg-red-50 text-red-800 border border-red-200'}`}>{msg.text}</div>
      )}

      {state.error && <div className="text-[10px] text-red-700 mb-2">{state.error}</div>}
      {state.folder === false && <div className="text-[10px] text-gray-500">{state.message}</div>}

      {inStep && (
        <div className="text-[10px] text-emerald-700 font-semibold">
          ✓ In step. Every document here is in {state.subfolder || 'the SOW folder'}.
        </div>
      )}

      {/* Already in the folder under the same name — nothing to copy, just a link
          nobody recorded. Said before the missing list, because it is the larger and
          more reassuring half of what used to look like a mess. */}
      {sameName.length > 0 && (
        <div className="mb-2">
          <div className="text-[10px] font-semibold text-blue-800 mb-1">
            {sameName.length} document{sameName.length === 1 ? '' : 's'} already in SharePoint, not linked yet
          </div>
          <ul className="text-[10px] text-gray-600 mb-1.5 space-y-0.5">
            {sameName.map(d => (
              <li key={d.id}>
                {d.title}
                <span className="text-gray-400">
                  {d.sharepoint_match?.by === 'content' ? ' — identical to ' : ' — matches '}
                  {d.sharepoint_match?.path ? `${d.sharepoint_match.path}/` : ''}{d.sharepoint_match?.name}
                </span>
                {d.sharepoint_size_differs && (
                  <span className="text-amber-700"> · SharePoint holds a different revision</span>
                )}
              </li>
            ))}
          </ul>
          <p className="text-[10px] text-gray-500">
            Same file, on both sides, with no record connecting them. "Send to SharePoint" records the link instead
            of copying over what is already there.
          </p>
        </div>
      )}

      {/* A recorded link whose file is no longer in the folder. The document reads as
          synced and it is not: moved or deleted in SharePoint. */}
      {broken.length > 0 && (
        <div className="mb-2">
          <div className="text-[10px] font-semibold text-red-800 mb-1">
            {broken.length} document{broken.length === 1 ? '' : 's'} linked to a file that is no longer in the folder
          </div>
          <ul className="text-[10px] text-gray-600 mb-1.5 space-y-0.5">
            {broken.map(d => <li key={d.id}>{d.title}</li>)}
          </ul>
          <p className="text-[10px] text-gray-500">
            Moved or deleted in SharePoint. The copy here is unaffected; sending it again puts it back.
          </p>
        </div>
      )}

      {notSynced.length > 0 && (
        <div className="mb-2">
          <div className="text-[10px] font-semibold text-amber-800 mb-1">
            {notSynced.length} document{notSynced.length === 1 ? '' : 's'} not yet in SharePoint
          </div>
          <ul className="text-[10px] text-gray-600 mb-1.5 space-y-0.5">
            {notSynced.map(d => (
              <li key={d.id}>
                {d.title}
                {d.sharepoint_error && <span className="text-red-600"> — {d.sharepoint_error}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}

      {canEdit && (notSynced.length > 0 || sameName.length > 0 || broken.length > 0) && (
        <button onClick={pushAll} disabled={busy}
          className="px-2.5 py-1.5 mb-2 rounded border border-navy text-navy bg-white hover:bg-navy/5 text-[10px] font-semibold disabled:opacity-50">
          {busy ? 'Working…'
            : notSynced.length
              ? `Send ${notSynced.length} to SharePoint${sameName.length ? ` and link ${sameName.length}` : ''}`
              : `Link ${sameName.length} to the files already there`}
        </button>
      )}

      {spOnly.length > 0 && (
        <div className="mt-2 pt-2 border-t border-gray-100">
          <div className="text-[10px] font-semibold text-blue-800 mb-1">
            {spOnly.length} file{spOnly.length === 1 ? '' : 's'} in SharePoint but not here
          </div>
          <div className="space-y-1 mb-1.5">
            {spOnly.map(f => (
              <label key={f.id} className="flex items-center gap-2 text-[10px] text-gray-700 cursor-pointer">
                {canEdit && (
                <input type="checkbox" className="w-3.5 h-3.5" checked={picked.includes(f.id)}
                  onChange={() => setPicked(p => p.includes(f.id) ? p.filter(x => x !== f.id) : [...p, f.id])} />
                )}
                <span className="font-medium">{f.name}</span>
                {f.last_modified && <span className="text-gray-400">{String(f.last_modified).slice(0, 10)}</span>}
              </label>
            ))}
          </div>
          {canEdit && (
          <button onClick={pullPicked} disabled={busy || !picked.length}
            className="px-2.5 py-1.5 rounded border border-navy text-navy bg-white hover:bg-navy/5 text-[10px] font-semibold disabled:opacity-50">
            Attach selected
          </button>
          )}
          <p className="text-[10px] text-gray-500 mt-1">
            A file whose contents already match a document here is linked to it rather than stored twice.
          </p>
        </div>
      )}
    </div>
  );
}

export default function SOWDetailPage() {
  const { toast } = useToast();
  const { detailId, detailSub, navigate, currentUser } = useApp();
  const [sow, setSow]               = useState(null);
  const [milestones, setMilestones] = useState([]);
  const [commentary, setCommentary] = useState([]);
  const [repeatableTasks, setRepeatableTasks] = useState([]);
  const [people, setPeople]         = useState([]);
  const [documents, setDocuments]   = useState([]);
  const [tab, setTab]               = useState('milestones');
  // Deep-link target: an action id to open in the Comments & Actions tab.
  const targetActionId = (detailSub && detailSub.startsWith('action-')) ? detailSub.slice('action-'.length) : null;
  // Deep-link target: a milestone id (e.g. from an invoice-number search) to
  // open the Milestones tab and scroll to / flash that row.
  const targetMilestoneId = (detailSub && detailSub.startsWith('milestone-')) ? detailSub.slice('milestone-'.length) : null;
  const [highlightMs, setHighlightMs] = useState(null);
  // ReAug tab state
  const [dateHistory,        setDateHistory]        = useState(null); // null = not loaded, used for inline history modal
  const [milHistoryFor,      setMilHistoryFor]      = useState(null); // milestone object when showing inline history
  const [qaReview,           setQaReview]           = useState(null); // null = not loaded
  const [ccns,               setCcns]               = useState(null); // null = not loaded
  const [showAddCcn,         setShowAddCcn]         = useState(false);
  const [editCcn,            setEditCcn]            = useState(null);
  const [reaugInvoices,   setReaugInvoices]   = useState([]);
  const [reaugTimesheets, setReaugTimesheets] = useState([]);
  const [reaugResources,  setReaugResources]  = useState([]);
  const [reaugLoading,    setReaugLoading]    = useState(false);
  const [reaugSubTab,     setReaugSubTab]     = useState('invoices');
  const [editReaugInv,    setEditReaugInv]    = useState(null); // null | 'new' | obj
  const [editReaugTs,     setEditReaugTs]     = useState(null);
  const [newComment, setNewComment] = useState('');
  const [legacyHistory, setLegacyHistory] = useState([]);       // Zoho cutover weekly snapshots, read-only
  const [showLegacyHistory, setShowLegacyHistory] = useState(false);
  const [showAddMilestone, setShowAddMilestone] = useState(false);
  const [showAddDocument,  setShowAddDocument]  = useState(false);
  const [sowDocPrompt,     setSowDocPrompt]     = useState(null); // { title, version, issued_version, issued_date } after uploading a SOW doc
  const [sowDocSyncing,    setSowDocSyncing]    = useState(false);
  const [showAddHardware,     setShowAddHardware]     = useState(false);
  const [quickAddHardware,    setQuickAddHardware]    = useState(false);
  const [quickAddResources,   setQuickAddResources]   = useState(false);
  const [showAddPassThrough,  setShowAddPassThrough]  = useState(false);
  const [selectedHwLines,     setSelectedHwLines]     = useState(new Set());
  const [showCreatePO,        setShowCreatePO]        = useState(false);
  // Raising a PO needs the Purchase Orders page or hardware review (the server
  // refuses anybody else since pen test AD25996), so do not offer the button.
  const canRaisePo = canAccess('pos') || canAccess('hardware-requests:review');
  // SOW money (values, rates, costs, contractor pay) is the sow:see-financials
  // grant; the API leaves it out for anybody else (pen test AD26341, AD12/AD16),
  // so the tiles, columns and wholly commercial tabs are not offered either.
  // Hardware trade cost and margin need trade-cost:see on top, and are also
  // shown to whoever raises a PO from these lines (src/api/sowMoney.js).
  const seesMoney = canAccess('sow:see-financials');
  const seesTradeCost = canAccess('trade-cost:see') && (seesMoney || canRaisePo);
  const [poQuickView,         setPoQuickView]         = useState(null); // po_id | null
  const [showIssueModal,      setShowIssueModal]      = useState(false);
  // What a re-issue started from: the document just uploaded, and the version read off it.
  const [reissuePreset,       setReissuePreset]       = useState(null);
  const [hwItemsLine,      setHwItemsLine]      = useState(null); // hardware line obj | null — shows allocated items modal
  const [editingMarginId,  setEditingMarginId]  = useState(null);
  const [editingQtyId,     setEditingQtyId]     = useState(null);
  const [qtyEditVal,       setQtyEditVal]       = useState('');
  const [statusOverride,   setStatusOverride]   = useState(null); // { lineId, currentStatus, newStatus, reason }
  const [renewalsFor, setRenewalsFor] = useState(null); // hardware line obj | null — manage its service renewals
  const [marginEditVal,    setMarginEditVal]    = useState('');
  const [expandedBundleLines, setExpandedBundleLines] = useState(new Set());
  const [bundleLineItems,     setBundleLineItems]     = useState({});
  const [showAddResource,  setShowAddResource]  = useState(false);
  const [editResourceLine, setEditResourceLine] = useState(null); // resource line obj | null
  const [showEditSOW,      setShowEditSOW]      = useState(false);
  const [pmCascade,        setPmCascade]        = useState(null); // { newPmId, newPmName, eligible:[milestones] } after PM change on save
  const [pmCascadeSaving,  setPmCascadeSaving]  = useState(false);
  const [showCloseSow,     setShowCloseSow]     = useState(false);
  const [closeSowReason,   setCloseSowReason]   = useState('No longer required');
  const [showApplyTemplate, setShowApplyTemplate] = useState(false);
  const [showGenerateSOW,   setShowGenerateSOW]   = useState(false);
  const [generatingMiniSow, setGeneratingMiniSow] = useState(false);
  const [showPOReceived,   setShowPOReceived]   = useState(false);
  const [editMilestone,    setEditMilestone]    = useState(null);
  const [loading, setLoading]       = useState(true);
  const [purchases,        setPurchases]        = useState([]); // actual ad-hoc/supplier-invoice costs booked to this SOW
  const [titleCopied,      setTitleCopied]      = useState(false);
  const [tableCopied,      setTableCopied]      = useState(false);
  const [milestoneSort,    setMilestoneSort]    = useState(DEFAULT_MILESTONE_SORT);
  const [spFolderBusy,     setSpFolderBusy]     = useState(false);
  const [spUrl,            setSpUrl]            = useState(null);
  const [spChecked,        setSpChecked]        = useState(false);
  // Resource sections management
  const [sections, setSections]           = useState(['Delivery']);
  const [newSectionInput, setNewSectionInput] = useState('');
  const [editingSection, setEditingSection]   = useState(null); // old name being edited
  const [sectionEditValue, setSectionEditValue] = useState('');
  const [addResourceSection, setAddResourceSection] = useState('');
  const [resourceAddOverride, setResourceAddOverride] = useState(false);
  const [resourceAutoPopulating, setResourceAutoPopulating] = useState(false);

  const [docTypeMap, setDocTypeMap] = useState({});
  const [selectedDocIds, setSelectedDocIds] = useState([]); // up to 2 for AI compare
  const [showAiDiff,     setShowAiDiff]     = useState(false);
  const [showAiRedact,   setShowAiRedact]   = useState(null); // doc object | null


  const loadData = useCallback(() => {
    if (!detailId) return;
    Promise.all([
      api.get(`sows/${detailId}`),
      api.get('milestones', { sow_id: detailId }),
      api.get('commentary', { entity_type: 'sow', entity_id: detailId }),
      api.get('people'),
      api.get(`sows/${detailId}/documents`),
    ]).then(([s, m, c, p, d]) => {
      setSow(s); setMilestones(m); setCommentary(c); setPeople(p); setDocuments(d || []); setLoading(false);
      if (s && !['Active', 'Complete', 'Archived'].includes(s.status)) {
        setTab('tasks');
      }
    }).catch(() => setLoading(false));
  }, [detailId]);

  const loadRepeatableTasks = useCallback(() => {
    if (!detailId) return;
    api.get(`sows/${detailId}/repeatable-tasks`).then(setRepeatableTasks).catch(() => setRepeatableTasks([]));
  }, [detailId]);

  // Legacy Zoho cutover history — only populated for SOWs that existed before
  // go-live. Silently empty (button stays hidden) for every SOW created after.
  useEffect(() => {
    if (!detailId) return;
    api.get(`sows/${detailId}/legacy-history`).then(r => setLegacyHistory(r || [])).catch(() => setLegacyHistory([]));
  }, [detailId]);

  const loadDocuments = useCallback(() => {
    if (!detailId) return;
    api.get(`sows/${detailId}/documents`).then(setDocuments).catch(() => setDocuments([]));
  }, [detailId]);

  const loadPurchases = useCallback(() => {
    if (!detailId || !seesMoney) return;
    api.get(`sows/${detailId}/purchases`).then(r => setPurchases(Array.isArray(r) ? r : [])).catch(() => setPurchases([]));
  }, [detailId, seesMoney]);
  useEffect(() => { loadPurchases(); }, [loadPurchases]);

  const generateMiniSow = async () => {
    setGeneratingMiniSow(true);
    try {
      await api.post(`sows/${detailId}/mini-sow`, {});
      toast('Mini SOW PDF generated — see it in the documents list below.', 'success');
      loadDocuments();
    } catch (e) {
      toast(e.message || 'Failed to generate Mini SOW PDF', 'error');
    }
    setGeneratingMiniSow(false);
  };

  const loadReaug = useCallback(() => {
    if (!detailId || !seesMoney) return;
    setReaugLoading(true);
    Promise.all([
      api.get(`sows/${detailId}/reaug-invoices`),
      api.get(`sows/${detailId}/reaug-timesheets`),
      api.get(`sows/${detailId}/resource-workers`),
    ])
      .then(([inv, ts, rw]) => {
        setReaugInvoices(inv || []);
        setReaugTimesheets(ts || []);
        setReaugResources(rw || []);
        setReaugLoading(false);
      })
      .catch(() => setReaugLoading(false));
  }, [detailId, seesMoney]);

  useEffect(loadData, [loadData]);
  // Deep-linked from a notification or My Tasks → open the Comments & Actions tab.
  // Runs once the SOW has loaded so it wins over loadData's default-tab pick.
  useEffect(() => {
    if (!loading && (targetActionId || detailSub === 'commentary')) setTab('commentary');
    // #sow-detail/<id>/history lands on the job's history, for links from search.
    if (!loading && detailSub === 'history') setTab('history');
  }, [loading, detailSub, targetActionId]);
  // Deep-linked to a specific milestone (e.g. an invoice-number search) → open
  // the Milestones tab, scroll to that row and flash it briefly.
  useEffect(() => {
    if (loading || !targetMilestoneId) return;
    setTab('milestones');
    const t1 = setTimeout(() => {
      document.getElementById(`ms-${targetMilestoneId}`)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setHighlightMs(targetMilestoneId);
    }, 150);
    const t2 = setTimeout(() => setHighlightMs(null), 3200);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [loading, targetMilestoneId]);
  // Framework call-offs have pre-agreed pricing — default straight into
  // Quick Add so pricing lines can be entered as fast as the old spreadsheet
  // allowed. Only forces this once, on first load, so turning it back off
  // manually sticks for the rest of the session.
  const quickAddDefaultedRef = useRef(false);
  useEffect(() => {
    if (sow?.parent_sow?.is_framework && !quickAddDefaultedRef.current) {
      quickAddDefaultedRef.current = true;
      setQuickAddHardware(true);
      setQuickAddResources(true);
    }
  }, [sow?.parent_sow?.is_framework]);
  useEffect(() => {
    api.get('config-options', { category: 'document_type' })
      .then(opts => {
        const map = {};
        (opts || []).forEach(o => { map[o.value] = o.label; });
        setDocTypeMap(map);
      })
      .catch(() => {});
  }, []);
  useEffect(() => { if (tab === 'tasks') loadRepeatableTasks(); }, [tab, loadRepeatableTasks]);
  useEffect(() => { if (tab === 'documents') loadDocuments(); }, [tab, loadDocuments]);
  useEffect(() => { if (tab === 'resource-augmentation') loadReaug(); }, [tab, loadReaug]);
  // Detect the SOW's SharePoint folder (without creating it) so the button can
  // show "Open" for folders that already exist, and "Create Folder" only when none does.
  useEffect(() => {
    if (!sow?.id) return;
    if (sow.sharepoint_folder_url) { setSpUrl(sow.sharepoint_folder_url); setSpChecked(true); return; }
    setSpUrl(null); setSpChecked(false);
    api.get(`sows/${sow.id}/sharepoint-folder`)
      .then(r => { if (r?.exists && r.webUrl) setSpUrl(r.webUrl); })
      .catch(() => {})
      .finally(() => setSpChecked(true));
  }, [sow?.id, sow?.sharepoint_folder_url]);
  // Preload date history for inline milestone history modal
  const ensureDateHistory = () => {
    if (!detailId || dateHistory !== null) return;
    api.get(`sows/${detailId}/date-history`)
      .then(data => setDateHistory(data))
      .catch(() => setDateHistory({ milestones: [], changes: [] }));
  };

  const loadQa = () => {
    if (!detailId) return;
    api.get(`sows/${detailId}/qa`).then(setQaReview).catch(() => setQaReview([]));
  };
  useEffect(() => {
    if (tab === 'qa-review' && qaReview === null) loadQa();
  }, [tab, qaReview, detailId]); // eslint-disable-line react-hooks/exhaustive-deps
  // Also preload QA when SOW is Draft so the Issue button can gate correctly
  useEffect(() => { if (detailId) loadQa(); }, [detailId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCcns = () => {
    if (!detailId) return;
    api.get(`sows/${detailId}/ccns`).then(setCcns).catch(() => setCcns([]));
  };
  useEffect(() => {
    if (tab === 'ccn' && ccns === null) loadCcns();
  }, [tab, ccns, detailId]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitComment = async () => {
    if (!newComment.trim()) return;
    await api.post('commentary', { entity_type: 'sow', entity_id: detailId, content: newComment.trim() });
    setNewComment('');
    const c = await api.get('commentary', { entity_type: 'sow', entity_id: detailId });
    setCommentary(c);
  };

  const addMilestone = async (data) => {
    const res = await api.post('milestones', { ...data, sow_id: detailId, reference: sow.reference });
    if (res?.sow_reopened) toast('SOW is back in delivery: this milestone still has to be invoiced.', 'success');
    setShowAddMilestone(false);
    const [s, m] = await Promise.all([api.get(`sows/${detailId}`), api.get('milestones', { sow_id: detailId })]);
    setSow(s); setMilestones(m);
  };

  const saveMilestone = async (id, data) => {
    const res = await api.put(`milestones/${id}`, data);
    if (res?.sow_auto_completed) toast('SOW marked Complete — every milestone on it is now paid.', 'success');
    if (res?.sow_awaiting_payment) toast('SOW is now Awaiting Payment: every milestone on it is invoiced or paid.', 'success');
    if (res?.sow_reopened) toast('SOW is back in delivery: this milestone still has to be invoiced.', 'success');
    setEditMilestone(null);
    // Reload the SOW too: cancelling/reinstating a milestone can reconcile the
    // SOW total, so the Total/Remaining cards need to refresh with the milestones.
    const [s, m] = await Promise.all([api.get(`sows/${detailId}`), api.get('milestones', { sow_id: detailId })]);
    setSow(s); setMilestones(m);
  };

  const deleteMilestone = async (id) => {
    if (!confirm('Delete this milestone?')) return;
    await api.delete(`milestones/${id}`);
    const [s, m] = await Promise.all([api.get(`sows/${detailId}`), api.get('milestones', { sow_id: detailId })]);
    setSow(s); setMilestones(m);
  };

  const addHardwareLine = async (data) => {
    await api.post(`sows/${detailId}/hardware-lines`, data);
    setShowAddHardware(false);
    setSow(await api.get(`sows/${detailId}`));
  };

  const onPOCreated = async (po) => {
    setShowCreatePO(false);
    setSelectedHwLines(new Set());
    setSow(await api.get(`sows/${detailId}`));
    toast(`${po.po_number} created — hardware lines marked as Ordered`, 'success');
  };

  const deleteHardwareLine = async (lineId) => {
    const line = hardwareLines.find(h => h.id === lineId);
    const activePoStatuses = new Set(['draft', 'issued', 'acknowledged', 'part_delivered', 'delivered']);
    if (line?.procurement_po_id && activePoStatuses.has(line.procurement_po_status)) {
      alert(`This item is linked to active PO ${line.procurement_po_number}. Detach the PO first using the "Detach PO" button.`);
      return;
    }
    if (!confirm('Remove this hardware item?')) return;
    try {
      await api.delete(`sows/${detailId}/hardware-lines/${lineId}`);
      setSow(await api.get(`sows/${detailId}`));
    } catch (e) {
      alert(e.message || 'Failed to delete hardware item.');
    }
  };

  const detachPOFromLine = async (lineId) => {
    if (!confirm('Detach PO from this hardware item? Status will reset to Not Ordered.')) return;
    try {
      await api.put(`sows/${detailId}/hardware-lines/${lineId}`, { procurement_po_id: null, delivery_status: 'not_ordered' });
      setSow(await api.get(`sows/${detailId}`));
    } catch (e) {
      alert(e.message || 'Failed to detach PO.');
    }
  };

  const toggleBundleExpand = async (line) => {
    const next = new Set(expandedBundleLines);
    if (next.has(line.id)) {
      next.delete(line.id);
    } else {
      next.add(line.id);
      if (!bundleLineItems[line.id]) {
        const full = await api.get(`product-bundles/${line.bundle_id}`).catch(() => null);
        if (full?.items) setBundleLineItems(prev => ({ ...prev, [line.id]: full.items }));
      }
    }
    setExpandedBundleLines(next);
  };

  const updateHardwareStatus = async (lineId, delivery_status, override_reason) => {
    await api.put(`sows/${detailId}/hardware-lines/${lineId}`, { delivery_status, override_reason: override_reason || null });
    setStatusOverride(null);
    setSow(await api.get(`sows/${detailId}`));
  };

  const unallocateHardwareLine = async (lineId) => {
    if (!confirm('Unallocate this item? The inventory unit will be returned to available stock.')) return;
    await api.put(`sows/${detailId}/hardware-lines/${lineId}`, { unallocate: true });
    setSow(await api.get(`sows/${detailId}`));
  };

  const saveMargin = async (lineId) => {
    const m = parseFloat(marginEditVal);
    if (isNaN(m) || m < 0 || m >= 100) { setEditingMarginId(null); return; }
    setEditingMarginId(null);
    await api.put(`sows/${detailId}/hardware-lines/${lineId}`, { margin_pct: m });
    setSow(await api.get(`sows/${detailId}`));
  };

  const saveQty = async (line) => {
    const newQty = Math.max(1, parseInt(qtyEditVal) || 1);
    setEditingQtyId(null);
    if (newQty === line.quantity) return;
    // Keep line totals the same: recalculate unit prices from stored line values
    const newUnitCost = line.line_cost > 0 ? parseFloat((line.line_cost / newQty).toFixed(4)) : line.unit_cost;
    const newUnitSell = line.line_sell > 0 ? parseFloat((line.line_sell / newQty).toFixed(4)) : line.unit_sell;
    await api.put(`sows/${detailId}/hardware-lines/${line.id}`, { quantity: newQty, unit_cost: newUnitCost, unit_sell: newUnitSell });
    setSow(await api.get(`sows/${detailId}`));
  };

  const addResourceLine = async (data) => {
    await api.post(`sows/${detailId}/resource-lines`, data);
    setShowAddResource(false);
    setSow(await api.get(`sows/${detailId}`));
  };

  const deleteResourceLine = async (lineId) => {
    if (!confirm('Remove this resource line?')) return;
    await api.delete(`sows/${detailId}/resource-lines/${lineId}`);
    setSow(await api.get(`sows/${detailId}`));
  };

  const updateResourceLine = async (lineId, data) => {
    await api.put(`sows/${detailId}/resource-lines/${lineId}`, data);
    setSow(await api.get(`sows/${detailId}`));
  };

  // Resource lines from the plan, by each assignee's own band, priced off the
  // plan's rate card, with the tasks linked to the lines they produce (Phase 2).
  // It used to match team names to band names and fall back to the first band
  // on the card, so "Infrastructure" work could be priced as anything.
  const handleAutoPopulateResources = async () => {
    setResourceAutoPopulating(true);
    try {
      const r = await api.post(`sows/${detailId}/resource-lines/from-plan`, { apply: true });
      if (!r.created?.length) {
        toast.warning(r.unpriced?.length
          ? `No lines created: ${r.unpriced.length} piece${r.unpriced.length === 1 ? '' : 's'} of plan work belong to people with no band on the rate card. Give them a band, or add lines by hand.`
          : 'No lines created: nobody in the plan is assigned to unlinked work yet.');
        return;
      }
      setSow(await api.get(`sows/${detailId}`));
      setResourceAddOverride(true);
      toast.success(`Created ${r.created.length} resource line${r.created.length !== 1 ? 's' : ''} from the plan, by each person's band, and linked their tasks.${r.unpriced?.length ? ` ${r.unpriced.length} piece${r.unpriced.length === 1 ? '' : 's'} of work could not be priced; see the Resourcing tab.` : ''}`);
    } catch (e) {
      toast.error('Auto-populate failed: ' + (e?.message || 'Unknown error'));
    } finally {
      setResourceAutoPopulating(false);
    }
  };

  // ── Section helpers ────────────────────────────────────────────────────────
  // Sync sections whenever the sow (and thus its resource lines) loads/changes
  useEffect(() => {
    if (sow?.id) {
      const fromLines = (sow.resource_lines || []).map(l => l.work_package).filter(Boolean);
      setSections(prev => [...new Set(['Delivery', ...prev, ...fromLines])]);
    }
  }, [sow?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const addSection = () => {
    const name = newSectionInput.trim();
    if (!name) return;
    setSections(prev => prev.includes(name) ? prev : [...prev, name]);
    setNewSectionInput('');
  };

  const confirmRenameSection = async (oldName) => {
    const newName = sectionEditValue.trim();
    setEditingSection(null);
    if (!newName || newName === oldName) return;
    setSections(prev => prev.map(s => s === oldName ? newName : s));
    const hasLines = (sow?.resource_lines || []).some(l => (l.work_package || 'General') === oldName);
    if (hasLines) {
      await api.post(`sows/${detailId}/resource-lines/rename-section`, { old_name: oldName, new_name: newName });
      setSow(await api.get(`sows/${detailId}`));
    }
  };

  const saveSOW = async (data) => {
    const resolvedMismatch = data._resolvedMismatch;
    const newTotal = data.total_excl_vat;
    delete data._resolvedMismatch;
    // Detect a Project Manager (owner_id) change so we can offer to cascade it
    // to the SOW's open milestones after the save lands.
    const prevPmId = sow.owner_id || '';
    const newPmId  = data.owner_id || '';
    const pmChanged = newPmId && newPmId !== prevPmId;
    await api.put(`sows/${detailId}`, data);
    if (resolvedMismatch) {
      await api.post('commentary', {
        entity_type: 'sow',
        entity_id: detailId,
        content: `Data validation resolution: SOW total updated to ${formatMoney(newTotal, 2)} to match active milestone sum.`,
      });
    }
    setShowEditSOW(false);
    setSow(await api.get(`sows/${detailId}`));
    // Offer to reassign the PM on milestones that aren't already settled.
    if (pmChanged) {
      const TERMINAL = ['Paid', 'Invoiced', 'Complete', 'Completed', 'Cancelled', 'Split', 'Credit Noted'];
      const eligible = milestones.filter(m => !TERMINAL.includes(m.status) && (m.project_manager_id || '') !== newPmId);
      if (eligible.length > 0) {
        const person = people.find(p => p.id === newPmId);
        const newPmName = person ? `${person.first_name} ${person.last_name}`.trim() : 'the new PM';
        setPmCascade({ newPmId, newPmName, eligible });
      }
    }
  };

  // Apply the chosen PM to every eligible milestone (each PUT is audited server-side).
  const applyPmCascade = async () => {
    if (!pmCascade) return;
    setPmCascadeSaving(true);
    try {
      for (const m of pmCascade.eligible) {
        await api.put(`milestones/${m.id}`, { project_manager_id: pmCascade.newPmId });
      }
      setMilestones(await api.get('milestones', { sow_id: detailId }));
      toast(`Reassigned ${pmCascade.eligible.length} milestone${pmCascade.eligible.length !== 1 ? 's' : ''} to ${pmCascade.newPmName}.`, 'success');
      setPmCascade(null);
    } catch (e) {
      toast.error(e.message || 'Could not reassign milestones');
    } finally {
      setPmCascadeSaving(false);
    }
  };

  // ── Clipboard helpers ────────────────────────────────────────────────────
  const copyTitle = () => {
    navigator.clipboard.writeText(sow.reference).then(() => {
      setTitleCopied(true);
      setTimeout(() => setTitleCopied(false), 1500);
    });
  };

  const copyMilestoneTable = () => {
    const fmtD = (d) => {
      if (!d) return '';
      const [y, m, day] = d.split('-');
      return new Date(+y, +m - 1, +day).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
    };
    const fmtM = (n) => n != null ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(n) : '';
    // In the order the table is showing, so the email matches the screen.
    const rows = sortedMilestones.map(m => ({
      code: m.milestone_code,
      desc: m.description || '',
      status: m.status,
      contract: fmtD(m.contract_date),
      forecast: fmtD(m.forecast_date),
      amount: fmtM(m.invoice_amount_excl),
    }));
    const total = fmtM(rows.reduce((s, r) => {
      const n = milestones.find(m => m.milestone_code === r.code)?.invoice_amount_excl || 0;
      return s + n;
    }, 0));

    // Escaped and formula-guarded on the way to the clipboard (pen test AD11).
    const { html, plain } = milestoneTableClipboard({ title: `${sow.reference} - ${sow.detail}`, rows, total, showAmount: seesMoney });

    try {
      navigator.clipboard.write([
        new ClipboardItem({
          'text/html':  new Blob([html],  { type: 'text/html' }),
          'text/plain': new Blob([plain], { type: 'text/plain' }),
        }),
      ]).then(() => { setTableCopied(true); setTimeout(() => setTableCopied(false), 2000); });
    } catch {
      navigator.clipboard.writeText(plain).then(() => { setTableCopied(true); setTimeout(() => setTableCopied(false), 2000); });
    }
  };

  // ── Status transition helpers ─────────────────────────────────────────────
  const issueSOW = () => {
    setShowIssueModal(true);
  };

  const activateSOW = async (poData) => {
    // Called from PO Received modal — moves Issued → Active
    const { milestoneDates, ...sowData } = poData;
    await api.put(`sows/${detailId}`, {
      status: 'Active',
      previous_status: sow.status,
      po_number: sowData.po_number,
      payment_terms: parseInt(sowData.payment_terms) || null,
      po_received_date: sowData.po_received_date,
      ses_applicable: sowData.ses_applicable ? 1 : 0,
    });
    if (milestoneDates?.length > 0) {
      await Promise.all(milestoneDates.map(({ id, ...dates }) => api.put(`milestones/${id}`, dates)));
    }
    setShowPOReceived(false);
    const [s, m] = await Promise.all([api.get(`sows/${detailId}`), api.get('milestones', { sow_id: detailId })]);
    setSow(s);
    setMilestones(m);
  };

  const revertToDraft = async () => {
    if (!confirm('Put this Opportunity back to Draft status?')) return;
    await api.put(`sows/${detailId}`, { status: 'Draft', previous_status: sow.status });
    setSow(await api.get(`sows/${detailId}`));
  };

  const declineSOW = async () => {
    if (!confirm('Mark this Opportunity as Not Won?\n\nThis records that the client declined.')) return;
    await api.put(`sows/${detailId}`, { status: 'Not Won', previous_status: sow.status });
    setSow(await api.get(`sows/${detailId}`));
  };

  // Cancel a live bid (Issued but being withdrawn) — distinct from Not Won, which
  // records a client decline. Sets the SOW to Cancelled; it can be moved back to
  // Draft later. No milestone guard here: an issued (pre-PO) bid has no billing.
  const cancelBid = async () => {
    if (!confirm('Cancel this bid?\n\nThe SOW will be set to Cancelled and removed from the active pipeline. You can move it back to Draft later if needed.')) return;
    await api.put(`sows/${detailId}`, { status: 'Cancelled', previous_status: sow.status });
    setSow(await api.get(`sows/${detailId}`));
  };

  const holdSOW = async () => {
    if (!confirm('Put this SOW On Hold?')) return;
    await api.put(`sows/${detailId}`, { status: 'On Hold', previous_status: sow.status });
    setSow(await api.get(`sows/${detailId}`));
  };

  const revertToIssued = async () => {
    if (!confirm('Revert this SOW back to Issued?\n\nPO number, PO received date and SES flag will be cleared.')) return;
    await api.put(`sows/${detailId}`, { status: 'Issued', previous_status: sow.status, po_number: null, po_received_date: null, ses_applicable: 0 });
    setSow(await api.get(`sows/${detailId}`));
  };

  const revertCancelledToDraft = async () => {
    if (!confirm('Move this SOW back to Draft?\n\nThe cancellation reason will be cleared and the SOW will re-enter the pipeline for editing.')) return;
    await api.put(`sows/${detailId}`, { status: 'Draft', previous_status: sow.status });
    setSow(await api.get(`sows/${detailId}`));
  };

  // Not Won → Issued: client changed their mind, or it was only marked Not Won
  // because validity lapsed. Put it back into the pipeline as a live Issued SOW;
  // the backend restarts the issued_date (validity clock) from today.
  const reinstateToIssued = async () => {
    if (!confirm('Move this SOW back to Issued?\n\nIt re-enters the pipeline as a live issued SOW, and its validity clock restarts from today.')) return;
    await api.put(`sows/${detailId}`, { status: 'Issued', previous_status: sow.status });
    setSow(await api.get(`sows/${detailId}`));
  };

  const restoreSOW = async () => {
    const target = sow.previous_status || 'Active';
    if (!confirm(`Restore SOW to "${target}"?`)) return;
    await api.put(`sows/${detailId}`, { status: target, previous_status: null });
    setSow(await api.get(`sows/${detailId}`));
  };

  const completeSOW = async () => {
    const awaitingCount = milestones.filter(m => m.status === 'Awaiting').length;
    if (awaitingCount > 0) {
      toast.error(`Cannot complete this SOW — ${awaitingCount} milestone${awaitingCount !== 1 ? 's are' : ' is'} still Awaiting. Update or cancel them first.`);
      return;
    }
    if (!confirm('Mark this SOW as Complete?')) return;
    await api.put(`sows/${detailId}`, { status: 'Complete', previous_status: sow.status });
    setSow(await api.get(`sows/${detailId}`));
  };

  const cancelSOW = async () => {
    const awaitingCount = milestones.filter(m => m.status === 'Awaiting').length;
    if (awaitingCount > 0) {
      toast.error(`Cannot cancel this SOW — ${awaitingCount} milestone${awaitingCount !== 1 ? 's are' : ' is'} still Awaiting. Update or cancel them first.`);
      return;
    }
    if (!confirm('Cancel this SOW? This cannot be undone easily.')) return;
    await api.put(`sows/${detailId}`, { status: 'Cancelled', previous_status: sow.status });
    setSow(await api.get(`sows/${detailId}`));
  };

  // Expired → Issued: the SOW simply lapsed (no PO in time), nothing about the
  // deal itself needs re-approving. Moves back to Issued and restarts the
  // 56-day validity clock from today — but the backend still re-checks
  // milestone totals against the SOW value (BL-053), so a genuine cost
  // mismatch is caught rather than silently waved through.
  const revalidateExpiredSOW = async () => {
    if (!confirm('Revalidate this SOW?\n\nIt moves back to Issued and its validity restarts from today (new expiry date). Milestone costs are re-checked against the SOW value, but it does not need to go through the issue approval workflow again.')) return;
    try {
      await api.put(`sows/${detailId}`, { status: 'Issued', previous_status: sow.status });
      setSow(await api.get(`sows/${detailId}`));
    } catch (e) {
      toast.error(e.message || 'Could not revalidate SOW — check that milestone totals match the SOW value.');
    }
  };

  const cancelExpiredSOW = async () => {
    const awaitingCount = milestones.filter(m => m.status === 'Awaiting').length;
    if (awaitingCount > 0) {
      toast.error(`Cannot cancel this SOW — ${awaitingCount} milestone${awaitingCount !== 1 ? 's are' : ' is'} still Awaiting. Update or cancel them first.`);
      return;
    }
    if (!confirm('Cancel this SOW? It is no longer a live opportunity. This cannot be undone easily.')) return;
    await api.put(`sows/${detailId}`, { status: 'Cancelled', previous_status: sow.status });
    setSow(await api.get(`sows/${detailId}`));
  };

  // Expired → Not Won: the client's decision arrives after the SOW has already
  // lapsed. Distinct from cancelExpiredSOW above, which is us withdrawing it.
  const declineExpiredSOW = async () => {
    if (!confirm('Mark this Opportunity as Not Won?\n\nRecords that the client made a decision not to proceed, even though the SOW had already lapsed.')) return;
    await api.put(`sows/${detailId}`, { status: 'Not Won', previous_status: sow.status });
    setSow(await api.get(`sows/${detailId}`));
  };

  const archiveSOW = async () => {
    if (!confirm('Archive this Draft SOW? It will be hidden from all standard views. You can restore it from the Opp. Tracker using "Show Archived".')) return;
    await api.put(`sows/${detailId}`, { status: 'Archived', previous_status: sow.status });
    setSow(await api.get(`sows/${detailId}`));
  };

  const closeSOW = async () => {
    try {
      const updated = await api.post(`sows/${detailId}/close`, { reason: closeSowReason });
      setSow(updated);
      setShowCloseSow(false);
    } catch (e) {
      toast.error(e.message || 'Could not close SOW');
    }
  };

  const addDocument = async (data) => {
    await api.post(`sows/${detailId}/documents`, data);
    setShowAddDocument(false);
    loadDocuments();
    // When the uploaded document is the Statement of Work itself, offer to keep
    // the SOW record consistent by matching its issued version + issued date.
    if (data.document_type === 'sow') {
      const m = String(data.version || '').match(/\d+/);
      const parsed = m ? parseInt(m[0], 10) : null;
      // The document that was just uploaded, so a re-issue can start from it rather than
      // asking somebody to find it again in a dropdown.
      const docs = await api.get(`sows/${detailId}/documents`).catch(() => []);
      const uploaded = (docs || []).find(d => d.title === data.title && d.url === data.url)
        || (docs || []).find(d => d.title === data.title);
      setSowDocPrompt({
        title: data.title,
        version: data.version || '',
        parsed_version: (parsed && parsed > 0) ? parsed : null,
        document_id: uploaded?.id || null,
        issued_version: (parsed && parsed > 0) ? parsed : (sow.issued_version || 0),
        issued_date: new Date().toISOString().split('T')[0],
        // ── WHY THIS IS THE WHOLE POINT ──────────────────────────────────────────
        //
        // "I up issued a SOW with a new version number as part of the documents
        //  functionality, and I thought it would up issue the main SOW details."
        //
        // It half did, and the half it missed cost £173,097 of live opportunity: it moved
        // the version and the issued date and left the expiry date worked out from the
        // ORIGINAL issue, so the nightly validity check expired the SOW the next morning.
        //
        // A new version of the Statement of Work going to a client is not a record-keeping
        // tidy-up, it is a RE-ISSUE. So on a SOW that has already been issued this stops
        // offering to patch two fields and offers the issue workflow instead — which sets
        // the version, the issued date AND the expiry together, records the issued version,
        // re-checks the milestone totals, and lets the milestone dates be corrected while
        // somebody is looking at them.
        reissue: ['Issued', 'Active', 'Expired', 'Not Won'].includes(sow.status),
      });
    }
  };

  // Apply the SOW-document prompt: sync issued version + issued date onto the SOW.
  const applySowDocSync = async () => {
    if (!sowDocPrompt) return;
    setSowDocSyncing(true);
    try {
      await api.put(`sows/${detailId}`, {
        issued_version: parseInt(sowDocPrompt.issued_version, 10) || 0,
        issued_date: sowDocPrompt.issued_date || null,
      });
      setSow(await api.get(`sows/${detailId}`));
      toast('SOW issued version and date updated to match the document.', 'success');
      setSowDocPrompt(null);
    } catch (e) {
      toast.error(e.message || 'Could not update the SOW');
    } finally {
      setSowDocSyncing(false);
    }
  };

  const deleteDocument = async (docId) => {
    if (!confirm('Remove this document link?')) return;
    await api.delete(`sows/${detailId}/documents/${docId}`);
    loadDocuments();
  };

  const updateRepeatableTask = async (templateId, patch) => {
    await api.put(`sows/${detailId}/repeatable-tasks/${templateId}`, patch);
    loadRepeatableTasks();
  };

  if (loading || !sow) return <div className="text-center py-8 text-gray-400 text-sm">Loading...</div>;

  // Grants, not role lists (pen test AD25996): the API asks the same keys, so
  // nothing is offered here that the server would refuse. Reading a SOW stays
  // open to all staff.
  const canEdit = canAccess('sow:edit');
  const canEditMilestones = canAccess('sow:edit-milestones');
  // Finance record the Coupa approval date (src/lib/coupaChaseActions.js).
  const canRecordCoupa = canAccess('sow:coupa-approved');
  const canCloseDraft = canAccess('sow:close-draft');

  // Build tab list — QA Review always last; hidden from bar when Active (accessible via small link)
  const buildTabs = () => {
    let tabs;
    if (IS_REAUG(sow.type)) {
      const base = ['milestones', 'tasks', 'resource-augmentation', 'resources', 'hardware', 'costs', 'plan', 'risks', 'history', 'commentary', 'documents', 'qa-review'];
      if (sow.status === 'Active') {
        base.splice(base.indexOf('tasks') + 1, 0, 'ccn');
        const qaIdx = base.indexOf('qa-review');
        if (qaIdx !== -1) base.splice(qaIdx, 1);
      }
      tabs = base;
    } else if (sow.status === 'Active') {
      // milestones first, then tasks (checklist), CCN inserted, qa-review hidden
      const base = [...BASE_TABS];
      base.splice(base.indexOf('tasks') + 1, 0, 'ccn');
      const qaIdx = base.indexOf('qa-review');
      if (qaIdx !== -1) base.splice(qaIdx, 1);
      tabs = base;
    } else {
      // Draft / Issued / other: checklist (tasks) first, plan second
      tabs = ['tasks', 'plan', 'milestones', 'resources', 'hardware', 'costs', 'risks', 'history', 'commentary', 'documents', 'qa-review'];
    }
    // Resourcing (Phase 2): sold, planned and booked per resource line, right
    // after the lines themselves.
    const resIdx = tabs.indexOf('resources');
    if (resIdx !== -1) tabs.splice(resIdx + 1, 0, 'resourcing');
    // Site surveys (migration 0408), for those who hold the Surveys page.
    if (canAccess('surveys')) {
      const riskIdx = tabs.indexOf('risks');
      riskIdx !== -1 ? tabs.splice(riskIdx, 0, 'surveys') : tabs.push('surveys');
    }
    if (sow.children?.length > 0) {
      const docsIdx = tabs.indexOf('documents');
      docsIdx !== -1 ? tabs.splice(docsIdx, 0, 'children') : tabs.push('children');
    }
    // Files: the SOW's SharePoint folder, browsed in place, after Documents.
    const filesAt = tabs.indexOf('documents');
    filesAt !== -1 ? tabs.splice(filesAt + 1, 0, 'files') : tabs.push('files');
    // Purchases booked to the SOW and Re-Aug invoices and timesheets are money
    // and nothing else, so without the grant the tabs are not offered.
    if (!seesMoney) tabs = tabs.filter(t => t !== 'costs' && t !== 'resource-augmentation');
    return tabs;
  };
  const TABS = buildTabs();

  const qaPendingCount = (qaReview || []).filter(s => s.approval?.status !== 'approved').length;
  const qaApprovedCount = (qaReview || []).filter(s => s.approval?.status === 'approved').length;
  const qaTotal = (qaReview || []).length;

  const resourceLines = sow.resource_lines || [];
  const hardwareLines = sow.hardware_lines || [];
  // AI-extracted legacy hardware that never matched a catalogue item, so
  // was never committed into hardwareLines above — shown separately,
  // read-only, since it isn't a real priced/costed line yet.
  const legacyUnmatchedHardware = sow.legacy_unmatched_hardware || [];
  const legacyUnmatchedHardwareTotal = legacyUnmatchedHardware.reduce((s, l) => s + (l.line_total || 0), 0);
  // Active milestone sum excludes cancelled/credit-noted/split — this is what's
  // shown in the Milestones stat tile and footer row, so the mismatch warning
  // below must compare against the SAME figure. Comparing against a sum that
  // includes cancelled milestones produced a confusing banner claiming a gap
  // while displaying two identical-looking totals (the cancelled milestone's
  // value was silently baked into the "difference" but not into either number
  // shown on screen).
  const milestoneSum  = milestones.filter(m => !['Cancelled','Split','Credit Noted'].includes(m.status)).reduce((s, m) => s + (m.invoice_amount_excl || 0), 0);
  const milestoneGap  = milestoneSum - (sow.total_excl_vat || 0);
  // Framework only: call-off milestones rolled up against each pot (see
  // src/api/frameworkRollup.js). Totals include the unmatched row so they
  // reconcile with the call-offs.
  const rollup = sow.framework_rollup || null;
  const rollupTotals = rollup && [...Object.values(rollup.lines), rollup.unmatched]
    .reduce((t, b) => ({ called_off: t.called_off + b.called_off, invoiced: t.invoiced + b.invoiced }), { called_off: 0, invoiced: 0 });
  const sortedMilestones = sortMilestones(milestones, milestoneSort, rollup?.lines);
  const rollupItemCount = rollup ? [...Object.values(rollup.lines), rollup.unmatched].reduce((n, b) => n + b.items.length, 0) : 0;
  // Tolerance of £1 — milestones are typically entered as whole pounds; sub-£1
  // differences are rounding artefacts from pence in the SOW total, not real gaps.
  const MILESTONE_GAP_TOLERANCE = 1.00;
  const resourceTotal = resourceLines.reduce((s, l) => s + (l.line_total || 0), 0);
  // Rolled into the hardware total shown on this page (not into
  // sow.total_excl_vat, which is entered manually on the Edit page and
  // unaffected by either hardware or resource totals) so the figure here
  // reflects the SOW's real extracted value, not just what's been
  // reconciled against the catalogue so far.
  const hardwareTotal = hardwareLines.reduce((s, l) => s + (l.line_sell || 0), 0) + legacyUnmatchedHardwareTotal;
  // Checkbox, item, qty, milestone, status and actions, plus whichever money columns are shown.
  const hwColCount = 6 + (seesTradeCost ? 2 : 0) + (seesMoney ? 2 : 0);

  const resourceSections = resourceLines.reduce((acc, l) => {
    const sec = l.work_package || 'General';
    if (!acc[sec]) acc[sec] = [];
    acc[sec].push(l);
    return acc;
  }, {});
  // All section names: tracked sections + any from lines not yet tracked (keeps order stable)
  const allSectionNames = [...new Set([...sections, ...resourceLines.map(l => l.work_package || 'General')])];

  const tabLabel = (t) => {
    if (t === 'qa-review')             return qaTotal ? `QA Review (${qaApprovedCount}/${qaTotal})` : 'QA Review';
    if (t === 'milestones')            return `Milestones (${milestones.length})`;
    if (t === 'ccn')                   return ccns ? `CCN (${ccns.length})` : 'CCN';
    if (t === 'resource-augmentation') return `Resource Augmentation${reaugInvoices.length ? ` (${reaugInvoices.length})` : ''}`;
    if (t === 'resources')             return `Resources${resourceLines.length ? ` (${resourceLines.length})` : ''}`;
    if (t === 'resourcing')            return 'Resourcing';
    if (t === 'hardware')              return `Hardware / Services${hardwareLines.length ? ` (${hardwareLines.length})` : ''}`;
    if (t === 'costs')                 return `Costs${purchases.length ? ` (${purchases.length})` : ''}`;
    if (t === 'tasks')                 return `Checklist`;
    if (t === 'plan')                  return `Plan / Gantt`;
    if (t === 'surveys')               return `Surveys`;
    if (t === 'risks')                 return `Risks`;
    if (t === 'history')               return `History`;
    if (t === 'commentary')            return `Comments & Actions${commentary.length ? ` (${commentary.length})` : ''}`;
    if (t === 'documents')             return `Documents (${documents.length})`;
    if (t === 'files')                 return `Files`;
    if (t === 'children')              return `Children (${sow.children?.length || 0})`;
    return t;
  };

  const owner = people.find(p => p.id === sow.owner_id);

  return (
    <div className="fade-in">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1 text-[9px] text-gray-400 mb-2">
        <a className="text-gold cursor-pointer hover:underline" onClick={() => navigate('tracker')}>Tracker</a>
        <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="9 18 15 12 9 6"/></svg>
        <span>{sow.reference}</span>
        {legacyHistory.length > 0 && (
          <button onClick={() => setShowLegacyHistory(true)}
            title="Read-only weekly history imported from the legacy Zoho tracker before go-live"
            className="ml-2 px-1.5 py-0.5 text-[8px] font-bold bg-amber-50 text-amber-700 border border-amber-200 rounded hover:bg-amber-100">
            📜 Legacy History ({legacyHistory.length})
          </button>
        )}
      </div>

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between mb-2 gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <h2 className="text-sm sm:text-base font-extrabold text-navy leading-tight">{sow.reference} - {sow.detail}</h2>
            <button
              onClick={copyTitle}
              title="Copy SOW reference to clipboard"
              className="flex-shrink-0 p-0.5 rounded text-gray-400 hover:text-navy hover:bg-gray-100 transition-colors"
            >
              {titleCopied
                ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-500"><polyline points="20 6 9 17 4 12"/></svg>
                : <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>
              }
            </button>
          </div>
          <div className="text-[9px] text-gray-500 mt-0.5 flex items-center gap-2 flex-wrap">
            <span>{sow.client_name || 'No client'}</span>
            <span>·</span>
            <span>{owner ? `${owner.first_name} ${owner.last_name}` : sow.owner_name || 'Unassigned'}</span>
            {sow.received_date && <><span>·</span><span className="text-gray-400">Received {formatDate(sow.received_date)}</span></>}
            {(sow.issued_date || sow.sow_date) && <><span>·</span><span>Issued {formatDate(sow.issued_date || sow.sow_date)}</span></>}
            {sow.issued_version > 0 && <span className="bg-navy/10 text-navy px-1 rounded text-[8px] font-bold">v{sow.issued_version}</span>}
            {sow.status === 'Issued' && sow.expiry_date && <><span>·</span><span className={new Date(sow.expiry_date) < new Date() ? 'text-red-600 font-semibold' : ''}>Expires {formatDate(sow.expiry_date)}</span></>}
            {sow.status === 'Expired' && sow.expiry_date && <><span>·</span><span className="text-orange-600 font-semibold">Expired {formatDate(sow.expiry_date)}</span></>}
            <span>·</span><Tag status={sow.status} />
            {/* Project health summary — links into Project Update focused on this
                SOW. Shown for delivering/finished projects (or whenever a health
                update exists). Also surfaces when it was last updated and by whom. */}
            {(sow.status === 'Active' || sow.status === 'Complete' || sow.status === 'Completed' || sow.health) && (() => {
              const h = sow.health;
              const rag = h?.overall_rag || (() => {
                const r = [h?.schedule_rag, h?.budget_rag, h?.resource_rag, h?.risk_rag].filter(Boolean);
                return r.includes('red') ? 'red' : r.includes('amber') ? 'amber' : r.length ? 'green' : null;
              })();
              const ragDot = rag === 'red' ? '#EF4444' : rag === 'amber' ? '#F59E0B' : rag === 'green' ? '#10B981' : '#9CA3AF';
              const when = h?.week_commencing || h?.submitted_at || h?.updated_at;
              const canOpen = canAccess('project-update');
              const label = h?.delivery_status ? (HEALTH_DELIVERY_LABELS[h.delivery_status] || 'Health') : 'Health';
              const inner = (
                <>
                  <span className="inline-block w-2 h-2 rounded-full" style={{ background: ragDot }} />
                  <span className="font-bold">{label}</span>
                  {when
                    ? <span className="text-gray-500 font-normal">· updated {formatDate(when)}{h?.updated_by_name ? ` by ${h.updated_by_name}` : ''}</span>
                    : <span className="text-gray-400 font-normal italic">· no health update yet</span>}
                </>
              );
              return (
                <>
                  <span>·</span>
                  {canOpen ? (
                    <button type="button" onClick={() => navigate('project-update', sow.id)}
                      title="Open this project's health in Project Update"
                      className="inline-flex items-center gap-1 px-1.5 py-px rounded text-[8px] border border-gray-200 hover:border-navy hover:bg-gray-50 transition-colors">
                      {inner}
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-1.5 py-px rounded text-[8px] border border-gray-100">{inner}</span>
                  )}
                </>
              );
            })()}
            {sow.magnitude && <span className={`px-1.5 py-px rounded text-[8px] font-bold ${sow.magnitude === 'Large' ? 'bg-red-100 text-red-700' : sow.magnitude === 'Medium' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{sow.magnitude}</span>}
            {sow.client_priority && <span className="px-1.5 py-px rounded text-[8px] font-bold bg-slate-200 text-slate-700" title="Client-assigned priority">{sow.client_priority}</span>}
            {['Draft', 'Issued'].includes(sow.status) && sow.win_probability != null && <><span>·</span><span className="bg-indigo-100 text-indigo-700 px-1.5 py-px rounded text-[8px] font-bold">{sow.win_probability}% win</span></>}
            {['Draft', 'Issued'].includes(sow.status) && sow.likely_start_date && <><span>·</span><span className="text-gray-400">Est. start <span className="text-gray-600 font-semibold">{formatDate(sow.likely_start_date)}</span></span></>}
            {sow.ses_applicable ? <span className="bg-purple-100 text-purple-700 px-1.5 py-px rounded text-[8px] font-bold">SES</span> : null}
            {sow.closed_reason && <><span>·</span><span className="bg-red-50 text-red-600 border border-red-200 px-1.5 py-px rounded text-[8px] font-semibold">Closed: {sow.closed_reason}{sow.closed_by_name ? ` — ${sow.closed_by_name}` : ''}</span></>}
            {sow.parent_sow && (
              <span className={`inline-flex items-center gap-1 px-1.5 py-px rounded text-[8px] font-semibold cursor-pointer hover:opacity-80 ${sow.parent_sow.is_framework ? 'bg-emerald-50 border border-emerald-200 text-emerald-700' : 'bg-blue-50 border border-blue-200 text-blue-700'}`} onClick={() => navigate('sow-detail', sow.parent_sow.id)}>
                {sow.parent_sow.is_framework ? 'Call-off under' : '↑ Child of'} <span className="font-mono">{sow.parent_sow.reference}</span>
              </span>
            )}
            {sow.is_framework ? (
              <span className="inline-flex items-center gap-1 bg-navy/10 border border-navy/20 text-navy px-1.5 py-px rounded text-[8px] font-bold">FRAMEWORK</span>
            ) : null}
            {sow.client_reference && <span className="text-gray-400">Their Ref: <span className="text-gray-600 font-semibold">{sow.client_reference}</span></span>}
            {sow.po_number
              ? <span className="inline-flex items-center gap-0.5 text-gray-400">PO: <span className="text-gray-600 font-semibold">{sow.po_number}</span><CopyButton text={sow.po_number} label="Copy PO number to clipboard" size={11} /></span>
              : sow.parent_sow_id
                ? <span className="text-[8px] text-blue-500 italic">PO at parent level</span>
                : null
            }
            {sow.po_received_date && <span className="text-gray-400">Received {formatDate(sow.po_received_date)}</span>}
          </div>
          {sow.children?.length > 0 && (
            <div className="mt-1 flex items-center gap-1.5 flex-wrap">
              <span className="text-[8px] font-bold text-gray-400 uppercase">Child SOWs:</span>
              {sow.children.slice(0, 3).map(c => (
                <span key={c.id} className="cursor-pointer text-[9px] font-mono font-bold text-navy bg-navy/5 px-1.5 py-px rounded hover:bg-navy/10" onClick={() => navigate('sow-detail', c.id)}>{c.reference}</span>
              ))}
              {sow.children.length > 3 && (
                <button onClick={() => setTab('children')} className="text-[9px] text-blue-600 hover:underline font-semibold">+{sow.children.length - 3} more →</button>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-wrap flex-shrink-0">
          {/* SharePoint folder — open if it already exists, otherwise create + mirror the template */}
          {!spChecked ? (
            <span className="px-2.5 py-1 text-[9px] text-gray-400">📁 Checking…</span>
          ) : spUrl ? (
            <button
              onClick={() => window.open(spUrl, '_blank', 'noopener')}
              className="px-2.5 py-1 text-[9px] font-semibold border border-sky-300 text-sky-700 rounded hover:bg-sky-50"
              title="Open this SOW's SharePoint folder"
            >
              📁 Open in SharePoint
            </button>
          ) : canEdit && (
            <button
              onClick={async () => {
                setSpFolderBusy(true);
                try {
                  const res = await api.post(`sows/${sow.id}/sharepoint-folder`, {});
                  if (res?.webUrl) { setSpUrl(res.webUrl); window.open(res.webUrl, '_blank', 'noopener'); }
                } catch (e) { alert(e?.message || 'Could not create the SharePoint folder'); }
                finally { setSpFolderBusy(false); }
              }}
              disabled={spFolderBusy}
              className="px-2.5 py-1 text-[9px] font-semibold border border-sky-300 text-sky-700 rounded hover:bg-sky-50 disabled:opacity-50"
              title="Create the SharePoint folder structure for this SOW"
            >
              {spFolderBusy ? 'Working…' : '📁 Create Folder'}
            </button>
          )}
          {/* Status-driven action buttons: every one of them writes to the SOW */}
          {canEdit && (<>
          {sow.status === 'Draft' && (<>
            <button
              onClick={() => setShowApplyTemplate(true)}
              className="px-2.5 py-1 text-[9px] font-semibold border border-purple-300 text-purple-700 rounded hover:bg-purple-50"
              title={sow.template_applied_at ? `Template applied ${new Date(sow.template_applied_at).toLocaleDateString('en-GB')}` : 'Apply a SOW template'}
            >
              {sow.template_applied_at ? '✓ Template Applied' : '⊞ Apply Template'}
            </button>
            <button onClick={issueSOW}
              title={qaPendingCount > 0 ? `${qaPendingCount} QA step${qaPendingCount !== 1 ? 's' : ''} pending` : 'Issue this Opportunity to the client'}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-[9px] font-bold rounded transition-all ${
                qaPendingCount > 0
                  ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}>
              {qaPendingCount > 0
                ? <><span className="w-1.5 h-1.5 rounded-full bg-amber-400 flex-shrink-0" />QA Pending ({qaPendingCount})</>
                : 'Issue Opportunity →'}
            </button>
            <button onClick={archiveSOW}
              className="px-2.5 py-1 text-[9px] font-semibold border border-amber-300 text-amber-700 rounded hover:bg-amber-50"
              title="Archive this draft — hides it from all standard views">
              📦 Archive
            </button>
            {canCloseDraft && (
              <div className="relative">
                <button onClick={() => setShowCloseSow(v => !v)}
                  className="px-2.5 py-1 text-[9px] font-semibold border border-red-300 text-red-600 rounded hover:bg-red-50"
                  title="Close this draft SOW without going through the approval workflow">
                  ✕ Close SOW
                </button>
                {showCloseSow && (
                  <div className="absolute right-0 top-full mt-1 z-30 bg-white border border-gray-200 rounded-lg shadow-xl p-3 w-64 max-sm:fixed max-sm:inset-x-4 max-sm:top-1/2 max-sm:bottom-auto max-sm:right-auto max-sm:mt-0 max-sm:w-auto max-sm:-translate-y-1/2 max-sm:z-50">
                    <div className="text-[9px] font-bold text-gray-800 mb-1">Close SOW — select reason</div>
                    <div className="text-[8px] text-gray-500 mb-2">This will immediately cancel this Draft, bypassing the approval workflow.</div>
                    <select value={closeSowReason} onChange={e => setCloseSowReason(e.target.value)} className="form-select text-[9px] mb-3 w-full">
                      <option>No longer required</option>
                      <option>No bid</option>
                    </select>
                    <div className="flex gap-1.5">
                      <button onClick={() => setShowCloseSow(false)} className="flex-1 px-2 py-1 text-[9px] border border-gray-200 rounded hover:bg-gray-50">Cancel</button>
                      <button onClick={closeSOW} className="flex-1 px-2 py-1 text-[9px] font-bold bg-red-600 text-white rounded hover:bg-red-700">Close SOW</button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>)}
          {sow.status === 'Issued' && (<>
            <button onClick={() => setShowPOReceived(true)}
              className="px-2.5 py-1 text-[9px] font-bold bg-navy text-white rounded hover:bg-navy/80">
              PO Received ✓
            </button>
            <button onClick={declineSOW}
              className="px-2.5 py-1 text-[9px] font-semibold border border-red-300 text-red-600 rounded hover:bg-red-50"
              title="Client declined — records the bid as Not Won">
              Not Won
            </button>
            <button onClick={cancelBid}
              className="px-2.5 py-1 text-[9px] font-semibold border border-red-300 text-red-600 rounded hover:bg-red-50"
              title="Withdraw this bid — sets the SOW to Cancelled">
              Cancel Bid
            </button>
            <button onClick={revertToDraft}
              className="px-2.5 py-1 text-[9px] font-semibold border border-gray-300 text-gray-600 rounded hover:bg-gray-50"
              title="Return to Draft for further editing">
              ← Back to Draft
            </button>
          </>)}
          {sow.status === 'Active' && (<>
            <button onClick={holdSOW}
              className="px-2.5 py-1 text-[9px] font-semibold border border-amber-300 text-amber-700 rounded hover:bg-amber-50">
              On Hold
            </button>
            {canAccess('sow:complete') && (
            <button onClick={completeSOW}
              className="px-2.5 py-1 text-[9px] font-semibold border border-emerald-300 text-emerald-700 rounded hover:bg-emerald-50">
              Complete
            </button>
            )}
            <button onClick={cancelSOW}
              className="px-2.5 py-1 text-[9px] font-semibold border border-red-300 text-red-600 rounded hover:bg-red-50">
              Cancel
            </button>
            <button onClick={revertToIssued}
              className="px-2.5 py-1 text-[9px] font-semibold border border-gray-300 text-gray-500 rounded hover:bg-gray-50"
              title="Undo accidental activation — returns to Issued status">
              ← Revert to Issued
            </button>
          </>)}
          {sow.status === 'On Hold' && (
            <button onClick={restoreSOW}
              className="px-2.5 py-1 text-[9px] font-bold bg-navy text-white rounded hover:bg-navy/80">
              Restore → {sow.previous_status || 'Active'}
            </button>
          )}
          {sow.status === 'Archived' && (
            <button onClick={restoreSOW}
              className="px-2.5 py-1 text-[9px] font-bold bg-amber-600 text-white rounded hover:bg-amber-700">
              ↩ Restore to {sow.previous_status || 'Draft'}
            </button>
          )}
          {sow.status === 'Not Won' && (
            <button onClick={reinstateToIssued}
              className="px-2.5 py-1 text-[9px] font-bold bg-blue-600 text-white rounded hover:bg-blue-700"
              title="Client changed their mind — move back to Issued (validity restarts from today)">
              ↩ Back to Issued
            </button>
          )}
          {sow.status === 'Cancelled' && (
            <button onClick={revertCancelledToDraft}
              className="px-2.5 py-1 text-[9px] font-bold bg-gray-600 text-white rounded hover:bg-gray-700"
              title="Reopen this SOW for editing — clears the cancellation">
              ↩ Back to Draft
            </button>
          )}
          {sow.status === 'Expired' && (<>
            <button onClick={revalidateExpiredSOW}
              className="px-2.5 py-1 text-[9px] font-bold bg-blue-600 text-white rounded hover:bg-blue-700"
              title="Move back to Issued and restart the validity clock — skips the full issue approval workflow, but milestone costs are still re-checked">
              ↻ Revalidate
            </button>
            <button onClick={declineExpiredSOW}
              className="px-2.5 py-1 text-[9px] font-semibold border border-red-300 text-red-600 rounded hover:bg-red-50"
              title="Client made a decision — records the bid as Not Won">
              Not Won
            </button>
            <button onClick={cancelExpiredSOW}
              className="px-2.5 py-1 text-[9px] font-semibold border border-red-300 text-red-600 rounded hover:bg-red-50"
              title="No longer a live opportunity — sets the SOW to Cancelled">
              Cancel
            </button>
          </>)}
          <button onClick={() => setShowEditSOW(true)} className="px-2 py-1 text-[9px] font-semibold border border-gray-200 rounded bg-white hover:bg-gray-50">✏ Edit</button>
          </>)}
        </div>
      </div>

      {/* Milestone sum warning */}
      {seesMoney && milestones.length > 0 && Math.abs(milestoneGap) >= MILESTONE_GAP_TOLERANCE && (
        <div className={`mb-2 px-3 py-1.5 rounded flex items-center gap-2 text-[9px] font-semibold ${milestoneGap > 0 ? 'bg-amber-50 border border-amber-300 text-amber-700' : 'bg-red-50 border border-red-300 text-red-700'}`}>
          ⚠ Milestone sum {formatMoney(milestoneSum, 2)} {milestoneGap > 0 ? 'exceeds' : 'is below'} SOW total {formatMoney(sow.total_excl_vat, 2)} — difference {formatMoney(Math.abs(milestoneGap), 2)}. Manual verification required.
        </div>
      )}

      {/* Stats bar */}
      {seesMoney && <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-3">
        {[
          ['Total',      formatMoney(sow.total_excl_vat, 2), 'text-navy'],
          ['Milestones', formatMoney(milestoneSum, 2), milestones.length > 0 && Math.abs(milestoneGap) >= MILESTONE_GAP_TOLERANCE ? 'text-amber-500' : 'text-navy'],
          ['Invoiced',   formatMoney(sow.invoiced_sum, 2), 'text-navy'],
          ['Remaining',  formatMoney(sow.remaining_to_invoice, 2), 'text-amber-500'],
          // Resources/Hardware totals here include unreviewed/uncommitted lines
          // (e.g. legacy AI extraction still pending catalogue match) and are
          // shown for visibility only — they aren't a source of truth yet.
          ['Resources',  formatMoney(sow.total_resource_value || resourceTotal, 2), 'text-blue-600', 'For info only — not used in calculations, can’t be relied on as accurate yet.'],
          ['Hardware',   formatMoney((sow.total_hardware_value || (hardwareTotal - legacyUnmatchedHardwareTotal)) + legacyUnmatchedHardwareTotal, 2), 'text-emerald-600', 'For info only — not used in calculations, can’t be relied on as accurate yet.'],
        ].map(([label, val, cls, note]) => (
          <div key={label} className="bg-white border border-gray-200 rounded p-2">
            <div className="text-[8px] text-gray-500 uppercase font-bold">{label}</div>
            <div className={`text-[11px] sm:text-sm font-extrabold truncate ${cls}`}>{val}</div>
            {note && <div className="text-[7px] text-gray-400 leading-tight mt-0.5">{note}</div>}
          </div>
        ))}
      </div>}

      {/* Tabs */}
      <div className="flex items-end border-b border-gray-200 mb-3 overflow-x-auto">
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-3 py-1.5 text-[10px] font-medium border-b-2 -mb-px whitespace-nowrap ${tab === t ? 'text-gold border-gold font-semibold' : 'text-gray-400 border-transparent hover:text-gray-600'}`}>
            {tabLabel(t)}
          </button>
        ))}
        {/* QA Review hidden from main tabs when Active — show small link at right */}
        {sow.status === 'Active' && (
          <button onClick={() => setTab('qa-review')}
            className={`ml-auto px-2 py-1 text-[8px] -mb-px whitespace-nowrap border-b-2 transition-colors ${tab === 'qa-review' ? 'text-gold border-gold font-semibold' : 'text-gray-300 border-transparent hover:text-gray-500'}`}
            title="View QA Review">
            QA ↗
          </button>
        )}
      </div>

      {/* ── QA REVIEW ── */}
      {tab === 'qa-review' && (() => {
        const userRoles = parseRoles(currentUser?.user_role);
        const isAdminUser = userRoles.includes('admin');

        const canApprove = (step) => {
          if (isAdminUser) return true;
          if (step.required_role && !userRoles.includes(step.required_role)) return false;
          if (step.required_team_id && currentUser?.team_id !== step.required_team_id) return false;
          return true;
        };

        const handleApprove = async (step) => {
          try {
            await api.post(`sows/${detailId}/qa/${step.id}`, { action: 'approve' });
            loadQa();
          } catch (e) { toast.error(e.data?.error || e.message || 'Failed to approve'); }
        };

        const handleReset = async (step) => {
          if (!confirm(`Reset approval for "${step.name}"?`)) return;
          try {
            await api.post(`sows/${detailId}/qa/${step.id}`, { action: 'reset' });
            loadQa();
          } catch (e) { toast.error(e.data?.error || e.message || 'Failed to reset'); }
        };

        const allApproved = qaTotal > 0 && qaPendingCount === 0;

        return (
          <div>
            {/* Header bar */}
            <div className={`mb-4 p-3 rounded-lg border flex items-center gap-3 ${
              allApproved ? 'bg-emerald-50 border-emerald-300' : qaPendingCount > 0 ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'
            }`}>
              <div className="flex-1">
                <div className={`text-[10px] font-bold ${allApproved ? 'text-emerald-700' : 'text-gray-700'}`}>
                  {allApproved ? '✓ All QA steps approved — ready to issue' : `${qaPendingCount} of ${qaTotal} step${qaTotal !== 1 ? 's' : ''} pending approval`}
                </div>
                <div className="text-[8px] text-gray-500 mt-0.5">All steps must be approved before this Opportunity can be issued to the client.</div>
              </div>
              {sow.status === 'Draft' && canEdit && (
                <button onClick={issueSOW}
                  disabled={qaPendingCount > 0}
                  className={`px-3 py-1.5 text-[9px] font-bold rounded transition-all ${
                    qaPendingCount === 0 ? 'bg-blue-600 text-white hover:bg-blue-700' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                  }`}>
                  Issue Opportunity →
                </button>
              )}
            </div>

            {/* Step list */}
            {qaReview === null ? (
              <div className="text-center py-8 text-[10px] text-gray-400">Loading…</div>
            ) : qaReview.length === 0 ? (
              <div className="text-center py-8 text-[10px] text-gray-400">No QA steps configured. Add steps in Admin → QA Review Steps.</div>
            ) : (
              <div className="space-y-3">
                {qaReview.map((step, idx) => {
                  const approved = step.approval?.status === 'approved';
                  const able = canApprove(step);
                  const adminOverride = isAdminUser && !(!step.required_role && !step.required_team_id);
                  return (
                    <div key={step.id} className={`bg-white border rounded-lg overflow-hidden ${approved ? 'border-emerald-300' : 'border-gray-200'}`}>
                      <div className="flex items-center gap-3 px-4 py-3">
                        {/* Step number + status indicator */}
                        <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[9px] font-bold flex-shrink-0 ${
                          approved ? 'bg-emerald-500 text-emerald-950' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {approved ? '✓' : idx + 1}
                        </div>

                        {/* Step info */}
                        <div className="flex-1 min-w-0">
                          <div className="text-[10px] font-bold text-navy">{step.name}</div>
                          {step.description && <div className="text-[8px] text-gray-500">{step.description}</div>}
                          <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                            {step.required_role && (
                              <span className="text-[7px] bg-blue-100 text-blue-700 font-bold px-1.5 py-0.5 rounded">
                                Role: {step.required_role}
                              </span>
                            )}
                            {step.team_name && (
                              <span className="text-[7px] bg-purple-100 text-purple-700 font-bold px-1.5 py-0.5 rounded">
                                Team: {step.team_name}
                              </span>
                            )}
                            {!step.required_role && !step.required_team_id && (
                              <span className="text-[7px] text-gray-400">Any team member</span>
                            )}
                          </div>
                        </div>

                        {/* Approval status */}
                        <div className="flex-shrink-0 text-right">
                          {approved ? (
                            <div>
                              <div className="flex items-center gap-1.5 justify-end">
                                <span className="text-[8px] font-bold text-emerald-700">Approved</span>
                                {step.approval.is_admin_override ? (
                                  <span className="text-[7px] bg-amber-100 text-amber-700 font-bold px-1.5 py-0.5 rounded" title="Admin overrule — normal eligibility rules were bypassed">
                                    ⚠ Admin Override
                                  </span>
                                ) : null}
                              </div>
                              <div className="text-[7px] text-gray-500 mt-0.5">{step.approval.reviewer_name}</div>
                              <div className="text-[7px] text-gray-400">
                                {fmtDateTimeLong(step.approval.reviewed_at)}
                              </div>
                              {isAdminUser && (
                                <button onClick={() => handleReset(step)}
                                  className="text-[7px] text-gray-400 hover:text-red-500 underline mt-1">
                                  Reset
                                </button>
                              )}
                            </div>
                          ) : (
                            <div className="flex flex-col items-end gap-1">
                              {able ? (
                                <button onClick={() => handleApprove(step)}
                                  className={`px-3 py-1.5 text-[8px] font-bold rounded transition-all ${
                                    adminOverride
                                      ? 'bg-amber-500 text-amber-950 hover:bg-amber-600'
                                      : 'bg-emerald-600 text-white hover:bg-emerald-700'
                                  }`}>
                                  {adminOverride ? '⚠ Approve (Admin Override)' : 'Approve ✓'}
                                </button>
                              ) : (
                                <div className="text-right">
                                  <div className="text-[8px] font-semibold text-gray-400">Awaiting approval</div>
                                  <div className="text-[7px] text-gray-400">
                                    {step.required_role ? `Requires '${step.required_role}' role` : step.team_name ? `Requires '${step.team_name}' team membership` : ''}
                                  </div>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })()}

      {/* ── MILESTONES ── */}
      {tab === 'milestones' && (
        <div>
          <div className="flex items-center justify-between mb-2">
            {canEditMilestones && (
              <div className="text-[8px] text-gray-400">Click contract or forecast date to edit inline</div>
            )}
            <div className="ml-auto flex items-center gap-2">
              {rollupItemCount > 0 && (
                <button
                  onClick={() => downloadCallOffExport(sow, milestones, rollup)}
                  className="flex items-center gap-1 px-2 py-1 text-[9px] font-semibold border border-gray-200 rounded text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                  title="Download every call-off milestone counted against this framework, with a summary per pot, as an Excel workbook"
                >
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>Export call-offs
                </button>
              )}
              {milestones.length > 0 && (
                <button
                  onClick={copyMilestoneTable}
                  className="flex items-center gap-1 px-2 py-1 text-[9px] font-semibold border border-gray-200 rounded text-gray-600 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                  title="Copy milestone table for pasting into email"
                >
                  {tableCopied
                    ? <><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-500"><polyline points="20 6 9 17 4 12"/></svg><span className="text-emerald-600">Copied!</span></>
                    : <><svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>Copy for email</>
                  }
                </button>
              )}
              {canEditMilestones && (
                <button onClick={() => setShowAddMilestone(true)} className="px-2 py-1 text-[9px] font-semibold bg-navy text-white rounded hover:bg-navy/80">+ Add Milestone</button>
              )}
            </div>
          </div>
          <div className="bg-white border border-gray-200 rounded overflow-x-auto">
            <table className="w-full">
              <thead><tr>
                {[['Milestone', 'code'], ['Status', 'status'], ['Contract', 'contract'], ['Forecast', 'forecast'], ['Description', 'description'], ...(seesMoney ? [['Amount', 'amount']] : []),
                  ...(rollup ? [['Called off', 'called_off'], ['Invoiced', 'invoiced'], ['Left', 'left']] : []),
                  ['Coupa approved', 'coupa'], ['PM', 'pm'], ['', null], ...(canEditMilestones ? [['', null]] : [])].map(([h, col], i) => {
                  const align = MONEY_HEADINGS.includes(h) ? 'text-right' : 'text-left';
                  if (!col) return <th key={i} className={`px-2 py-1 ${align} text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50`} />;
                  const active = milestoneSort.col === col;
                  return (
                    <th key={i} onClick={() => setMilestoneSort(s => nextMilestoneSort(s, col))}
                      aria-sort={active ? (milestoneSort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                      title={`Sort by ${h.toLowerCase()}`}
                      className={`px-2 py-1 ${align} text-[8px] uppercase font-bold border-b bg-gray-50 cursor-pointer select-none whitespace-nowrap hover:bg-gray-100 ${active ? 'text-gold' : 'text-gray-500'}`}>
                      {h}{active ? (milestoneSort.dir === 'asc' ? ' ↑' : ' ↓') : ''}
                    </th>
                  );
                })}
              </tr></thead>
              <tbody>
                {sortedMilestones.map(m => {
                  const settled = ['Invoiced', 'Paid', 'Cancelled', 'Split', 'Credit Noted'].includes(m.status);
                  const todayStr = new Date().toISOString().split('T')[0];
                  const slipped = !settled && m.forecast_date && m.contract_date && m.forecast_date > m.contract_date;
                  const overdue = !settled && m.forecast_date && m.forecast_date < todayStr;
                  return (
                    <MilestoneRow
                      key={m.id}
                      milestone={m}
                      highlight={highlightMs === m.id}
                      slipped={slipped}
                      overdue={overdue}
                      canEdit={canEditMilestones}
                      onEdit={() => setEditMilestone(m)}
                      onDelete={() => deleteMilestone(m.id)}
                      onHistory={() => { ensureDateHistory(); setMilHistoryFor(m); }}
                      onInlineSave={saveMilestone}
                      rollup={rollup ? rollup.lines[m.id] || null : undefined}
                      seesMoney={seesMoney}
                      canRecordCoupa={canRecordCoupa}
                    />
                  );
                })}
                {rollup?.unmatched.items.length > 0 && <UnmatchedCallOffsRow line={rollup.unmatched} canEdit={canEditMilestones} />}
                {milestones.length === 0 && <tr><td colSpan={8 + (seesMoney ? 1 : 0) + (rollup ? 3 : 0) + (canEditMilestones ? 1 : 0)} className="text-center py-6 text-[10px] text-gray-400">No milestones yet.</td></tr>}
              </tbody>
              {seesMoney && milestones.length > 0 && (
                <tfoot><tr className="bg-gray-50">
                  <td colSpan="5" className="px-2 py-1 text-[9px] font-bold text-gray-600 text-right">Milestones Total</td>
                  <td className={`px-2 py-1 text-[10px] font-extrabold text-right ${Math.abs(milestoneGap) >= MILESTONE_GAP_TOLERANCE ? 'text-amber-500' : 'text-navy'}`}>{formatMoney(milestoneSum, 2)}</td>
                  {rollup && <>
                    <td className="px-2 py-1 text-[10px] font-extrabold text-right text-navy">{formatMoney(rollupTotals.called_off, 2)}</td>
                    <td className="px-2 py-1 text-[10px] font-extrabold text-right text-navy">{formatMoney(rollupTotals.invoiced, 2)}</td>
                    <td className={`px-2 py-1 text-[10px] font-extrabold text-right ${milestoneSum - rollupTotals.called_off < 0 ? 'text-red-600' : 'text-navy'}`}>{formatMoney(milestoneSum - rollupTotals.called_off, 2)}</td>
                  </>}
                  <td colSpan={canEditMilestones ? 4 : 3} />
                </tr></tfoot>
              )}
            </table>
          </div>

        </div>
      )}

      {/* ── MILESTONE HISTORY MODAL (inline, triggered from milestone History button) ── */}
      {milHistoryFor && (
        <Modal title={`History — ${milHistoryFor.milestone_code} · ${milHistoryFor.description}`} onClose={() => setMilHistoryFor(null)} width="max-w-3xl">
          {dateHistory === null ? (
            <div className="text-center py-8 text-[10px] text-gray-400">Loading…</div>
          ) : (() => {
            const changes = (dateHistory.changes || []).filter(c => c.milestone_id === milHistoryFor.id);
            if (changes.length === 0) return <div className="text-center py-8 text-[10px] text-gray-400">No changes recorded for this milestone.</div>;
            const FIELD_META = {
              contract_date: { label: 'Contract',    cls: 'bg-amber-100 text-amber-700',   isDate: true  },
              forecast_date: { label: 'Forecast',    cls: 'bg-blue-100 text-blue-700',     isDate: true  },
              status:        { label: 'Status',      cls: 'bg-indigo-100 text-indigo-700', isDate: false },
              milestone_code:{ label: 'Code',        cls: 'bg-purple-100 text-purple-700', isDate: false },
              description:   { label: 'Description', cls: 'bg-gray-100 text-gray-700',     isDate: false },
              invoice_amount_excl:     { label: 'Invoice Amount',   cls: 'bg-emerald-100 text-emerald-700', isDate: false },
              invoice_amount_incl:     { label: 'Invoice Amount (incl)', cls: 'bg-emerald-100 text-emerald-700', isDate: false },
              invoice_number:          { label: 'Invoice No.',      cls: 'bg-emerald-100 text-emerald-700', isDate: false },
              formal_invoice_date:     { label: 'Invoiced',         cls: 'bg-emerald-100 text-emerald-700', isDate: true  },
              paid_date:               { label: 'Paid',             cls: 'bg-emerald-100 text-emerald-700', isDate: true  },
              est_pay_date:            { label: 'Est. Pay Date',    cls: 'bg-emerald-100 text-emerald-700', isDate: true  },
              customer_po:             { label: 'Customer PO',      cls: 'bg-gray-100 text-gray-700',       isDate: false },
              po_line_item:            { label: 'PO Line Item',     cls: 'bg-gray-100 text-gray-700',       isDate: false },
              coupa_approved:          { label: 'Coupa Approved',   cls: 'bg-emerald-100 text-emerald-700', isDate: true  },
              delivery_comments:       { label: 'Comments',         cls: 'bg-gray-100 text-gray-700',       isDate: false },
              missed_milestone_reason: { label: 'Missed Reason',    cls: 'bg-red-100 text-red-700',         isDate: false },
              credit_note_number:      { label: 'Credit Note',      cls: 'bg-red-100 text-red-700',         isDate: false },
              credit_noted_date:       { label: 'Credit Noted',     cls: 'bg-red-100 text-red-700',         isDate: true  },
              labour_cost_to_complete:    { label: 'Labour to complete',    cls: 'bg-blue-100 text-blue-700',     isDate: false },
              materials_cost_to_complete: { label: 'Materials to complete', cls: 'bg-violet-100 text-violet-700', isDate: false },
            };
            // Lifecycle actions recorded without a field diff (e.g. the milestone
            // being added in the first place) — shown as a single-badge event row.
            const ACTION_META = {
              created:               { label: '✓ Milestone added',   cls: 'bg-emerald-100 text-emerald-700' },
              deleted:               { label: 'Milestone deleted',   cls: 'bg-red-100 text-red-700' },
              created_via_ccn:       { label: '✓ Added via CCN',     cls: 'bg-emerald-100 text-emerald-700' },
              cancelled_via_ccn:     { label: 'Cancelled via CCN',   cls: 'bg-red-100 text-red-700' },
              amended_via_ccn:       { label: 'Amended via CCN',     cls: 'bg-indigo-100 text-indigo-700' },
              zoho_reconcile_update: { label: 'Reconciled',          cls: 'bg-gray-100 text-gray-600' },
            };
            return (
              <table className="w-full text-left">
                <thead><tr>
                  {['When','By','Field','Previous','New Value','Movement'].map(h => (
                    <th key={h} className="px-3 py-1.5 text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {changes.map((c, i) => {
                    if (!c.field_name) {
                      const am = ACTION_META[c.action] || { label: c.action, cls: 'bg-gray-100 text-gray-600' };
                      return (
                        <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-3 py-1.5 text-[9px] text-gray-500 whitespace-nowrap">{c.changed_at ? fmtDateTimeLong(c.changed_at) : '—'}</td>
                          <td className="px-3 py-1.5 text-[9px] font-semibold">{c.changed_by || '—'}</td>
                          <td className="px-3 py-1.5" colSpan={4}><span className={`text-[8px] font-bold px-1.5 py-0.5 rounded ${am.cls}`}>{am.label}</span></td>
                        </tr>
                      );
                    }
                    const meta = FIELD_META[c.field_name] || { label: c.field_name, cls: 'bg-gray-100 text-gray-600', isDate: false };
                    const oldD = meta.isDate && c.old_value ? new Date(c.old_value) : null;
                    const newD = meta.isDate && c.new_value ? new Date(c.new_value) : null;
                    const movDays = oldD && newD ? Math.round((newD - oldD) / 86400000) : null;
                    // action is "updated" for a normal manual edit, or "updated (source)"
                    // when it came from a bulk/system operation — surface that source.
                    const sourceMatch = /^updated\s*\((.+)\)$/i.exec(c.action || '');
                    return (
                      <tr key={i} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="px-3 py-1.5 text-[9px] text-gray-500 whitespace-nowrap">{c.changed_at ? fmtDateTimeLong(c.changed_at) : '—'}</td>
                        <td className="px-3 py-1.5 text-[9px] font-semibold">
                          {c.changed_by || '—'}
                          {sourceMatch && <div className="text-[7px] font-semibold text-gray-400 normal-case">via {sourceMatch[1]}</div>}
                        </td>
                        <td className="px-3 py-1.5"><span className={`text-[8px] font-bold px-1.5 py-0.5 rounded ${meta.cls}`}>{meta.label}</span></td>
                        {/* An amount change carries no values for anybody not shown SOW money. */}
                        <td className="px-3 py-1.5 text-[9px] text-gray-400 line-through">{c.old_value === undefined && c.new_value === undefined ? '' : meta.isDate ? (c.old_value ? formatDate(c.old_value) : 'not set') : (c.old_value || 'not set')}</td>
                        <td className="px-3 py-1.5 text-[9px] font-semibold">{c.old_value === undefined && c.new_value === undefined ? '' : meta.isDate ? (c.new_value ? formatDate(c.new_value) : 'cleared') : (c.new_value || 'cleared')}</td>
                        <td className="px-3 py-1.5">{movDays !== null ? <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded ${movDays > 0 ? 'bg-red-50 text-red-600' : movDays < 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-gray-100 text-gray-500'}`}>{movDays > 0 ? `+${movDays}d` : movDays < 0 ? `${movDays}d` : 'no change'}</span> : '—'}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            );
          })()}
        </Modal>
      )}

      {/* ── RESOURCES ── */}
      {tab === 'resources' && (
        <div>
          {/* Draft guard — block resource add until checklist + plan are defined */}
          {sow.status === 'Draft' && !resourceAddOverride && canEdit && (
            <div className="mb-4 border border-amber-300 bg-amber-50 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-1">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-amber-600 flex-shrink-0"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
                <span className="text-[10px] font-bold text-amber-800">Define Checklist &amp; Plan Before Adding Resources</span>
              </div>
              <p className="text-[9px] text-amber-700 mb-3 leading-relaxed">
                Resources are best defined after the project checklist and delivery plan are in place — this ensures resource effort aligns with the actual work required.
                {repeatableTasks.filter(t => t.is_applicable !== 0).length > 0
                  ? <span className="block mt-1 text-amber-600">✓ Checklist has {repeatableTasks.filter(t => t.is_applicable !== 0).length} applicable items.</span>
                  : <span className="block mt-1 text-red-600">✗ No checklist items are marked as applicable yet.</span>
                }
              </p>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={handleAutoPopulateResources}
                  disabled={resourceAutoPopulating}
                  className="px-3 py-1.5 text-[9px] font-semibold bg-navy text-white rounded hover:bg-navy/90 disabled:opacity-50 flex items-center gap-1.5"
                >
                  {resourceAutoPopulating ? 'Populating…' : (
                    <>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/><polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/></svg>
                      Auto-populate from Plan
                    </>
                  )}
                </button>
                <button
                  onClick={() => setResourceAddOverride(true)}
                  className="px-3 py-1.5 text-[9px] font-semibold border border-amber-400 text-amber-700 rounded hover:bg-amber-100"
                >Override — Add Manually</button>
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 mb-3 p-2 bg-gray-50 border border-gray-200 rounded">
            <span className="text-[8px] font-bold text-gray-500 uppercase tracking-wide">Sections:</span>
            {sections.map(sec => (
              <div key={sec} className="flex items-center gap-0.5">
                {editingSection === sec ? (
                  <input
                    className="border border-gold rounded px-1.5 py-0.5 text-[9px] w-28 font-semibold focus:outline-none"
                    value={sectionEditValue}
                    onChange={e => setSectionEditValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') confirmRenameSection(sec); if (e.key === 'Escape') setEditingSection(null); }}
                    onBlur={() => confirmRenameSection(sec)}
                    autoFocus
                  />
                ) : (
                  <span className="text-[9px] px-2 py-0.5 bg-navy/10 text-navy rounded font-semibold">{sec}</span>
                )}
                {canEdit && (
                <button
                  onClick={() => { setEditingSection(sec); setSectionEditValue(sec); }}
                  title="Rename section"
                  className="text-gray-300 hover:text-gold text-[10px] leading-none ml-0.5"
                >✎</button>
                )}
              </div>
            ))}
            {canEdit && (
            <div className="flex items-center gap-1 ml-1">
              <input
                className="border border-gray-300 rounded px-1.5 py-0.5 text-[9px] w-24 focus:outline-none focus:border-gold"
                placeholder="New section..."
                value={newSectionInput}
                onChange={e => setNewSectionInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && newSectionInput.trim()) addSection(); }}
              />
              <button
                onClick={addSection}
                disabled={!newSectionInput.trim()}
                className="px-1.5 py-0.5 text-[8px] font-bold bg-navy/10 text-navy rounded hover:bg-navy/20 disabled:opacity-30"
              >+ Add</button>
            </div>
            )}
            {canEdit && (sow.status !== 'Draft' || resourceAddOverride) && (
              <button
                onClick={() => setQuickAddResources(v => !v)}
                title="Type-and-add row per section — faster than the Add Line modal for pricing up several lines at once"
                className={`ml-auto px-2 py-0.5 text-[8px] font-bold rounded ${quickAddResources ? 'bg-navy text-white' : 'bg-white border border-gray-200 text-gray-500 hover:bg-gray-50'}`}
              >⚡ Quick Add {quickAddResources ? 'On' : 'Off'}</button>
            )}
          </div>

          {/* Sections with resource lines */}
          <div className="space-y-3">
            {allSectionNames.map(section => {
              const lines = resourceSections[section] || [];
              const sTotal = lines.reduce((s, l) => s + (l.line_total || 0), 0);
              const sDays  = lines.reduce((s, l) => s + (l.days || 0), 0);
              return (
                <div key={section} className="bg-white border border-gray-200 rounded overflow-hidden">
                  <div className="px-3 py-1.5 bg-navy/5 border-b border-gray-200 flex items-center justify-between">
                    <span className="text-[10px] font-bold text-navy uppercase tracking-wide">{section}</span>
                    <div className="flex items-center gap-3">
                      {lines.length > 0 && <span className="text-[9px] text-gray-500">{sDays}d{seesMoney && <> · <span className="font-bold text-navy">{formatMoney(sTotal)}</span></>}</span>}
                      {canEdit && (sow.status !== 'Draft' || resourceAddOverride) && (
                        <button
                          onClick={() => { setAddResourceSection(section); setShowAddResource(true); }}
                          className="px-2 py-0.5 text-[8px] font-semibold bg-navy text-white rounded hover:bg-navy/80"
                        >+ Add Line</button>
                      )}
                    </div>
                  </div>
                  {lines.length > 0 ? (
                    <table className="w-full">
                      <thead><tr>
                        {(seesMoney ? ['Band','Description','Day Rate','Days','Total',''] : ['Band','Description','Days','']).map(h => (
                          <th key={h} className={`px-2 py-1 text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50 ${['Day Rate','Days','Total'].includes(h) ? 'text-right' : 'text-left'}`}>{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {lines.map(r => (
                          <tr key={r.id} className="hover:bg-gold/5 border-b border-gray-100">
                            <td className="px-2 py-1.5">
                              <div className="flex items-center gap-1 flex-wrap">
                                <span className="text-[9px] font-mono font-bold text-navy bg-navy/10 px-1.5 py-0.5 rounded">{r.band_code}</span>
                                {r.source === 'legacy_extraction' && (
                                  <span className="text-[7px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 uppercase tracking-wide"
                                    title="Auto-loaded from AI extraction of the original SOW document — not yet manually reviewed">
                                    🤖 AI Extracted
                                  </span>
                                )}
                              </div>
                              <div className="text-[8px] text-gray-400 mt-0.5">{r.band_name}</div>
                            </td>
                            <td className="px-2 py-1.5 text-[9px] text-gray-600">{r.description || '-'}</td>
                            {seesMoney && <td className="px-2 py-1.5 text-[10px] text-right">{formatMoney(r.day_rate, 2)}</td>}
                            <td className="px-2 py-1.5 text-[10px] font-semibold text-right">{r.days}</td>
                            {seesMoney && <td className="px-2 py-1.5 text-[10px] font-bold text-right text-navy">{formatMoney(r.line_total)}</td>}
                            <td className="px-2 py-1.5 text-center whitespace-nowrap">
                              {canEdit && (<>
                              <button onClick={() => setEditResourceLine(r)} className="text-[8px] px-1.5 py-0.5 rounded border border-gray-200 hover:bg-gray-50 text-gray-500 mr-1">Edit</button>
                              <button onClick={() => deleteResourceLine(r.id)} className="text-red-400 hover:text-red-600 font-bold">×</button>
                              </>)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="px-3 py-4 text-center text-[9px] text-gray-400">{canEdit ? 'No lines yet — click + Add Line above' : 'No lines yet'}</div>
                  )}
                  {canEdit && quickAddResources && (sow.status !== 'Draft' || resourceAddOverride) && (
                    <QuickAddResourceRow detailId={detailId} section={section} onAdded={async () => setSow(await api.get(`sows/${detailId}`))} />
                  )}
                </div>
              );
            })}
          </div>

          {/* Grand total */}
          {resourceLines.length > 0 && (
            <div className="border border-navy/15 bg-navy/5 rounded p-2 flex items-center justify-between mt-3">
              <div className="text-[10px] text-gray-500">Total Resources</div>
              <div className="flex gap-6 text-right">
                <div><div className="text-[8px] text-gray-400 uppercase">Days</div><div className="text-sm font-semibold text-navy">{resourceLines.reduce((s, l) => s + (l.days || 0), 0)}</div></div>
                {seesMoney && <div><div className="text-[8px] text-gray-400 uppercase">Value</div><div className="text-sm font-semibold text-navy">{formatMoney(resourceTotal)}</div></div>}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── HARDWARE ── */}
      {tab === 'hardware' && (
        <div>
          <div className="flex justify-end gap-2 mb-2">
            {canRaisePo && selectedHwLines.size > 0 && (
              <button onClick={() => setShowCreatePO(true)} className="px-2 py-1 text-[9px] font-semibold bg-navy text-white rounded hover:bg-navy/80">
                Create PO ({selectedHwLines.size} item{selectedHwLines.size !== 1 ? 's' : ''})
              </button>
            )}
            {canEdit && (<>
            <button onClick={() => setShowAddPassThrough(true)} className="px-2 py-1 text-[9px] font-semibold border border-blue-300 text-blue-700 rounded hover:bg-blue-50">+ PO Pass-through</button>
            <button onClick={() => setShowAddHardware(true)} className="px-2 py-1 text-[9px] font-semibold bg-navy text-white rounded hover:bg-navy/80">+ Add Item</button>
            <button
              onClick={() => setQuickAddHardware(v => !v)}
              title="Type-and-add row — faster than the Add Item modal for pricing up several lines at once"
              className={`px-2 py-1 text-[9px] font-bold rounded ${quickAddHardware ? 'bg-navy text-white' : 'bg-white border border-gray-200 text-gray-500 hover:bg-gray-50'}`}
            >⚡ Quick Add {quickAddHardware ? 'On' : 'Off'}</button>
            </>)}
          </div>
          {canEdit && quickAddHardware && (
            <QuickAddHardwareRow detailId={detailId} onAdded={async () => setSow(await api.get(`sows/${detailId}`))} />
          )}
          <div className="bg-white border border-gray-200 rounded overflow-x-auto">
            <table className="w-full">
              <thead><tr>
                <th className="px-2 py-1 w-6 border-b bg-gray-50">
                  <input type="checkbox" className="w-3 h-3 cursor-pointer"
                    checked={hardwareLines.length > 0 && hardwareLines.every(h => selectedHwLines.has(h.id))}
                    onChange={e => setSelectedHwLines(e.target.checked ? new Set(hardwareLines.map(h => h.id)) : new Set())}
                  />
                </th>
                {['Item','Qty', ...(seesTradeCost ? ['Cost','Margin%'] : []), ...(seesMoney ? ['Sell','Line Sell'] : []),'Milestone','Status',''].map(h => (
                  <th key={h} className={`px-2 py-1 text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50 ${['Qty','Cost','Margin%','Sell','Line Sell'].includes(h) ? 'text-right' : 'text-left'}`}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {hardwareLines.map(h => (<>
                  <tr key={h.id} className={`hover:bg-gold/5 border-b border-gray-100 ${selectedHwLines.has(h.id) ? 'bg-navy/5' : ''}`}>
                    <td className="px-2 py-1 w-6">
                      <input type="checkbox" className="w-3 h-3 cursor-pointer"
                        checked={selectedHwLines.has(h.id)}
                        onChange={e => {
                          const next = new Set(selectedHwLines);
                          e.target.checked ? next.add(h.id) : next.delete(h.id);
                          setSelectedHwLines(next);
                        }}
                      />
                    </td>
                    <td className="px-2 py-1">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {h.bundle_id && (
                          <button onClick={() => toggleBundleExpand(h)} className="text-violet-600 text-[10px] leading-none flex-shrink-0" title="Show bundle breakdown">
                            {expandedBundleLines.has(h.id) ? '▼' : '▶'}
                          </button>
                        )}
                        <span className="text-[10px] font-semibold">{h.item_description}</span>
                        {h.bundle_id && (
                          <span className="text-[7px] font-bold px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 uppercase tracking-wide">⊛ Bundle</span>
                        )}
                        {h.source_po_id && (
                          <span className="text-[7px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 uppercase tracking-wide">PO Pass-through</span>
                        )}
                        {h.source === 'legacy_extraction' && (
                          <span className="text-[7px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 uppercase tracking-wide"
                            title="Auto-loaded from AI extraction of the original SOW document — not yet manually reviewed">
                            🤖 AI Extracted
                          </span>
                        )}
                        {h.renewal_count > 0 ? (
                          <button
                            onClick={() => canEdit && setRenewalsFor(h)}
                            title={canEdit ? 'View / manage service renewals' : undefined}
                            className={`text-[7px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide ${
                              !h.next_renewal_end ? 'bg-blue-100 text-blue-700'
                              : h.next_renewal_end < new Date().toISOString().slice(0, 10) ? 'bg-red-100 text-red-700'
                              : h.next_renewal_end <= new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10) ? 'bg-amber-100 text-amber-700'
                              : 'bg-blue-100 text-blue-700'
                            }`}
                          >
                            🔁 {h.renewal_count} renewal{h.renewal_count > 1 ? 's' : ''}{h.next_renewal_end ? ` · next ${formatDate(h.next_renewal_end)}` : ''}
                          </button>
                        ) : canEdit && (
                          <button onClick={() => setRenewalsFor(h)}
                            title="Add a service renewal (e.g. leased line, support contract) for this line"
                            className="text-[7px] font-semibold px-1.5 py-0.5 rounded border border-gray-200 text-gray-400 hover:text-navy hover:border-navy uppercase tracking-wide">
                            + Renewal
                          </button>
                        )}
                      </div>
                      {h.product_catalog_id && (
                        <div className="flex items-center gap-1 flex-wrap mt-0.5">
                          <a href={`#product-catalog/${h.product_catalog_id}`} target="_blank" rel="noopener noreferrer"
                            className="text-[8px] font-mono text-navy hover:underline" title="Open in Product Catalog (new tab)">
                            {h.catalog_code || 'View in catalogue'} ↗
                          </a>
                          {/* The line above carries a SOW-specific description; when it differs from the shared
                              catalogue product's canonical name, surface that name so the same code appearing
                              against different descriptions isn't confusing. */}
                          {h.catalog_description
                            && h.catalog_description.trim().toLowerCase() !== (h.item_description || '').trim().toLowerCase() && (
                            <span className="text-[8px] text-gray-400" title="Canonical catalogue product name — this line shows a SOW-specific description of the same catalogue item">
                              · {h.catalog_description}
                            </span>
                          )}
                        </div>
                      )}
                      {h.source_po_id ? (
                        <div className="text-[8px] text-blue-600 font-mono mt-0.5">
                          PO: {h.source_po_number} · {h.source_po_supplier_name}{h.pass_through_pct ? ` · ${h.pass_through_pct}% markup` : ''}
                        </div>
                      ) : (
                        <div className="text-[8px] text-gray-400">{h.manufacturer} {h.part_number}</div>
                      )}
                      {h.procurement_po_id && (
                        <div className="mt-0.5 flex items-center gap-1">
                          <button onClick={() => setPoQuickView(h.procurement_po_id)} className="text-[8px] font-mono text-navy hover:underline">{h.procurement_po_number}</button>
                          {h.procurement_po_supplier_name && <span className="text-[7px] text-gray-400">{h.procurement_po_supplier_name}</span>}
                        </div>
                      )}
                      {h.delivery_status === 'allocated' ? (
                        <div className="text-[7px] font-semibold mt-0.5 flex flex-col gap-0.5">
                          <div className="flex items-center gap-1 flex-wrap">
                            <svg viewBox="0 0 12 12" width="9" height="9" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-emerald-600"><polyline points="2,6 5,9 10,3"/></svg>
                            <span className="text-emerald-600">
                              {h.quantity > 1 ? `${h.quantity} units allocated` : 'Allocated'}
                            </span>
                            {/* Clarify whether item is pre-order (incoming) vs available (in_stock) */}
                            {h.inv_status === 'incoming' && (
                              <span className="px-1 py-0 rounded font-bold uppercase tracking-wide bg-amber-100 text-amber-700" title="Pre-allocated from a purchase order — not yet received">Pre-order</span>
                            )}
                          </div>
                          {/* Inventory reference — single item navigates to inventory, multi opens detail modal */}
                          {h.quantity > 1 ? (
                            <button
                              onClick={e => { e.stopPropagation(); setHwItemsLine(h); }}
                              className="ml-3 text-left hover:underline text-emerald-700 font-mono flex items-center gap-0.5"
                              title="View all allocated inventory items"
                            >
                              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>
                              View {h.quantity} items →
                            </button>
                          ) : (h.inv_asset_tag || h.inv_serial_number) ? (
                            <button
                              onClick={e => { e.stopPropagation(); navigate('inventory'); }}
                              className="ml-3 text-left hover:underline text-emerald-700 font-mono flex items-center gap-0.5"
                              title="View in Inventory"
                            >
                              <svg width="8" height="8" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                              {h.inv_asset_tag && <span>Tag: {h.inv_asset_tag}</span>}
                              {h.inv_asset_tag && h.inv_serial_number && <span className="text-gray-300 mx-1">|</span>}
                              {h.inv_serial_number && <span>S/N: {h.inv_serial_number}</span>}
                            </button>
                          ) : null}
                          {h.inv_expected_delivery && (
                            <div className="ml-3 flex items-center gap-0.5 text-[7px] text-amber-700 font-semibold">
                              <svg viewBox="0 0 12 12" width="8" height="8" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="1" y="2" width="10" height="9" rx="1"/><line x1="4" y1="1" x2="4" y2="3"/><line x1="8" y1="1" x2="8" y2="3"/><line x1="1" y1="5" x2="11" y2="5"/></svg>
                              ETA: {h.inv_expected_delivery}
                            </div>
                          )}
                        </div>
                      ) : h.available_in_stock != null ? (
                        <div className={`text-[7px] font-semibold mt-0.5 ${h.available_in_stock > 0 ? 'text-emerald-600' : 'text-gray-400'}`}>
                          {h.available_in_stock > 0 ? `✓ ${h.available_in_stock} available in stock` : '✗ none in stock'}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-2 py-1 text-[10px] text-right">
                      {editingQtyId === h.id ? (
                        <input
                          type="number" min="1" step="1"
                          className="w-14 text-right border border-navy rounded px-1 text-[9px]"
                          value={qtyEditVal}
                          onChange={e => setQtyEditVal(e.target.value)}
                          onBlur={() => saveQty(h)}
                          onKeyDown={e => { if (e.key === 'Enter') saveQty(h); if (e.key === 'Escape') setEditingQtyId(null); }}
                          autoFocus
                        />
                      ) : (
                        <span
                          className={`cursor-pointer hover:underline ${canEdit ? 'hover:text-navy' : ''}`}
                          title={canEdit ? 'Click to edit quantity' : undefined}
                          onClick={() => { if (canEdit) { setEditingQtyId(h.id); setQtyEditVal(String(h.quantity)); } }}
                        >{h.quantity}</span>
                      )}
                    </td>
                    {seesTradeCost && <td className="px-2 py-1 text-[10px] text-right">{formatMoney(h.unit_cost, 2)}</td>}
                    {seesTradeCost && <td className="px-2 py-1 text-[10px] font-semibold text-right" style={{ color: h.margin_pct > 15 ? '#10B981' : '#F59E0B' }}>
                      {editingMarginId === h.id ? (
                        <input
                          type="number" step="0.1" min="0" max="99.9"
                          className="w-14 text-right border border-gold rounded px-1 text-[9px]"
                          value={marginEditVal}
                          onChange={e => setMarginEditVal(e.target.value)}
                          onBlur={() => saveMargin(h.id)}
                          onKeyDown={e => { if (e.key === 'Enter') saveMargin(h.id); if (e.key === 'Escape') setEditingMarginId(null); }}
                          autoFocus
                        />
                      ) : (
                        <span
                          className={canEdit ? 'cursor-pointer hover:underline' : ''}
                          title={canEdit ? 'Click to edit margin %' : undefined}
                          onClick={() => { if (canEdit) { setEditingMarginId(h.id); setMarginEditVal(h.margin_pct?.toFixed(1) || '0'); } }}
                        >{h.margin_pct?.toFixed(1)}%</span>
                      )}
                    </td>}
                    {seesMoney && <td className="px-2 py-1 text-[10px] font-semibold text-right">{formatMoney(h.unit_sell, 2)}</td>}
                    {seesMoney && <td className="px-2 py-1 text-[10px] font-bold text-right text-navy">{formatMoney(h.line_sell)}</td>}
                    <td className="px-2 py-1">
                      {canEdit ? (
                        <select
                          className="text-[9px] border border-gray-200 rounded px-1 py-0.5 bg-white max-w-[110px]"
                          title="Milestone this hardware is delivered against; its sell value becomes that milestone's materials budget on the Costs tab"
                          value={h.milestone_id || ''}
                          onChange={async e => {
                            try {
                              await api.put(`sows/${detailId}/hardware-lines/${h.id}`, { milestone_id: e.target.value || null });
                              setSow(await api.get(`sows/${detailId}`));
                            } catch (err) { toast(err.message || 'Could not assign the milestone.', 'error'); }
                          }}
                        >
                          <option value="">—</option>
                          {milestones.filter(m => !['Cancelled', 'Split', 'Credit Noted'].includes(m.status)).map(m => (
                            <option key={m.id} value={m.id}>{m.milestone_code}</option>
                          ))}
                        </select>
                      ) : (
                        <span className="text-[9px] text-gray-600">{milestones.find(m => m.id === h.milestone_id)?.milestone_code || '—'}</span>
                      )}
                    </td>
                    <td className="px-2 py-1">
                      <HardwareStatusCell
                        line={h}
                        canEdit={canEdit}
                        onUnallocate={() => unallocateHardwareLine(h.id)}
                        onChangeStatus={(lineId) => setStatusOverride({ lineId, currentStatus: h.delivery_status, newStatus: h.delivery_status, reason: '' })}
                        onDetachPO={() => detachPOFromLine(h.id)}
                      />
                    </td>
                    <td className="px-2 py-1 text-center">{canEdit && <button onClick={() => deleteHardwareLine(h.id)} className="text-red-400 hover:text-red-600 font-bold">×</button>}</td>
                  </tr>
                  {h.bundle_id && expandedBundleLines.has(h.id) && (
                    <tr key={`${h.id}-bundle`} className="bg-violet-50/60 border-b border-violet-100">
                      <td colSpan={hwColCount} className="px-4 py-2">
                        {bundleLineItems[h.id] ? (
                          <div>
                            <div className="text-[7.5px] font-bold text-violet-700 uppercase mb-1.5">
                              Bundle breakdown{h.quantity > 1 ? ` · ${h.quantity}× bundle` : ''}
                            </div>
                            <table className="w-full text-[8px]">
                              <thead><tr>
                                <th className="px-1 py-0.5 text-left text-[7px] uppercase text-gray-400 font-bold border-b border-violet-100">Item</th>
                                <th className="px-1 py-0.5 text-center text-[7px] uppercase text-gray-400 font-bold border-b border-violet-100">Qty{h.quantity > 1 ? ` (×${h.quantity})` : ''}</th>
                                {seesTradeCost && <th className="px-1 py-0.5 text-right text-[7px] uppercase text-gray-400 font-bold border-b border-violet-100">Unit Cost</th>}
                                {seesTradeCost && <th className="px-1 py-0.5 text-right text-[7px] uppercase text-gray-400 font-bold border-b border-violet-100">Line Cost</th>}
                                <th className="px-1 py-0.5 text-center text-[7px] uppercase text-gray-400 font-bold border-b border-violet-100">Optional</th>
                              </tr></thead>
                              <tbody>
                                {bundleLineItems[h.id].map(item => {
                                  const itemCost = item.min_price ?? item.unit_cost ?? null;
                                  const storedQtys = (() => { try { return JSON.parse(h.bundle_items_json || '[]'); } catch { return []; } })();
                                  const storedEntry = storedQtys.find(s => s.product_catalog_id === item.product_catalog_id);
                                  const perBundleQty = storedEntry ? storedEntry.quantity : item.quantity;
                                  const totalQty = perBundleQty * h.quantity;
                                  return (
                                  <tr key={item.id} className={`border-b border-violet-50 ${item.is_optional ? 'opacity-60' : ''}`}>
                                    <td className="px-1 py-0.5">
                                      <span className="font-mono text-navy mr-1">{item.code}</span>
                                      <span>{item.description}</span>
                                    </td>
                                    <td className="px-1 py-0.5 text-center">{totalQty}{(h.quantity > 1 || perBundleQty > 1) && <span className="text-gray-400 ml-0.5">({perBundleQty}×{h.quantity})</span>}</td>
                                    {seesTradeCost && <td className="px-1 py-0.5 text-right">{itemCost != null ? `£${Number(itemCost).toFixed(2)}` : '—'}</td>}
                                    {seesTradeCost && <td className="px-1 py-0.5 text-right font-semibold">{itemCost != null ? `£${(Number(itemCost) * totalQty).toFixed(2)}` : '—'}</td>}
                                    <td className="px-1 py-0.5 text-center">{!!item.is_optional ? <span className="text-[6.5px] px-1 py-0 rounded bg-gray-100 text-gray-500">opt</span> : ''}</td>
                                  </tr>
                                  );
                                })}
                                {seesTradeCost && <tr className="bg-violet-100/50">
                                  <td colSpan={3} className="px-1 py-0.5 text-right text-[7px] font-bold text-violet-700">
                                    {h.quantity > 1 ? `Total cost (${h.quantity} bundles)` : 'Bundle total cost'}
                                  </td>
                                  <td className="px-1 py-0.5 text-right text-[8px] font-extrabold text-violet-800">
                                    £{bundleLineItems[h.id].reduce((s, i) => { const sq = (() => { try { return JSON.parse(h.bundle_items_json || '[]'); } catch { return []; } })().find(e => e.product_catalog_id === i.product_catalog_id); const pq = sq ? sq.quantity : i.quantity; return s + (i.min_price ?? i.unit_cost ?? 0) * pq * h.quantity; }, 0).toFixed(2)}
                                  </td>
                                  <td />
                                </tr>}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div className="text-[8px] text-gray-400 py-1">Loading bundle details…</div>
                        )}
                      </td>
                    </tr>
                  )}
                </>))}
                {hardwareLines.length === 0 && <tr><td colSpan={hwColCount} className="text-center py-6 text-[10px] text-gray-400">No hardware items.</td></tr>}
              </tbody>
              {seesMoney && hardwareLines.length > 0 && (
                <tfoot><tr className="bg-gray-50">
                  <td colSpan={seesTradeCost ? 6 : 4} className="px-2 py-1.5 text-[9px] font-bold text-gray-600 text-right">Hardware Total (Sell)</td>
                  <td className="px-2 py-1.5 text-[10px] font-extrabold text-right text-navy">{formatMoney(hardwareTotal)}</td>
                  <td colSpan="3" />
                </tr></tfoot>
              )}
            </table>
          </div>

          {legacyUnmatchedHardware.length > 0 && (
            <div className="mt-3 bg-amber-50/40 border border-amber-200 rounded overflow-x-auto">
              <div className="px-3 py-2 border-b border-amber-200 flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <div className="text-[9px] font-bold text-amber-800 uppercase">Legacy hardware — unreviewed ({legacyUnmatchedHardware.length})</div>
                  <div className="text-[8px] text-amber-700">
                    AI-extracted from the original SOW document but never matched to a product catalogue item, so it isn't a real priced line yet —
                    shown here for reference only.{seesMoney && ' Included in the Hardware total above.'}
                  </div>
                </div>
                <button onClick={() => navigate('sow-legacy-extraction')}
                  className="px-2 py-1 text-[8px] font-semibold border border-amber-300 text-amber-800 rounded bg-white hover:bg-amber-100 flex-shrink-0">
                  Resolve in Match to Catalogue →
                </button>
              </div>
              <table className="w-full">
                <thead><tr>
                  {['Item','Qty','Unit', ...(seesMoney ? ['Unit Price','Line Total'] : []),'Confidence'].map(h => (
                    <th key={h} className={`px-2 py-1 text-[8px] uppercase text-amber-700 font-bold border-b border-amber-100 ${['Qty','Unit Price','Line Total','Confidence'].includes(h) ? 'text-right' : 'text-left'}`}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {legacyUnmatchedHardware.map(l => (
                    <tr key={l.id} className="border-b border-amber-100 last:border-0">
                      <td className="px-2 py-1.5 text-[9px]" title={l.raw_text || ''}>{l.description}</td>
                      <td className="px-2 py-1.5 text-[9px] text-right">{l.quantity ?? '—'}</td>
                      <td className="px-2 py-1.5 text-[9px] text-right">{l.unit || '—'}</td>
                      {seesMoney && <td className="px-2 py-1.5 text-[9px] text-right">{l.unit_price != null ? formatMoney(l.unit_price) : '—'}</td>}
                      {seesMoney && <td className="px-2 py-1.5 text-[9px] font-semibold text-right">{formatMoney(l.line_total)}</td>}
                      <td className="px-2 py-1.5 text-[8px] text-right capitalize text-amber-700">{l.extraction_confidence || '—'}</td>
                    </tr>
                  ))}
                </tbody>
                {seesMoney && <tfoot><tr className="bg-amber-100/50">
                  <td colSpan="4" className="px-2 py-1.5 text-[9px] font-bold text-amber-800 text-right">Legacy Hardware Total</td>
                  <td className="px-2 py-1.5 text-[10px] font-extrabold text-right text-amber-800">{formatMoney(legacyUnmatchedHardwareTotal)}</td>
                  <td />
                </tr></tfoot>}
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── COSTS — actual purchases booked to this SOW ── */}
      {tab === 'costs' && (() => {
        const PAY_LABEL = { company: 'Company', personal: 'Personal (reimburse)', unpaid: 'Not yet paid' };
        const total = purchases.reduce((s, p) => s + (p.total_incl_vat || 0), 0);
        const rechargeTotal = purchases.filter(p => p.rechargeable).reduce((s, p) => s + (p.total_incl_vat || 0), 0);
        return (
          <div>
            <ProjectCostTable sowId={detailId} refreshKey={milestones.length} onChanged={async () => setMilestones(await api.get('milestones', { sow_id: detailId }).catch(() => milestones))} />
            <div className="text-[9px] font-bold uppercase text-gray-500 mb-1 mt-2">Supplier invoices and ad-hoc purchases</div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[9px] text-gray-500">
                Actual purchases tied to this SOW — captured from forwarded order confirmations (Ad-hoc Purchases) or finance-inbox invoices. For visibility; not included in profitability or the project cost view above.
              </div>
              {purchases.length > 0 && (
                <div className="text-[9px] text-gray-600 whitespace-nowrap">
                  Total <span className="font-bold text-navy">{formatMoney(total, 2)}</span>
                  {rechargeTotal > 0 && <span className="ml-2 text-emerald-700">· {formatMoney(rechargeTotal, 2)} rechargeable</span>}
                </div>
              )}
            </div>
            <div className="bg-white border border-gray-200 rounded overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    {['Supplier', 'Item / note', 'Type', 'Payment', 'Amount', 'Date', 'PO', 'Source', ''].map(h => (
                      <th key={h} className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {purchases.map(p => (
                    <tr key={p.id} className="border-b border-gray-100 hover:bg-gold/5">
                      <td className="px-2 py-1 text-[10px] font-semibold">
                        {p.supplier_name || '—'}
                        {p.rechargeable ? <span className="ml-1 text-[7px] font-bold text-emerald-700 bg-emerald-50 px-1 py-0.5 rounded">RECHARGE</span> : null}
                      </td>
                      <td className="px-2 py-1 text-[9px] text-gray-600 max-w-[220px]">{p.pmo_note || p.notes || '—'}</td>
                      <td className="px-2 py-1 text-[9px]">{p.cost_type || '—'}</td>
                      <td className="px-2 py-1 text-[9px]">{PAY_LABEL[p.payment_method] || '—'}</td>
                      <td className="px-2 py-1 text-[10px] text-right font-bold">{p.total_incl_vat != null ? formatMoney(p.total_incl_vat, 2) : '—'}</td>
                      <td className="px-2 py-1 text-[8px] text-gray-400 whitespace-nowrap">{p.invoice_date ? formatDate(p.invoice_date) : '—'}</td>
                      <td className="px-2 py-1 text-[9px]">
                        {p.po_number
                          ? <span className="text-navy font-semibold cursor-pointer hover:underline" onClick={() => navigate('pos', p.po_id)}>{p.po_number}</span>
                          : <span className="text-gray-300">—</span>}
                      </td>
                      <td className="px-2 py-1 text-[8px] text-gray-400">{p.source === 'adhoc_employee' ? 'Ad-hoc' : 'Invoice'}</td>
                      <td className="px-2 py-1 whitespace-nowrap">
                        {(p.attachments || []).map(a => (
                          <a key={a.id} href={`/api/procurement/attachments/${a.id}/download`} target="_blank" rel="noreferrer"
                            className="text-[8px] text-blue-500 hover:underline mr-1" title={a.filename}>PDF</a>
                        ))}
                      </td>
                    </tr>
                  ))}
                  {purchases.length === 0 && (
                    <tr><td colSpan="9" className="text-center py-6 text-[10px] text-gray-400">No purchases booked to this SOW yet.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* ── RESOURCE AUGMENTATION ── */}
      {tab === 'resource-augmentation' && seesMoney && (() => {
        const daysBilled   = reaugInvoices.reduce((s, i) => s + (i.days_billed   || 0), 0);
        const labourBilled = reaugInvoices.reduce((s, i) => s + (i.labour_amount || 0), 0);
        const tsBilled     = reaugInvoices.reduce((s, i) => s + (i.ts_amount     || 0), 0);
        const daysLogged   = reaugTimesheets.reduce((s, t) => s + (t.days_worked || 0), 0);
        const daysRem      = (sow.reaug_days || 0) - daysBilled;
        const tsRem        = (sow.reaug_ts_budget || 0) - tsBilled;
        const daysPct      = sow.reaug_days ? Math.min((daysBilled / sow.reaug_days) * 100, 100) : 0;
        const overBudget   = daysBilled > (sow.reaug_days || 0);

        const saveReaugInvoice = async (form) => {
          if (editReaugInv === 'new') await api.post(`sows/${detailId}/reaug-invoices`, form);
          else await api.put(`sows/${detailId}/reaug-invoices/${editReaugInv.id}`, form);
          setEditReaugInv(null);
          loadReaug();
        };
        const deleteReaugInvoice = async (id) => {
          if (!confirm('Delete this invoice?')) return;
          await api.delete(`sows/${detailId}/reaug-invoices/${id}`);
          loadReaug();
        };
        const saveReaugTs = async (form) => {
          if (editReaugTs === 'new') await api.post(`sows/${detailId}/reaug-timesheets`, form);
          else await api.put(`sows/${detailId}/reaug-timesheets/${editReaugTs.id}`, form);
          setEditReaugTs(null);
          loadReaug();
        };
        const deleteReaugTs = async (id) => {
          if (!confirm('Delete this timesheet entry?')) return;
          await api.delete(`sows/${detailId}/reaug-timesheets/${id}`);
          loadReaug();
        };

        const INV_PILL = { Submitted: 'bg-blue-100 text-blue-700', Approved: 'bg-amber-100 text-amber-700', Paid: 'bg-emerald-100 text-emerald-700' };
        const TS_PILL  = { Draft: 'bg-gray-100 text-gray-600', Submitted: 'bg-blue-100 text-blue-700', Approved: 'bg-emerald-100 text-emerald-700' };

        return (
          <div>
            {/* Config strip */}
            <div className="grid grid-cols-3 gap-2 mb-3">
              {[
                ['Provisioned Days', sow.reaug_days != null ? `${sow.reaug_days}d` : '—'],
                ['Day Rate',         sow.reaug_day_rate   != null ? formatMoney(sow.reaug_day_rate) : '—'],
                ['T&S Budget',       sow.reaug_ts_budget  != null ? formatMoney(sow.reaug_ts_budget) : '—'],
              ].map(([lbl, val]) => (
                <div key={lbl} className="bg-purple-50 border border-purple-100 rounded p-2">
                  <div className="text-[8px] text-purple-400 uppercase font-bold mb-0.5">{lbl}</div>
                  <div className="text-base font-extrabold text-purple-700">{val}</div>
                </div>
              ))}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-2 mb-2">
              {[
                ['Days Billed',    `${daysBilled.toFixed(1)}d`, overBudget ? 'text-red-600' : 'text-navy'],
                ['Days Remaining', `${daysRem.toFixed(1)}d`,   daysRem < 0 ? 'text-red-600' : daysRem < 10 ? 'text-amber-500' : 'text-emerald-600'],
                ['Days Logged',    `${daysLogged.toFixed(1)}d`,'text-purple-600'],
                ['T&S Remaining',  formatMoney(tsRem),         tsRem < 0 ? 'text-red-600' : 'text-navy'],
              ].map(([lbl, val, cls]) => (
                <div key={lbl} className="bg-white border border-gray-200 rounded p-2">
                  <div className="text-[8px] text-gray-400 uppercase font-bold mb-0.5">{lbl}</div>
                  <div className={`text-sm font-extrabold ${cls}`}>{val}</div>
                </div>
              ))}
            </div>
            {/* Progress bar */}
            {sow.reaug_days > 0 && (
              <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-2">
                <div className={`h-full rounded-full transition-all ${overBudget ? 'bg-red-500' : 'bg-gold'}`} style={{ width: `${daysPct}%` }} />
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 mb-3">
              {[
                ['Labour Billed', formatMoney(labourBilled)],
                ['T&S Billed',    formatMoney(tsBilled)],
              ].map(([lbl, val]) => (
                <div key={lbl} className="bg-white border border-gray-200 rounded p-2">
                  <div className="text-[8px] text-gray-400 uppercase font-bold mb-0.5">{lbl}</div>
                  <div className="text-sm font-extrabold text-navy">{val}</div>
                </div>
              ))}
            </div>

            {/* Sub-tabs */}
            <div className="flex border-b border-gray-200 mb-3">
              {[['invoices', `Invoices (${reaugInvoices.length})`], ['timesheets', `Timesheets (${reaugTimesheets.length})`], ['resources', `External Resources (${reaugResources.length})`]].map(([k, lbl]) => (
                <button key={k} onClick={() => setReaugSubTab(k)}
                  className={`px-3 py-1.5 text-[9px] font-medium border-b-2 -mb-px mr-1 ${reaugSubTab === k ? 'border-gold text-gold font-semibold' : 'border-transparent text-gray-400 hover:text-gray-600'}`}>
                  {lbl}
                </button>
              ))}
            </div>

            {reaugLoading && <div className="text-center py-8 text-[9px] text-gray-400">Loading…</div>}

            {/* Invoices */}
            {!reaugLoading && reaugSubTab === 'invoices' && (
              <div className="bg-white border border-gray-200 rounded">
                <div className="px-3 py-2 border-b border-gray-100">
                  <span className="text-[8px] text-gray-400">{reaugInvoices.length} invoice{reaugInvoices.length !== 1 ? 's' : ''}</span>
                </div>
                {reaugInvoices.length === 0 && <div className="text-center py-8 text-[9px] text-gray-300">No invoices yet</div>}
                {reaugInvoices.map(inv => (
                  <div key={inv.id} className="px-3 py-2.5 border-b border-gray-50 hover:bg-gray-50/50 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-[9px] font-bold text-navy">{inv.invoice_number}</span>
                        <span className={`text-[7px] font-bold px-1.5 py-0.5 rounded ${INV_PILL[inv.status] || 'bg-gray-100 text-gray-500'}`}>{inv.status}</span>
                        {inv.submitted_by_name && <span className="text-[7px] text-gray-400">{inv.submitted_by_name}</span>}
                      </div>
                      <div className="flex items-center gap-4 text-[8px] text-gray-500">
                        <span>{formatDate(inv.invoice_date)}</span>
                        <span className="font-semibold text-navy">{inv.days_billed != null ? `${inv.days_billed}d` : '—'}</span>
                        <span>Labour: {formatMoney(inv.labour_amount)}</span>
                        {inv.ts_amount > 0 && <span>T&S: {formatMoney(inv.ts_amount)}</span>}
                      </div>
                    </div>
                    {canEdit && (
                    <div className="flex gap-1">
                      <button onClick={() => setEditReaugInv(inv)} className="text-[8px] text-gray-400 hover:text-navy px-1.5 py-0.5 border border-transparent hover:border-gray-200 rounded">Edit</button>
                      <button onClick={() => deleteReaugInvoice(inv.id)} className="text-[8px] text-gray-400 hover:text-red-500 px-1.5 py-0.5 border border-transparent hover:border-red-200 rounded">✕</button>
                    </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Timesheets */}
            {!reaugLoading && reaugSubTab === 'timesheets' && (
              <div className="bg-white border border-gray-200 rounded">
                <div className="px-3 py-2 flex items-center justify-between border-b border-gray-100">
                  <span className="text-[8px] text-gray-400">{reaugTimesheets.length} entr{reaugTimesheets.length !== 1 ? 'ies' : 'y'}</span>
                  {canEdit && <button onClick={() => setEditReaugTs('new')} className="px-2 py-1 text-[8px] font-bold bg-navy text-white rounded hover:bg-navy/80">+ Add Entry</button>}
                </div>
                {reaugTimesheets.length === 0 && <div className="text-center py-8 text-[9px] text-gray-300">No timesheet entries yet</div>}
                {reaugTimesheets.map(ts => (
                  <div key={ts.id} className="px-3 py-2.5 border-b border-gray-50 hover:bg-gray-50/50 flex items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-[9px] font-semibold text-navy">w/c {formatDate(ts.week_commencing)}</span>
                        <span className={`text-[7px] font-bold px-1.5 py-0.5 rounded ${TS_PILL[ts.status] || 'bg-gray-100 text-gray-500'}`}>{ts.status}</span>
                        {ts.person_name && <span className="text-[7px] text-gray-400">{ts.person_name}</span>}
                      </div>
                      <div className="flex items-center gap-4 text-[8px] text-gray-500">
                        <span className="font-semibold text-navy">{ts.days_worked != null ? `${ts.days_worked}d` : '—'}</span>
                        {ts.invoice_id && (
                          <span className="text-[7px] bg-purple-50 text-purple-600 px-1.5 py-0.5 rounded">
                            Linked: {reaugInvoices.find(i => i.id === ts.invoice_id)?.invoice_number || 'Invoice'}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex gap-1">
                      {canEdit && (<>
                      <button onClick={() => setEditReaugTs(ts)} className="text-[8px] text-gray-400 hover:text-navy px-1.5 py-0.5 border border-transparent hover:border-gray-200 rounded">Edit</button>
                      <button onClick={() => deleteReaugTs(ts.id)} className="text-[8px] text-gray-400 hover:text-red-500 px-1.5 py-0.5 border border-transparent hover:border-red-200 rounded">✕</button>
                      </>)}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* External Resources */}
            {!reaugLoading && reaugSubTab === 'resources' && (
              <div>
                {reaugResources.length === 0 ? (
                  <div className="text-center py-6 text-[9px] text-gray-400">No external resources linked to this SOW. Add a contract for this SOW from the contractor's record on People → Contractor Dashboard.</div>
                ) : (
                  <div className="space-y-2">
                    {reaugResources.map(r => (
                      <button
                        key={r.id}
                        onClick={() => navigate('contractor-dashboard', r.id)}
                        className="w-full text-left bg-white border border-gray-200 rounded p-3 flex items-center justify-between hover:border-gold/50 hover:bg-gold/5 transition-colors group"
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-[10px] font-bold text-navy group-hover:text-gold transition-colors">{r.first_name} {r.last_name}</span>
                            {r.company_name && <span className="text-[8px] text-gray-500">{r.company_name}</span>}
                            {!r.is_active && <span className="text-[7px] bg-red-100 text-red-600 px-1 py-0.5 rounded font-semibold">Inactive</span>}
                          </div>
                          {r.email && <div className="text-[8px] text-gray-400">{r.email}</div>}
                          {r.role_description && <div className="text-[8px] text-gold mt-0.5">{r.role_description}</div>}
                        </div>
                        <span className="text-[8px] text-gray-300 group-hover:text-gold transition-colors">View →</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Invoice modal */}
            {editReaugInv && (
              <ReaugInvoiceModal
                sow={sow}
                invoice={editReaugInv === 'new' ? null : editReaugInv}
                onClose={() => setEditReaugInv(null)}
                onSave={saveReaugInvoice}
              />
            )}
            {/* Timesheet modal */}
            {editReaugTs && (
              <ReaugTimesheetModal
                timesheet={editReaugTs === 'new' ? null : editReaugTs}
                people={people}
                invoices={reaugInvoices}
                onClose={() => setEditReaugTs(null)}
                onSave={saveReaugTs}
              />
            )}
          </div>
        );
      })()}

      {/* ── CHECKLIST ── */}
      {tab === 'tasks' && <RepeatableTasksTab tasks={repeatableTasks} magnitude={sow?.magnitude} onUpdate={updateRepeatableTask} readOnly={!canEdit} />}

      {/* ── CCN (Contract Change Notes) ── */}
      {tab === 'ccn' && (
        <CcnTab
          sowId={detailId}
          sow={sow}
          milestones={milestones}
          ccns={ccns || []}
          loading={ccns === null}
          currentUser={currentUser}
          onReload={() => { loadCcns(); loadData(); }}
          showAdd={showAddCcn}
          setShowAdd={setShowAddCcn}
          editCcn={editCcn}
          setEditCcn={setEditCcn}
        />
      )}

      {/* ── PLAN / GANTT ── */}
      {tab === 'plan' && <ProjectPlanTab sowId={detailId} people={people} canEdit={canEdit} />}

      {tab === 'resourcing' && (
        <SowResourcingTab sowId={detailId} canEdit={canEdit}
          onLinesChanged={async () => setSow(await api.get(`sows/${detailId}`))} />
      )}

      {/* ── RISKS ── */}
      {tab === 'risks' && <SowRisksTab sowId={detailId} sow={sow} people={people} />}

      {tab === 'surveys' && <SurveysPanel sowId={detailId} />}

      {/* ── HISTORY: who was on the job and when ── */}
      {tab === 'history' && <SowHistoryTab sowId={detailId} />}

      {/* ── COMMENTARY / HISTORY ── */}
      {tab === 'commentary' && <CommentaryTab sowId={detailId} sow={sow} commentary={commentary} people={people} milestones={milestones} currentUser={currentUser} initialActionId={targetActionId} onRefresh={async () => { const c = await api.get('commentary', { entity_type: 'sow', entity_id: detailId }); setCommentary(c); }} />}

      {/* ── CHILDREN ── */}
      {tab === 'children' && (() => {
        const children = sow.children || [];
        const totalValue     = children.reduce((s, c) => s + (c.total_excl_vat  || 0), 0);
        const totalMilestone = children.reduce((s, c) => s + (c.milestones_sum  || 0), 0);
        const totalInvoiced  = children.reduce((s, c) => s + (c.invoiced_sum    || 0), 0);
        return (
          <div className="mt-3">
            {/* Aggregate summary */}
            {seesMoney && <div className="grid grid-cols-3 gap-3 mb-4">
              {[
                { label: 'Combined Value',    val: totalValue },
                { label: 'Milestones Total',  val: totalMilestone },
                { label: 'Invoiced Total',    val: totalInvoiced },
              ].map(({ label, val }) => (
                <div key={label} className="bg-gray-50 border border-gray-100 rounded p-3">
                  <div className="text-[8px] font-bold text-gray-400 uppercase mb-1">{label}</div>
                  <div className="text-[13px] font-bold text-navy">{formatMoney(val)}</div>
                </div>
              ))}
            </div>}
            {/* Children table */}
            <div className="border border-gray-100 rounded overflow-hidden">
              <table className="w-full text-[9px]">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="px-3 py-2 text-left font-bold text-gray-500 uppercase">Reference</th>
                    <th className="px-3 py-2 text-left font-bold text-gray-500 uppercase">Title</th>
                    <th className="px-3 py-2 text-left font-bold text-gray-500 uppercase">Status</th>
                    <th className="px-3 py-2 text-left font-bold text-gray-500 uppercase">Magnitude</th>
                    {seesMoney && <>
                    <th className="px-3 py-2 text-right font-bold text-gray-500 uppercase">Value</th>
                    <th className="px-3 py-2 text-right font-bold text-gray-500 uppercase">Milestones</th>
                    <th className="px-3 py-2 text-right font-bold text-gray-500 uppercase">Invoiced</th>
                    </>}
                    <th className="px-3 py-2 text-left font-bold text-gray-500 uppercase">Forecast End</th>
                    <th className="px-3 py-2 text-left font-bold text-gray-500 uppercase">Owner</th>
                  </tr>
                </thead>
                <tbody>
                  {children.map((c, i) => (
                    <tr key={c.id} className={`border-b border-gray-50 hover:bg-blue-50/40 cursor-pointer ${i % 2 === 0 ? '' : 'bg-gray-50/50'}`}
                        onClick={() => navigate('sow-detail', c.id)}>
                      <td className="px-3 py-2 font-mono font-bold text-navy whitespace-nowrap">{c.reference}</td>
                      <td className="px-3 py-2 text-gray-700 max-w-[200px] truncate" title={c.detail}>{c.detail || '—'}</td>
                      <td className="px-3 py-2"><Tag status={c.status} /></td>
                      <td className="px-3 py-2 text-gray-500">{c.magnitude || '—'}</td>
                      {seesMoney && <>
                      <td className="px-3 py-2 text-right font-semibold">{formatMoney(c.total_excl_vat || 0)}</td>
                      <td className="px-3 py-2 text-right text-gray-600">{formatMoney(c.milestones_sum || 0)}</td>
                      <td className="px-3 py-2 text-right text-gray-600">{formatMoney(c.invoiced_sum || 0)}</td>
                      </>}
                      <td className="px-3 py-2 text-gray-500">{c.forecast_completion ? formatDate(c.forecast_completion) : '—'}</td>
                      <td className="px-3 py-2 text-gray-500">{c.owner_name || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {/* ── DOCUMENTS ── */}
      {tab === 'files' && (
        <SharePointFiles kind="sow" id={detailId}
          emptyHint="No SharePoint folder is linked to this SOW. New SOWs get one when they are created; older SOWs are linked to their folder in the Opportunities library automatically where its name starts with the SOW reference." />
      )}
      {tab === 'documents' && <SharePointSyncPanel sowId={detailId} canEdit={canEdit} />}
      {tab === 'documents' && (() => {
        const sorDocs = documents.filter(d => d.document_type === 'sor').sort((a, b) => a.created_at?.localeCompare(b.created_at));
        const sowDocs = documents.filter(d => d.document_type === 'sow').sort((a, b) => a.created_at?.localeCompare(b.created_at));
        // Redacted PDFs are generated FROM a specific SOW document (parent_document_id)
        // — nested directly under that parent below rather than dumped in with the
        // generic "Other Documents", so it's obvious at a glance which SOW version has
        // both an unredacted and redacted copy. Any redacted doc whose parent has since
        // been deleted falls back into Other Documents so it's never silently hidden.
        const redactedDocs = documents.filter(d => d.document_type === 'redacted');
        const sowDocIds = new Set(sowDocs.map(d => d.id));
        const orphanRedactedDocs = redactedDocs.filter(d => !sowDocIds.has(d.parent_document_id));
        const otherDocs = documents.filter(d => d.document_type !== 'sor' && d.document_type !== 'sow' && d.document_type !== 'redacted').concat(orphanRedactedDocs);
        const hasSor = sorDocs.some(d => d.url && d.url !== 'REQUIRED — please upload');

        const toggleSelectDoc = (id) => {
          setSelectedDocIds(prev => {
            if (prev.includes(id)) return prev.filter(x => x !== id);
            if (prev.length >= 2) return [prev[1], id];
            return [...prev, id];
          });
        };

        const DocCard = ({ doc, siblings = [] }) => {
          const isSelected = selectedDocIds.includes(doc.id);
          const isRedacted = doc.title?.includes('REDACTED') || doc.version?.includes('redacted');
          const isFilePath = doc.url && /^([a-zA-Z]:\\|\\\\)/.test(doc.url);
          // ── WHAT ACTUALLY MAKES TWO DOCUMENTS A DUPLICATE ──
          //
          // This flagged "same notes as another document in this column", which was a poor
          // proxy and produced a badge on every single redacted PDF: the redaction generator
          // writes the SAME fixed note on every one of them ("Redacted PDF — monetary values
          // blanked"), so a redaction of v2 and a redaction of v4 matched each other by
          // construction. Every redaction on every SOW was tagged as a possible duplicate of
          // its siblings, and none of them were duplicates of anything.
          //
          // A machine-written description is not evidence. What IS evidence, and is already
          // stored on the row:
          //
          //   • the same content hash — the same bytes, which is the definitive answer
          //   • the same byte size AND the same name, for rows uploaded before hashing
          //   • the same version label in the same column — two v3s is a mistake either way
          //
          // And a derived document is never compared: a redaction has a parent, and two
          // redactions of two different parents are two different things whatever they look
          // like.
          const normTitle = (t) => String(t || '').toLowerCase().replace(/\.(pdf|docx?)$/, '').trim();
          const sameDocument = (a, b) => {
            if (a.content_sha256 && b.content_sha256) return a.content_sha256 === b.content_sha256;
            if (a.file_size > 0 && a.file_size === b.file_size && normTitle(a.title) === normTitle(b.title)) return true;
            return !!(a.version && a.version === b.version);
          };
          const isLikelyDupe = !doc.parent_document_id
            && siblings.some(s => s.id !== doc.id && !s.parent_document_id && sameDocument(doc, s));
          return (
            <div className={`border rounded-lg p-2 transition-all ${isSelected ? 'border-purple-400 bg-purple-50/60 shadow-sm' : isLikelyDupe ? 'border-amber-300 bg-amber-50/50' : 'border-gray-200 bg-white hover:border-gray-300'}`}>
              <div className="flex items-start gap-1.5">
                <input type="checkbox" className="mt-0.5 accent-purple-600 flex-shrink-0"
                  checked={isSelected} onChange={() => toggleSelectDoc(doc.id)} title="Select to compare" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {doc.version
                      ? <span className="text-[8px] font-bold px-1.5 py-0.5 rounded bg-navy text-white">v{doc.version}</span>
                      : <span className="text-[7px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-400">no version</span>}
                    {isRedacted && <span className="text-[7px] font-bold px-1 py-0.5 rounded bg-amber-100 text-amber-700">REDACTED</span>}
                    {isLikelyDupe && <span className="text-[7px] font-bold px-1 py-0.5 rounded bg-amber-200 text-amber-800">POSSIBLE DUPLICATE</span>}
                  </div>
                  <div className="text-[9px] font-semibold text-gray-800 mt-0.5 leading-snug break-words">{doc.title}</div>
                  {doc.notes && <div className="text-[8px] text-gray-500 mt-0.5 leading-snug break-words">{doc.notes}</div>}
                  <div className="flex items-center gap-1.5 mt-1 text-[7px] text-gray-400">
                    <span>{doc.created_at ? formatDate(doc.created_at) : 'date unknown'}</span>
                    {doc.created_by_name && <><span>·</span><span>{doc.created_by_name}</span></>}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1.5 mt-1.5 pt-1.5 border-t border-gray-100">
                {isFilePath ? (
                  <button onClick={() => navigator.clipboard.writeText(doc.url)}
                    className="text-[8px] px-1.5 py-0.5 bg-gray-100 hover:bg-gray-200 rounded text-gray-600">Copy path</button>
                ) : (
                  <DocumentLink kind="sow" id={doc.id} url={doc.url} title={doc.title}
                    className="text-[8px] px-1.5 py-0.5 bg-blue-50 hover:bg-blue-100 rounded text-blue-700 font-medium">View</DocumentLink>
                )}
                {canEdit && doc.document_type === 'sow' && !isRedacted && (
                  <button onClick={() => setShowAiRedact(doc)}
                    className="text-[8px] px-1.5 py-0.5 bg-purple-50 hover:bg-purple-100 rounded text-purple-700 font-semibold">✦ Redact</button>
                )}
                {canEdit && (
                <button onClick={() => deleteDocument(doc.id)}
                  className="ml-auto text-red-300 hover:text-red-500 text-[10px] leading-none font-bold">×</button>
                )}
              </div>
            </div>
          );
        };

        return (
          <div className="space-y-3">
            {/* Toolbar */}
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-[8px] text-gray-400 flex-1">
                {!canEdit && 'Adding, removing, comparing and generating documents needs the "Create and edit SOWs" grant.'}
                {canEdit && selectedDocIds.length === 0 && 'Select up to 2 documents to compare with AI'}
                {selectedDocIds.length === 1 && 'Select one more document to compare'}
                {selectedDocIds.length === 2 && '2 documents selected — ready to compare'}
              </span>
              {canEdit && selectedDocIds.length === 2 && (
                <button onClick={() => setShowAiDiff(true)}
                  className="px-2 py-1 text-[9px] font-semibold bg-purple-600 text-white rounded hover:bg-purple-700 flex items-center gap-1">
                  ✦ Compare with AI
                </button>
              )}
              {canEdit && hasSor && (
                <button onClick={() => setShowGenerateSOW(true)}
                  className="px-2 py-1 text-[9px] font-semibold bg-indigo-600 text-white rounded hover:bg-indigo-700 flex items-center gap-1">
                  ✦ Generate SOW with AI
                </button>
              )}
              {canEdit && sow.parent_sow?.is_framework && (
                <button onClick={generateMiniSow} disabled={generatingMiniSow}
                  title="Branded PDF summary of this call-off's line items — code, description, price, and when that price was last revised"
                  className="px-2 py-1 text-[9px] font-semibold bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-40 flex items-center gap-1">
                  {generatingMiniSow ? 'Generating…' : '📄 Generate Mini SOW PDF'}
                </button>
              )}
              {canEdit && (
              <button onClick={() => setShowAddDocument(true)}
                className="px-2 py-1 text-[9px] font-semibold bg-navy text-white rounded hover:bg-navy/80">+ Add Document</button>
              )}
            </div>

            {!hasSor && (
              <div className="p-2 bg-amber-50 border border-amber-200 rounded flex items-center gap-2 text-[8px] text-amber-800">
                <span>⚠</span>
                <span><strong>SOR required:</strong> Upload a Statement of Requirements (type = "sor") to enable AI SOW generation.</span>
              </div>
            )}

            {documents.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded p-8 text-center text-[10px] text-gray-400">
                No documents linked yet. Add SOW, SOR and supporting documents above.
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                {/* SOR column */}
                <div>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className="text-[7px] font-bold uppercase tracking-wide text-gray-400">Statement of Requirements</span>
                    <span className="text-[7px] text-gray-300">({sorDocs.length} version{sorDocs.length !== 1 ? 's' : ''})</span>
                  </div>
                  {sorDocs.length === 0 ? (
                    <div className="border border-dashed border-gray-200 rounded-lg p-4 text-center text-[8px] text-gray-300">No SOR uploaded</div>
                  ) : (
                    <div className="space-y-1.5">{sorDocs.map(d => <DocCard key={d.id} doc={d} siblings={sorDocs} />)}</div>
                  )}
                </div>

                {/* SOW column */}
                <div>
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className="text-[7px] font-bold uppercase tracking-wide text-gray-400">Statement of Work</span>
                    <span className="text-[7px] text-gray-300">({sowDocs.length} version{sowDocs.length !== 1 ? 's' : ''})</span>
                  </div>
                  {sowDocs.length === 0 ? (
                    <div className="border border-dashed border-gray-200 rounded-lg p-4 text-center text-[8px] text-gray-300">No SOW versions yet</div>
                  ) : (
                    <div className="space-y-1.5">
                      {sowDocs.map(d => (
                        <div key={d.id}>
                          <DocCard doc={d} siblings={sowDocs} />
                          {redactedDocs.filter(r => r.parent_document_id === d.id).map(r => (
                            <div key={r.id} className="ml-4 mt-1 pl-2 border-l-2 border-purple-200">
                              <DocCard doc={r} siblings={redactedDocs} />
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Other documents */}
            {otherDocs.length > 0 && (
              <div>
                <div className="text-[7px] font-bold uppercase tracking-wide text-gray-400 mb-1.5">Other Documents</div>
                <div className="bg-white border border-gray-200 rounded overflow-x-auto">
                  <table className="w-full">
                    <thead><tr>{['Type','Title','Notes','Version','Date','Link','Added By',''].map(h => (
                      <th key={h} className="px-2 py-1 text-left text-[7px] uppercase text-gray-400 font-bold border-b bg-gray-50">{h}</th>
                    ))}</tr></thead>
                    <tbody>
                      {otherDocs.map(d => (
                        <tr key={d.id} className="hover:bg-gold/5 border-b border-gray-100">
                          <td className="px-2 py-1"><span className="text-[8px] font-bold px-1 py-0.5 rounded bg-navy/10 text-navy">{docTypeMap[d.document_type] || d.document_type}</span></td>
                          <td className="px-2 py-1 text-[9px] font-semibold">{d.title}</td>
                          <td className="px-2 py-1 text-[8px] text-gray-500 max-w-[220px] truncate" title={d.notes || ''}>{d.notes || '—'}</td>
                          <td className="px-2 py-1 text-[8px] text-gray-500">{d.version || '—'}</td>
                          <td className="px-2 py-1 text-[8px] text-gray-400 whitespace-nowrap">{d.created_at ? formatDate(d.created_at) : '—'}</td>
                          <td className="px-2 py-1">
                            {/^([a-zA-Z]:\\|\\\\)/.test(d.url || '') ? (
                              <button onClick={() => navigator.clipboard.writeText(d.url)} className="text-[8px] px-1.5 py-0.5 bg-gray-100 rounded text-gray-600">Copy</button>
                            ) : (
                              <DocumentLink kind="sow" id={d.id} url={d.url} title={d.title} className="text-[9px] text-blue-600 hover:underline">View</DocumentLink>
                            )}
                          </td>
                          <td className="px-2 py-1 text-[8px] text-gray-400">{d.created_by_name || '—'}</td>
                          <td className="px-2 py-1 text-center">
                            {canEdit && <button onClick={() => deleteDocument(d.id)} className="text-red-400 hover:text-red-600 font-bold">×</button>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* ── MODALS ── */}
      {showLegacyHistory && <LegacyHistoryModal rows={legacyHistory} reference={sow.reference} onClose={() => setShowLegacyHistory(false)} />}
      {showAddMilestone && <AddMilestoneModal sowRef={sow.reference} sow={sow} people={people} nextCode={`AVM-${String(milestones.length + 1).padStart(2, '0')}`} onClose={() => setShowAddMilestone(false)} onSave={addMilestone} />}
      {editMilestone    && <EditMilestoneModal milestone={editMilestone} sow={sow} people={people} onClose={() => setEditMilestone(null)} onSave={saveMilestone} />}
      {showAddHardware     && <AddHardwareModal onClose={() => setShowAddHardware(false)} onSave={addHardwareLine} />}
      {showAddPassThrough  && <AddPOPassThroughModal onClose={() => setShowAddPassThrough(false)} onSave={addHardwareLine} />}
      {showCreatePO && (
        <CreatePOFromHardwareModal
          sow={sow}
          lines={hardwareLines.filter(h => selectedHwLines.has(h.id))}
          onClose={() => setShowCreatePO(false)}
          onCreated={onPOCreated}
        />
      )}
      {poQuickView && (
        <POQuickViewModal
          poId={poQuickView}
          onClose={() => setPoQuickView(null)}
          onNavigate={(id) => navigate('pos', id)}
        />
      )}
      {showApplyTemplate && (
        <ApplyTemplateModal
          sow={sow}
          onClose={() => setShowApplyTemplate(false)}
          onApplied={async () => { setShowApplyTemplate(false); setSow(await api.get(`sows/${detailId}`)); }}
        />
      )}
      {showGenerateSOW && (
        <GenerateSOWModal
          sow={sow}
          documents={documents}
          onClose={() => setShowGenerateSOW(false)}
          onGenerated={async () => { setShowGenerateSOW(false); loadDocuments(); }}
          sowId={detailId}
        />
      )}
      {statusOverride   && (
        <HardwareStatusOverrideModal
          line={statusOverride}
          onChange={(field, val) => setStatusOverride(p => ({ ...p, [field]: val }))}
          onConfirm={() => updateHardwareStatus(statusOverride.lineId, statusOverride.newStatus, statusOverride.reason)}
          onClose={() => setStatusOverride(null)}
        />
      )}
      {hwItemsLine      && <HardwareItemsModal line={hwItemsLine} onClose={() => setHwItemsLine(null)} />}
      {renewalsFor && (
        <ServiceRenewalsModal
          line={renewalsFor}
          sowId={detailId}
          onClose={async () => { setRenewalsFor(null); setSow(await api.get(`sows/${detailId}`)); }}
        />
      )}
      {showAddResource  && <AddResourceModal existingSections={allSectionNames} defaultSection={addResourceSection} onClose={() => { setShowAddResource(false); setAddResourceSection(''); }} onSave={addResourceLine} />}
      {editResourceLine && <EditResourceModal line={editResourceLine} existingSections={allSectionNames} onClose={() => setEditResourceLine(null)} onSave={async (data) => { await updateResourceLine(editResourceLine.id, data); setEditResourceLine(null); }} />}
      {showAddDocument  && <AddDocumentModal onClose={() => setShowAddDocument(false)} onSave={addDocument} />}
      {sowDocPrompt && (
        <Modal
          title={sowDocPrompt.reissue ? 'Re-issue this SOW?' : 'Update SOW to match this document?'}
          onClose={() => setSowDocPrompt(null)}
          footer={sowDocPrompt.reissue ? (
            <>
              <button onClick={() => setSowDocPrompt(null)}
                className="px-3 py-1 text-[10px] font-semibold border border-gray-200 rounded bg-white">
                Just keep the document
              </button>
              <button
                onClick={() => {
                  setReissuePreset({ document_id: sowDocPrompt.document_id, version: sowDocPrompt.parsed_version });
                  setSowDocPrompt(null);
                  setShowIssueModal(true);
                }}
                className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded">
                Re-issue{sowDocPrompt.parsed_version ? ` as v${sowDocPrompt.parsed_version}` : ''} →
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setSowDocPrompt(null)} disabled={sowDocSyncing}
                className="px-3 py-1 text-[10px] font-semibold border border-gray-200 rounded bg-white">
                Skip
              </button>
              <button onClick={applySowDocSync} disabled={sowDocSyncing}
                className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-60">
                {sowDocSyncing ? 'Updating…' : 'Update SOW'}
              </button>
            </>
          )}>
          {sowDocPrompt.reissue ? (
            <>
              <p className="text-[11px] text-gray-700 leading-relaxed">
                You uploaded a Statement of Work{sowDocPrompt.version ? <> (version <strong>{sowDocPrompt.version}</strong>)</> : ''} onto a
                SOW that has already been issued. Sending a new version to a client is a <strong>re-issue</strong>, not a
                change of paperwork, so it goes through the issue workflow — which sets the version, the issued date and
                the <strong>expiry date</strong> together, and lets you correct the milestone dates while you are there.
              </p>
              {/* The trap this replaces, named. Patching the version and the issued date on
                  their own left the expiry worked out from the previous issuance, so the
                  nightly validity check expired the SOW the next morning. */}
              <div className="mt-2 px-2.5 py-2 rounded border border-amber-300 bg-amber-50 text-[10px] text-amber-900">
                Updating just the version and the issued date is what expired this kind of SOW overnight: the expiry
                date stayed as it was from the previous issue.
                {sow.expiry_date && <> This one currently expires <strong>{formatDate(sow.expiry_date)}</strong>.</>}
              </div>
              <div className="text-[9px] text-gray-500 mt-2">
                Current: v{sow.issued_version ?? 0}
                {(sow.issued_date || sow.sow_date) ? ` · issued ${formatDate(sow.issued_date || sow.sow_date)}` : ' · not yet issued'}
                {sow.status ? ` · ${sow.status}` : ''}
              </div>
            </>
          ) : (
            <>
              <p className="text-[11px] text-gray-700 leading-relaxed">
                You uploaded a Statement of Work document{sowDocPrompt.version ? <> (version <strong>{sowDocPrompt.version}</strong>)</> : ''}.
                Update the SOW record so its <strong>issued version</strong> and <strong>issued date</strong> match? You can adjust the values below.
                This SOW has not been issued yet, so nothing here starts its validity clock — issuing it is still a separate step.
              </p>
              <div className="grid grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Issued Version</label>
                  <input type="number" min="0" className="form-input w-full"
                    value={sowDocPrompt.issued_version}
                    onChange={e => setSowDocPrompt(p => ({ ...p, issued_version: e.target.value }))} />
                </div>
                <div>
                  <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Issued Date</label>
                  <input type="date" className="form-input w-full"
                    value={sowDocPrompt.issued_date}
                    onChange={e => setSowDocPrompt(p => ({ ...p, issued_date: e.target.value }))} />
                </div>
              </div>
              <div className="text-[8px] text-gray-400 mt-2">Current: v{sow.issued_version ?? 0}{(sow.issued_date || sow.sow_date) ? ` · issued ${formatDate(sow.issued_date || sow.sow_date)}` : ' · not yet issued'}</div>
            </>
          )}
        </Modal>
      )}
      {showAiDiff && selectedDocIds.length === 2 && (
        <AiDocDiffModal
          doc1={documents.find(d => d.id === selectedDocIds[0])}
          doc2={documents.find(d => d.id === selectedDocIds[1])}
          onClose={() => setShowAiDiff(false)}
        />
      )}
      {showAiRedact && (
        <AiRedactModal
          doc={showAiRedact}
          onClose={() => setShowAiRedact(null)}
          onDone={() => { setShowAiRedact(null); loadDocuments(); }}
        />
      )}
      {showEditSOW      && <EditSOWModal sow={sow} people={people} milestoneSumActive={milestoneSum} onClose={() => setShowEditSOW(false)} onSave={saveSOW} />}
      {pmCascade && (
        <Modal title="Update milestone Project Managers?" onClose={() => setPmCascade(null)} footer={
          <>
            <button onClick={() => setPmCascade(null)} disabled={pmCascadeSaving}
              className="px-3 py-1 text-[10px] font-semibold border border-gray-200 rounded bg-white">
              No, just the SOW
            </button>
            <button onClick={applyPmCascade} disabled={pmCascadeSaving}
              className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-60">
              {pmCascadeSaving ? 'Reassigning…' : `Yes, reassign ${pmCascade.eligible.length}`}
            </button>
          </>
        }>
          <p className="text-[11px] text-gray-700 leading-relaxed">
            You changed the Project Manager to <strong>{pmCascade.newPmName}</strong>.
          </p>
          <p className="text-[11px] text-gray-700 leading-relaxed mt-2">
            Do you also want to set <strong>{pmCascade.newPmName}</strong> as the PM on the{' '}
            <strong>{pmCascade.eligible.length}</strong> open milestone{pmCascade.eligible.length !== 1 ? 's' : ''} on this SOW?
            Milestones that are Paid, Invoiced or Completed are left unchanged.
          </p>
          <div className="mt-3 max-h-40 overflow-y-auto border border-gray-100 rounded">
            {pmCascade.eligible.map(m => (
              <div key={m.id} className="flex items-center justify-between gap-2 px-2.5 py-1.5 border-b border-gray-50 last:border-b-0">
                <span className="text-[9px] font-semibold text-navy truncate">
                  {m.milestone_code}{m.description ? ` — ${m.description}` : ''}
                </span>
                <span className="text-[8px] font-semibold text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded flex-shrink-0">{m.status}</span>
              </div>
            ))}
          </div>
        </Modal>
      )}
      {showPOReceived   && <POReceivedModal sow={sow} milestones={milestones.filter(m => !['Cancelled','Split','Credit Noted'].includes(m.status))} onClose={() => setShowPOReceived(false)} onSave={activateSOW} />}
      {showIssueModal   && <IssueWorkflowModal sow={sow} detailId={detailId} milestoneSumActive={milestoneSum}
        milestones={milestones} preset={reissuePreset}
        onClose={() => { setShowIssueModal(false); setReissuePreset(null); }}
        onIssued={async () => {
          setShowIssueModal(false); setReissuePreset(null);
          setSow(await api.get(`sows/${detailId}`));
          // The milestone dates may have moved with it, so the list behind the modal is
          // reloaded too rather than left showing what it showed before.
          setMilestones(await api.get('milestones', { sow_id: detailId }).catch(() => milestones));
        }} />}

      {/* New Query Modal */}
    </div>
  );
}

// ── COMMENTARY / ACTIVITY HISTORY TAB ────────────────────────────────────────
// Shows user comments AND a chronological audit trail of all changes (item 15)
function CommentaryTab({ sowId, sow, commentary, people = [], milestones = [], currentUser, initialActionId, onRefresh }) {
  const [activity, setActivity] = useState(null);
  const [viewMode, setViewMode] = useState('feed');

  useEffect(() => {
    api.get(`sows/${sowId}/activity`)
      .then(rows => setActivity(rows || []))
      .catch(() => setActivity([]));
  }, [sowId, commentary]);

  const FIELD_LABELS = {
    status: 'Status', type: 'Type', commercial_type: 'Commercial Type',
    client_id: 'Client', owner_id: 'Project Manager', client_contact_id: 'Commercial Contact', non_commercial_contact_id: 'Non-commercial Contact',
    delivery_owner_id: 'Delivery Owner', project_manager_id: 'Project Manager', assigned_to_id: 'Assigned To',
    contact: 'Contact', detail: 'Detail', payment_terms: 'Payment Terms',
    forecast_completion: 'Expected Completion', po_number: 'PO Number',
    po_received_date: 'PO Received', issued_date: 'Issued Date',
    received_date: 'Date Received', submission_deadline: 'Submission Deadline', sourcing_event_id: 'Sourcing Event ID',
    magnitude: 'Magnitude', client_priority: 'Client Priority', total_excl_vat: 'Total (excl VAT)', sow_date: 'SOW Date',
    background: 'Background', scope: 'Scope', out_of_scope: 'Out of Scope',
    source: 'Source', sage_quote: 'Sage Quote', likely_start_date: 'Likely Start',
    win_probability: 'Win Probability', health_update_period: 'Health Update Period',
    ses_applicable: 'SES Applicable', issued_version: 'Issued Version',
    rate_card_id: 'Rate Card', document: 'Document',
    parent_sow_id: 'Parent Opportunity',
  };

  const maybeFmtDate = (v) => {
    if (!v) return v;
    if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return formatDate(v);
    return v;
  };

  const renderItem = (item) => {
    if (item.kind === 'comment') {
      return (
        <div key={item.id + item.ts} className="flex gap-2">
          <div className="w-2 h-2 rounded-full bg-gold mt-1.5 flex-shrink-0" />
          <div className="flex-1 bg-gold/5 border border-gold/20 rounded-r px-2 py-1.5">
            <div className="text-[8px] text-gray-400">{fmtDateTimeLong(item.ts)} · <span className="font-semibold">{item.actor}</span></div>
            <div className="text-[10px] text-gray-700 mt-0.5">{item.detail}</div>
          </div>
        </div>
      );
    }
    // audit entry
    const label = FIELD_LABELS[item.field_name] || item.field_name;
    const isCreate = !item.field_name || item.detail === 'Opportunity created';
    const isDocument = item.field_name === 'document';
    return (
      <div key={item.id + item.ts} className="flex gap-2">
        <div className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${isCreate ? 'bg-emerald-400' : isDocument ? 'bg-blue-400' : 'bg-gray-300'}`} />
        <div className="flex-1 py-0.5">
          <span className="text-[8px] text-gray-400">{fmtDateTimeLong(item.ts)}</span>
          {item.actor && <span className="text-[8px] text-gray-500"> · {item.actor}</span>}
          {isCreate ? (
            <span className="ml-1 text-[8px] font-semibold text-emerald-600">Opportunity created</span>
          ) : isDocument ? (
            <span className="ml-1 text-[8px] text-blue-700">
              <span className="font-semibold">{item.detail}</span>
              {item.new_value ? <> — <span className="font-medium">{item.new_value}</span></> : null}
            </span>
          ) : (
            <span className="ml-1 text-[8px] text-gray-600">
              Changed <span className="font-semibold text-navy">{label}</span>
              {/* A change to a SOW money field comes without its values for anybody not shown SOW money. */}
              {(item.old_value !== undefined || item.new_value !== undefined) && <>
              {' '}from <span className="line-through text-gray-400">{item.old_value ? maybeFmtDate(item.old_value) : '(empty)'}</span>
              {' '}to <span className="font-semibold">{item.new_value ? maybeFmtDate(item.new_value) : '(empty)'}</span>
              </>}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <div>
      {/* Unified actions + comments panel */}
      <ActionsPanel
        entityType="sow" entityId={sowId}
        people={people} milestones={milestones} currentUser={currentUser}
        onChanged={onRefresh}
      />

      {/* Activity feed / timeline */}
      <div className="bg-white border border-gray-200 rounded p-3">
        <div className="flex items-center justify-between mb-3">
          <div className="text-[8px] font-bold text-gray-500 uppercase">History</div>
          <div className="flex gap-1">
            {['feed','timeline'].map(m => (
              <button key={m} onClick={() => setViewMode(m)}
                className={`px-2 py-0.5 text-[8px] rounded capitalize font-semibold ${viewMode === m ? 'bg-navy text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                {m === 'feed' ? 'Feed' : 'Timeline'}
              </button>
            ))}
          </div>
        </div>
        {activity === null && <div className="text-center py-4 text-[10px] text-gray-400">Loading…</div>}
        {activity?.length === 0 && <div className="text-center py-4 text-[10px] text-gray-400">No activity yet.</div>}
        {(activity || []).length > 0 && viewMode === 'feed' && (
          <div className="space-y-1.5 relative">
            <div className="absolute left-[3px] top-3 bottom-3 w-px bg-gray-100" />
            {[...(activity || [])].sort((a, b) => (b.ts || '').localeCompare(a.ts || '')).map(renderItem)}
          </div>
        )}
        {(activity || []).length > 0 && viewMode === 'timeline' && (() => {
          const sorted = [...activity].sort((a, b) => (b.ts || '').localeCompare(a.ts || ''));
          const groups = [];
          for (const item of sorted) {
            const date = (item.ts || '').slice(0, 10);
            const last = groups[groups.length - 1];
            if (last && last.date === date) last.items.push(item);
            else groups.push({ date, items: [item] });
          }
          return (
            <div className="space-y-4">
              {groups.map(({ date, items: gItems }) => (
                <div key={date}>
                  <div className="flex items-center gap-2 mb-2">
                    <div className="flex-1 h-px bg-gray-100" />
                    <span className="text-[8px] font-bold text-gray-400 uppercase tracking-wider px-1 bg-white">
                      {formatDate(date)}
                    </span>
                    <div className="flex-1 h-px bg-gray-100" />
                  </div>
                  <div className="space-y-1.5 relative">
                    <div className="absolute left-[3px] top-3 bottom-3 w-px bg-gray-100" />
                    {gItems.map(renderItem)}
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </div>
    </div>
  );
}

// ── INLINE NEW CONTACT (reused in EditSOWModal) ───────────────────────────────
function NewContactInlineSow({ clientId, onCreated, nonCommercial = false }) {
  const { toast } = useToast();
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const create = async () => {
    if (!form.first_name.trim() || !form.last_name.trim()) return;
    setSaving(true);
    try {
      const is_non_commercial = nonCommercial ? 1 : 0;
      const r = await api.post('client-contacts', { ...form, client_id: clientId, is_non_commercial });
      onCreated({ id: r.id, first_name: form.first_name, last_name: form.last_name, email: form.email, is_non_commercial });
      setForm({ first_name: '', last_name: '', email: '', phone: '' });
      setShow(false);
    } catch (e) { toast.error('Failed to create contact: ' + e.message); }
    setSaving(false);
  };

  if (!show) return <button type="button" onClick={() => setShow(true)} className="text-[8px] text-gold underline ml-1 whitespace-nowrap">+ New</button>;
  return (
    <div className="mt-1 bg-blue-50 border border-blue-200 rounded p-2 space-y-1">
      <div className="text-[8px] font-bold text-blue-700 uppercase tracking-wide mb-1">{nonCommercial ? 'New Non-commercial Contact' : 'New Commercial Contact'}</div>
      <div className="grid grid-cols-2 gap-1">
        <input className="form-input text-[9px]" placeholder="First name *" value={form.first_name} onChange={e => set('first_name', e.target.value)} autoFocus />
        <input className="form-input text-[9px]" placeholder="Last name *" value={form.last_name} onChange={e => set('last_name', e.target.value)} />
        <input className="form-input text-[9px]" placeholder="Email" value={form.email} onChange={e => set('email', e.target.value)} />
        <input className="form-input text-[9px]" placeholder="Phone" value={form.phone} onChange={e => set('phone', e.target.value)} />
      </div>
      <div className="flex gap-1">
        <button type="button" onClick={create} disabled={!form.first_name.trim() || !form.last_name.trim() || saving} className="px-2 py-0.5 text-[8px] font-semibold bg-navy text-white rounded disabled:opacity-50">{saving ? '…' : 'Save Contact'}</button>
        <button type="button" onClick={() => setShow(false)} className="px-1.5 py-0.5 text-[8px] border border-gray-200 rounded text-gray-500">Cancel</button>
      </div>
    </div>
  );
}

// ── EDIT SOW MODAL ────────────────────────────────────────────────────────────
// The SOW's own money fields: offered and sent only to holders of sow:see-financials,
// so a save by anybody else cannot zero a value they were never shown.
const SOW_FORM_MONEY = ['total_excl_vat', 'reaug_day_rate', 'reaug_ts_budget'];

function EditSOWModal({ sow, people, milestoneSumActive, onClose, onSave }) {
  const seesMoney = canAccess('sow:see-financials');
  const withoutHiddenMoney = (data) => seesMoney ? data
    : Object.fromEntries(Object.entries(data).filter(([k]) => !SOW_FORM_MONEY.includes(k)));
  // Only show active staff; always keep the currently-selected person so existing assignments don't vanish
  const activePeople = people.filter(p =>
    p.employee_status === 'active' || p.id === sow.owner_id || p.id === sow.delivery_owner_id
  );
  const [mismatchAccepted, setMismatchAccepted] = useState(false);
  const [form, setForm] = useState({
    client_id: sow.client_id || '',
    detail: sow.detail || '',
    type: sow.type || '',
    commercial_type: sow.commercial_type || '',
    owner_id: sow.owner_id || '',
    delivery_owner_id: sow.delivery_owner_id || '',
    contact: sow.contact || '',
    client_contact_id: sow.client_contact_id || '',
    non_commercial_contact_id: sow.non_commercial_contact_id || '',
    issued_version: sow.issued_version ?? 0,
    issued_date: sow.issued_date || '',
    expiry_date: sow.expiry_date || '',
    total_excl_vat: sow.total_excl_vat || '',
    payment_terms: sow.payment_terms || 30,
    po_number: sow.po_number || '',
    forecast_completion: sow.forecast_completion || '',
    received_date: sow.received_date || '',
    submission_deadline: sow.submission_deadline || '',
    sourcing_event_id: sow.sourcing_event_id || '',
    status: sow.status || 'Draft',
    health_update_period: sow.health_update_period ?? 7,
    reaug_days: sow.reaug_days || '',
    reaug_day_rate: sow.reaug_day_rate || '',
    reaug_ts_budget: sow.reaug_ts_budget || '',
    magnitude: sow.magnitude || '',
    client_priority: sow.client_priority || '',
    win_probability: sow.win_probability ?? '',
    likely_start_date: sow.likely_start_date || '',
    ses_applicable: sow.ses_applicable ? 1 : 0,
    parent_sow_id: sow.parent_sow_id || '',
    is_framework: sow.is_framework ? 1 : 0,
    client_reference: sow.client_reference || '',
  });
  const [sowTypes, setSowTypes] = useState([]);
  const [commercialTypes, setCommercialTypes] = useState([]);
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [allSows, setAllSows] = useState([]);
  const [parentSearch, setParentSearch] = useState('');
  const [showParentDropdown, setShowParentDropdown] = useState(false);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get('config-options', { category: 'sow_type' })
      .then(opts => setSowTypes(opts || []))
      .catch(() => setSowTypes([{ label: 'PI/TI' }, { label: 'Pass-Through' }, { label: 'Support' }]));
    api.get('config-options', { category: 'commercial_type' })
      .then(opts => setCommercialTypes(opts || []))
      .catch(() => setCommercialTypes([{ label: 'Fixed Price' }, { label: 'T&M' }]));
    api.get('clients')
      .then(c => setClients(c || []))
      .catch(() => setClients([]));
  }, []);

  const effectiveClientId = form.client_id || sow.client_id;
  useEffect(() => {
    if (!effectiveClientId) return;
    api.get('client-contacts', { client_id: effectiveClientId })
      .then(c => setContacts(c || []))
      .catch(() => setContacts([]));
  }, [effectiveClientId]);

  useEffect(() => {
    api.get('sows', { limit: 1000 })
      .then(r => setAllSows((Array.isArray(r) ? r : (r?.results || [])).filter(s => s.id !== sow.id)))
      .catch(() => {});
  }, [sow.id]);

  const issuedDate = sow.issued_date || sow.sow_date || '';
  const parentSow = allSows.find(s => s.id === form.parent_sow_id) || null;
  const filteredParentSows = parentSearch.trim().length > 0
    ? allSows.filter(s => s.reference?.toLowerCase().includes(parentSearch.toLowerCase()) || s.detail?.toLowerCase().includes(parentSearch.toLowerCase())).slice(0, 8)
    : [];
  const poBlocksSave = !!form.parent_sow_id && !!form.po_number.trim();

  return (
    <Modal title={`Edit ${sow.reference}`} onClose={onClose} wide footer={
      <><button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
        <button disabled={poBlocksSave} onClick={() => onSave(withoutHiddenMoney({ ...form, issued_version: parseInt(form.issued_version) || 0, total_excl_vat: parseFloat(form.total_excl_vat) || 0, reaug_days: form.reaug_days !== '' ? parseFloat(form.reaug_days) : null, reaug_day_rate: form.reaug_day_rate !== '' ? parseFloat(form.reaug_day_rate) : null, reaug_ts_budget: form.reaug_ts_budget !== '' ? parseFloat(form.reaug_ts_budget) : null, magnitude: form.magnitude || null, client_priority: form.client_priority || null, win_probability: form.win_probability !== '' ? parseInt(form.win_probability) : null, likely_start_date: form.likely_start_date || null, parent_sow_id: form.parent_sow_id || null, is_framework: form.is_framework ? 1 : 0, client_reference: form.client_reference || null, _resolvedMismatch: mismatchAccepted }))} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40 disabled:cursor-not-allowed">Save</button></>
    }>
      <div className="grid grid-cols-2 gap-2">
        {seesMoney && milestoneSumActive > 0 && !mismatchAccepted && Math.abs(milestoneSumActive - (sow.total_excl_vat || 0)) >= 1.00 && (
          <div className="col-span-2 flex items-center gap-3 px-3 py-2 bg-amber-50 border border-amber-300 rounded text-[9px]">
            <span className="text-amber-700">⚠ Active milestone sum <strong>{formatMoney(milestoneSumActive, 2)}</strong> differs from SOW total <strong>{formatMoney(sow.total_excl_vat, 2)}</strong></span>
            <button type="button" onClick={() => { set('total_excl_vat', milestoneSumActive.toFixed(2)); setMismatchAccepted(true); }}
              className="ml-auto whitespace-nowrap px-2.5 py-1 text-[9px] font-semibold bg-amber-100 border border-amber-400 text-amber-800 rounded hover:bg-amber-200">
              Update total to {formatMoney(milestoneSumActive, 2)}
            </button>
            <button type="button" onClick={() => setMismatchAccepted(true)} className="text-[9px] text-gray-400 hover:text-gray-600">Dismiss</button>
          </div>
        )}
        <div className="col-span-2 relative">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Parent Opportunity <span className="font-normal normal-case text-gray-400">(optional — links this SOW to a parent's budget &amp; PO)</span></label>
          {parentSow ? (
            <div className="flex items-center gap-1.5 px-2 py-1 bg-blue-50 border border-blue-200 rounded text-[10px]">
              <span className="font-mono font-bold text-navy">{parentSow.reference}</span>
              <span className="text-gray-500 truncate">{parentSow.detail}</span>
              <button type="button" onClick={() => { set('parent_sow_id', ''); setParentSearch(''); }} className="ml-auto text-gray-400 hover:text-red-500 text-xs leading-none">✕</button>
            </div>
          ) : (
            <>
              <input
                className="form-input"
                placeholder="Search by reference or detail…"
                value={parentSearch}
                onChange={e => { setParentSearch(e.target.value); setShowParentDropdown(true); }}
                onFocus={() => setShowParentDropdown(true)}
                onBlur={() => setTimeout(() => setShowParentDropdown(false), 150)}
              />
              {showParentDropdown && filteredParentSows.length > 0 && (
                <div className="absolute z-50 left-0 right-0 top-full mt-0.5 bg-white border border-gray-200 rounded shadow-lg max-h-40 overflow-y-auto">
                  {filteredParentSows.map(s => (
                    <button key={s.id} type="button"
                      className="w-full text-left px-2 py-1.5 hover:bg-blue-50 text-[10px] flex items-center gap-2"
                      onMouseDown={() => { set('parent_sow_id', s.id); setParentSearch(''); setShowParentDropdown(false); }}>
                      <span className="font-mono font-bold text-navy whitespace-nowrap">{s.reference}</span>
                      {Number(s.is_framework) === 1 && <span className="text-[7px] font-bold px-1 py-0.5 rounded bg-navy/10 text-navy">FRAMEWORK</span>}
                      <span className="text-gray-500 truncate">{s.detail}</span>
                      <span className="ml-auto text-gray-400 whitespace-nowrap">{s.client_name}</span>
                    </button>
                  ))}
                </div>
              )}
            </>
          )}
          {parentSow?.is_framework ? (
            <div className="text-[8px] text-emerald-600 mt-1">This is a Framework — pricing is treated as pre-agreed, and the Mini SOW PDF is enabled below.</div>
          ) : (
            <div className="text-[8px] text-gray-400 mt-1">Pick a SOW flagged as a Framework (tick the box below on its own record) to make this a pre-agreed-pricing call-off.</div>
          )}
        </div>
        <div className="col-span-2 border-t border-gray-100 pt-2">
          <label className="flex items-center gap-1.5 text-[9px] font-semibold text-gray-600 mb-1.5">
            <input type="checkbox" checked={!!form.is_framework} onChange={e => set('is_framework', e.target.checked ? 1 : 0)} />
            This SOW is itself a Framework (pre-agreed pricing — carries the PO/budget that call-off SOWs above draw against)
          </label>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Client Reference <span className="font-normal normal-case text-gray-400">(their request/reference number — e.g. RR DS Key)</span></label>
          <input className="form-input" value={form.client_reference} onChange={e => set('client_reference', e.target.value)} />
        </div>
        <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Client</label>
          <select className="form-select" value={form.client_id} onChange={e => setForm(p => ({ ...p, client_id: e.target.value, client_contact_id: '', non_commercial_contact_id: '' }))}>
            <option value="">— Select client —</option>
            {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <div className="col-span-2"><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Description / Title</label><input className="form-input" value={form.detail} onChange={e => set('detail', e.target.value)} /></div>
        <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Type</label>
          <select className="form-select" value={form.type} onChange={e => set('type', e.target.value)}>
            <option value="">Select type...</option>
            {sowTypes.map(t => { const lbl = t.label || t.value || t; return <option key={lbl} value={lbl}>{lbl}</option>; })}
          </select>
        </div>
        <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Commercial Type</label>
          <select className="form-select" value={form.commercial_type} onChange={e => set('commercial_type', e.target.value)}>
            <option value="">Select...</option>
            {commercialTypes.map(t => { const lbl = t.label || t.value || t; return <option key={lbl} value={lbl}>{lbl}</option>; })}
          </select>
        </div>
        <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Project Manager</label>
          <select className="form-select" value={form.owner_id} onChange={e => set('owner_id', e.target.value)}>
            <option value="">Select...</option>
            {activePeople.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
          </select>
        </div>
        <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Delivery Owner</label>
          <select className="form-select" value={form.delivery_owner_id} onChange={e => set('delivery_owner_id', e.target.value)}>
            <option value="">— none —</option>
            {activePeople.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
          </select>
        </div>
        <div>
          <div className="flex items-center justify-between mb-0.5">
            <label className="text-[8px] font-bold text-gray-500 uppercase">Commercial Contact</label>
            {effectiveClientId && (canAccess('clients') || canAccess('tracker')) && <NewContactInlineSow clientId={effectiveClientId} onCreated={c => { setContacts(prev => [...prev, c]); set('client_contact_id', c.id); }} />}
          </div>
          <select className="form-select" value={form.client_contact_id} onChange={e => set('client_contact_id', e.target.value)}>
            <option value="">— none —</option>
            {contactOptions(contacts, { selectedId: form.client_contact_id, excludeId: form.non_commercial_contact_id })
              .map(c => <option key={c.id} value={c.id}>{contactOptionText(c)}</option>)}
          </select>
        </div>
        <div>
          <div className="flex items-center justify-between mb-0.5">
            <label className="text-[8px] font-bold text-gray-500 uppercase">Non-commercial Contact <span className="font-normal normal-case text-gray-400">(optional)</span></label>
            {effectiveClientId && (canAccess('clients') || canAccess('tracker')) && <NewContactInlineSow clientId={effectiveClientId} nonCommercial onCreated={c => { setContacts(prev => [...prev, c]); set('non_commercial_contact_id', c.id); }} />}
          </div>
          <NonCommercialContactSelect contacts={contacts} clientId={effectiveClientId}
            value={form.non_commercial_contact_id} commercialId={form.client_contact_id}
            onChange={v => set('non_commercial_contact_id', v)} />
        </div>
        <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Status</label>
          <div className="form-input bg-gray-50 text-gray-500 cursor-not-allowed text-[9px]">{sow.status}</div>
          <div className="text-[7px] text-gray-400 mt-0.5">Use the status action buttons to change status</div>
        </div>
        <div />
        {seesMoney && <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Total excl VAT (£)</label><input className="form-input" type="number" step="0.01" value={form.total_excl_vat} onChange={e => set('total_excl_vat', e.target.value)} /></div>}
        {/* Issued Date — normally auto-set on issue, but editable so bad/missing
            migration data can be cleansed. */}
        <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Issued Date <span className="font-normal text-gray-400 normal-case">(set on issue — edit to correct)</span></label>
          <input className="form-input" type="date" value={form.issued_date} onChange={e => set('issued_date', e.target.value)} />
        </div>
        <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Issued Version</label><input className="form-input" type="number" min="0" value={form.issued_version} onChange={e => set('issued_version', e.target.value)} /></div>
        <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Expiry Date</label><input className="form-input" type="date" value={form.expiry_date} onChange={e => set('expiry_date', e.target.value)} /></div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">PO Number</label>
          {form.parent_sow_id && !form.po_number.trim() ? (
            <div className="form-input bg-gray-50 text-gray-400 cursor-not-allowed text-[10px] italic">Not applicable — PO is held at the parent SOW level</div>
          ) : (
            <>
              <input className={`form-input ${poBlocksSave ? 'border-red-400 focus:ring-red-300' : ''}`} value={form.po_number} onChange={e => set('po_number', e.target.value)} />
              {poBlocksSave && <div className="text-[8px] text-red-600 mt-0.5 font-semibold">⚠ Clear this before saving — future invoices will be applied to the parent SOW's PO</div>}
            </>
          )}
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Payment Terms (days)</label>
          {form.parent_sow_id ? (
            <div className="form-input bg-gray-50 text-gray-500 text-[10px]">Inherited from parent ({parentSow?.payment_terms ?? 30} days)</div>
          ) : (
            <input className="form-input" type="number" value={form.payment_terms} onChange={e => set('payment_terms', e.target.value)} />
          )}
          {!form.parent_sow_id && <div className="text-[8px] text-gray-400 mt-0.5">Applies to the SOW / PO — not per milestone</div>}
        </div>
        <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Date Received</label><input className="form-input" type="date" value={form.received_date} onChange={e => set('received_date', e.target.value)} /></div>
        <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Submission Deadline</label><input className="form-input" type="date" value={form.submission_deadline} onChange={e => set('submission_deadline', e.target.value)} /></div>
        <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Sourcing Event ID</label><input className="form-input" value={form.sourcing_event_id} onChange={e => set('sourcing_event_id', e.target.value)} placeholder="e.g. SOR-12345" /></div>
        <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Expected Completion</label><input className="form-input" type="date" value={form.forecast_completion} onChange={e => set('forecast_completion', e.target.value)} /></div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Health Update Period (days)</label>
          <input className="form-input" type="number" min="1" max="90" value={form.health_update_period} onChange={e => set('health_update_period', parseInt(e.target.value) || 7)} />
          <div className="text-[8px] text-gray-400 mt-0.5">How often a health update is expected (default: 7 days)</div>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Expected Magnitude</label>
          <select className="form-select" value={form.magnitude} onChange={e => set('magnitude', e.target.value)}>
            <option value="">Select...</option>
            <option value="Small">Small</option>
            <option value="Medium">Medium</option>
            <option value="Large">Large</option>
          </select>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Client Priority</label>
          <select className="form-select" value={form.client_priority} onChange={e => set('client_priority', e.target.value)}>
            <option value="">Select...</option>
            <option value="P1">P1 — Highest</option>
            <option value="P2">P2</option>
            <option value="P3">P3</option>
            <option value="P4">P4</option>
            <option value="P5">P5 — Lowest</option>
          </select>
          <div className="text-[8px] text-gray-400 mt-0.5">The client's own priority ranking (optional) — helps focus delivery effort.</div>
        </div>
        {['Draft', 'Issued'].includes(form.status) && (<>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Win Probability (%)</label>
            <input className="form-input" type="number" min="0" max="100" value={form.win_probability} onChange={e => set('win_probability', e.target.value)} placeholder="0–100" />
            <div className="text-[8px] text-gray-400 mt-0.5">Likelihood of winning this SOW (used in pipeline forecasting)</div>
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Likely Start Date</label>
            <input className="form-input" type="date" value={form.likely_start_date} onChange={e => set('likely_start_date', e.target.value)} />
            <div className="text-[8px] text-gray-400 mt-0.5">Expected start if won (used in resource utilisation forecast)</div>
          </div>
        </>)}
        <div className="col-span-2 pt-1 border-t border-gray-100">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">SES Numbers Applicable to Invoices?</label>
          <div className="flex gap-3">
            {[{ val: 1, label: 'Yes — SES numbers required on invoices' }, { val: 0, label: 'No — SES numbers not required' }].map(({ val, label }) => (
              <label key={val} className={`flex items-center gap-2 px-3 py-2 rounded border cursor-pointer text-[9px] flex-1 ${form.ses_applicable === val ? 'border-gold bg-gold/5 font-semibold' : 'border-gray-200 text-gray-500 hover:border-gray-400'}`}>
                <input type="radio" name="edit_ses_applicable" className="sr-only" checked={form.ses_applicable === val} onChange={() => set('ses_applicable', val)} />
                <span className={`w-3 h-3 rounded-full border-2 flex-shrink-0 ${form.ses_applicable === val ? 'border-gold bg-gold' : 'border-gray-300'}`} />
                {label}
              </label>
            ))}
          </div>
          {!!form.ses_applicable && <div className="mt-1.5 text-[7px] text-amber-600">⚠ With SES enabled, each milestone needs a SES number before moving to Ready to Bill or Invoiced.</div>}
        </div>
        {['Resource Augmentation', 'ReAug'].includes(form.type) && (<>
          <div className="col-span-2 pt-1 border-t border-gray-100">
            <div className="text-[8px] font-bold text-purple-600 uppercase tracking-wide mb-2">Resource Augmentation Details</div>
          </div>
          <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Provisioned Days</label>
            <input className="form-input" type="number" step="0.5" value={form.reaug_days} onChange={e => set('reaug_days', e.target.value)} placeholder="e.g. 120" />
          </div>
          {seesMoney && <>
          <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Day Rate (£)</label>
            <input className="form-input" type="number" step="0.01" value={form.reaug_day_rate} onChange={e => set('reaug_day_rate', e.target.value)} placeholder="e.g. 480.00" />
          </div>
          <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">T&amp;S Budget (£)</label>
            <input className="form-input" type="number" step="0.01" value={form.reaug_ts_budget} onChange={e => set('reaug_ts_budget', e.target.value)} placeholder="e.g. 5000.00" />
          </div>
          </>}
        </>)}
      </div>
    </Modal>
  );
}

// ── REAUG INVOICE MODAL ───────────────────────────────────────────────────────
function ReaugInvoiceModal({ sow, invoice, onClose, onSave }) {
  const isEdit = !!invoice;
  const [form, setForm] = useState({
    invoice_number: invoice?.invoice_number || '',
    invoice_date:   invoice?.invoice_date   || new Date().toISOString().split('T')[0],
    days_billed:    invoice?.days_billed    ?? '',
    labour_amount:  invoice?.labour_amount  ?? '',
    ts_amount:      invoice?.ts_amount      ?? 0,
    status:         invoice?.status         || 'Submitted',
    notes:          invoice?.notes          || '',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const valid = form.invoice_number.trim() && form.invoice_date && form.days_billed !== '';

  return (
    <Modal title={isEdit ? `Edit Invoice ${invoice.invoice_number}` : 'Add Invoice'} onClose={onClose} wide footer={
      <><button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
        <button onClick={() => { if (valid) onSave(form); }} disabled={!valid}
          className="px-3 py-1 text-[10px] font-bold bg-navy text-white rounded disabled:opacity-40">
          {isEdit ? 'Save' : 'Add Invoice'}
        </button></>
    }>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Invoice Number *</label>
          <input className="form-input" value={form.invoice_number} onChange={e => set('invoice_number', e.target.value)} placeholder="e.g. INV-2026-001" /></div>
        <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Invoice Date *</label>
          <input className="form-input" type="date" value={form.invoice_date} onChange={e => set('invoice_date', e.target.value)} /></div>
        <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Days Billed *</label>
          <input className="form-input" type="number" step="0.5" min="0" value={form.days_billed} onChange={e => set('days_billed', e.target.value)} /></div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Labour Amount (£ excl VAT)</label>
          <input className="form-input" type="number" step="0.01" min="0" value={form.labour_amount} onChange={e => set('labour_amount', e.target.value)}
            placeholder={sow.reaug_day_rate && form.days_billed ? `e.g. ${(sow.reaug_day_rate * form.days_billed).toFixed(2)}` : ''} />
          {sow.reaug_day_rate && form.days_billed && !form.labour_amount && (
            <button onClick={() => set('labour_amount', (parseFloat(sow.reaug_day_rate) * parseFloat(form.days_billed)).toFixed(2))}
              className="text-[7px] text-gold hover:underline mt-0.5">
              Auto-fill (£{(sow.reaug_day_rate * form.days_billed).toFixed(2)})
            </button>
          )}
        </div>
        <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">T&amp;S Amount (£ excl VAT)</label>
          <input className="form-input" type="number" step="0.01" min="0" value={form.ts_amount} onChange={e => set('ts_amount', e.target.value)} placeholder="0.00" /></div>
        <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Status</label>
          <select className="form-select" value={form.status} onChange={e => set('status', e.target.value)}>
            {['Submitted','Approved','Paid'].map(s => <option key={s}>{s}</option>)}
          </select></div>
        <div className="col-span-2"><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Notes</label>
          <textarea className="form-input" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} /></div>
      </div>
    </Modal>
  );
}

// ── REAUG TIMESHEET MODAL ──────────────────────────────────────────────────────
function ReaugTimesheetModal({ timesheet, people, invoices, onClose, onSave }) {
  const isEdit = !!timesheet;
  const d = new Date();
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1);
  d.setDate(diff);
  const mondayDefault = d.toISOString().split('T')[0];

  const [form, setForm] = useState({
    person_id:       timesheet?.person_id       || '',
    week_commencing: timesheet?.week_commencing || mondayDefault,
    days_worked:     timesheet?.days_worked     ?? '',
    notes:           timesheet?.notes           || '',
    status:          timesheet?.status          || 'Draft',
    invoice_id:      timesheet?.invoice_id      || '',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const valid = form.week_commencing && form.days_worked !== '';

  return (
    <Modal title={isEdit ? 'Edit Timesheet Entry' : 'Add Timesheet Entry'} onClose={onClose} wide footer={
      <><button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
        <button onClick={() => { if (valid) onSave(form); }} disabled={!valid}
          className="px-3 py-1 text-[10px] font-bold bg-navy text-white rounded disabled:opacity-40">
          {isEdit ? 'Save' : 'Add Entry'}
        </button></>
    }>
      <div className="grid grid-cols-2 gap-3">
        <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Contractor</label>
          <select className="form-select" value={form.person_id} onChange={e => set('person_id', e.target.value)}>
            <option value="">— select person —</option>
            {people.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
          </select></div>
        <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Week Commencing (Mon) *</label>
          <input className="form-input" type="date" value={form.week_commencing} onChange={e => set('week_commencing', e.target.value)} /></div>
        <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Days Worked *</label>
          <input className="form-input" type="number" step="0.5" min="0" max="5" value={form.days_worked} onChange={e => set('days_worked', e.target.value)} placeholder="0–5" /></div>
        <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Status</label>
          <select className="form-select" value={form.status} onChange={e => set('status', e.target.value)}>
            {['Draft','Submitted','Approved'].map(s => <option key={s}>{s}</option>)}
          </select></div>
        <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Link to Invoice</label>
          <select className="form-select" value={form.invoice_id} onChange={e => set('invoice_id', e.target.value)}>
            <option value="">— unlinked —</option>
            {invoices.map(inv => <option key={inv.id} value={inv.id}>{inv.invoice_number} ({formatDate(inv.invoice_date)})</option>)}
          </select></div>
        <div className="col-span-2"><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Notes</label>
          <textarea className="form-input" rows={2} value={form.notes} onChange={e => set('notes', e.target.value)} /></div>
      </div>
    </Modal>
  );
}

// ── PO RECEIVED MODAL ─────────────────────────────────────────────────────────
function IssueWorkflowModal({ sow, detailId, milestoneSumActive, milestones = [], onClose, onIssued, preset = null }) {
  const { toast } = useToast();
  const [saving,        setSaving]        = useState(false);
  const [documents,     setDocuments]     = useState(null);
  const [req,           setReq]           = useState(null);
  const [history,       setHistory]       = useState(null);
  const [error,         setError]         = useState('');
  const [mismatchFixed,    setMismatchFixed]    = useState(false);
  const [fixingMismatch,   setFixingMismatch]   = useState(false);
  // Milestone forecast dates, edited here and saved with the issuance. Held apart from the
  // milestones themselves so an untouched date is not rewritten with the same value — a
  // no-op PUT still stamps updated_at and still writes an audit row.
  const [msDates,          setMsDates]          = useState({});
  const todayStr = new Date().toISOString().slice(0, 10);
  const addDaysStr = (d, n) => { const dt = new Date(`${d}T00:00:00`); if (isNaN(dt) || n === '' || isNaN(Number(n))) return ''; dt.setDate(dt.getDate() + Number(n)); return dt.toISOString().slice(0, 10); };
  const daysBetween = (a, b) => { const da = new Date(`${a}T00:00:00`), db = new Date(`${b}T00:00:00`); if (isNaN(da) || isNaN(db)) return ''; return String(Math.round((db - da) / 86400000)); };
  const [form, setForm] = useState(() => {
    const t = new Date().toISOString().slice(0, 10);
    return {
      version:         '',   // set once history loads
      document_id:     '',
      override:        false,
      override_reason: '',
      // Admin correction: issue the SAME version number again (wrong file attached).
      reissue_same:    false,
      reissue_reason:  '',
      notes:           '',
      issued_date:     t,
      days_to_expire:  '56',
      expiry_date:     addDaysStr(t, 56),
    };
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  // Keep issued date, days-to-expire and expiry date consistent when any changes.
  const onIssuedDate = (v) => setForm(f => ({ ...f, issued_date: v, expiry_date: addDaysStr(v, f.days_to_expire) || f.expiry_date }));
  const onDaysToExpire = (v) => setForm(f => ({ ...f, days_to_expire: v, expiry_date: addDaysStr(f.issued_date, v) || f.expiry_date }));
  const onExpiryDate = (v) => setForm(f => ({ ...f, expiry_date: v, days_to_expire: daysBetween(f.issued_date, v) || f.days_to_expire }));

  useEffect(() => {
    Promise.all([
      api.get(`sows/${detailId}/documents`).catch(() => []),
      api.get(`sows/${detailId}/issue-requirements`).catch(() => null),
      api.get(`sows/${detailId}/issued-versions`).catch(() => []),
    ]).then(([docs, r, hist]) => {
      const pdfs = (docs || []).filter(d =>
        d.url?.startsWith('/api/uploads/') &&
        (/\.pdf(\?|$)/i.test(d.url) || /\.pdf$/i.test(d.title || ''))
      );
      setDocuments(pdfs);
      setReq(r);
      // Newest first, and within a version the live issue before any it replaced.
      const versions = (hist || []).sort((a, b) =>
        b.version - a.version || String(b.issued_at || '').localeCompare(String(a.issued_at || '')));
      setHistory(versions);
      const nextVersion = versions.length > 0 ? versions[0].version + 1 : (sow.issued_version || 0) + 1 || 1;
      setForm(f => ({
        ...f,
        // A version read off the document somebody just uploaded, where there is one. It
        // still has to be unused, and the conflict warning below says so if it is not.
        version: String(preset?.version && !versions.some(v => v.version === preset.version) ? preset.version : nextVersion),
        // The document that started this. Only if it is one of the PDFs the issue workflow
        // will accept — an external link is refused by the server and preselecting it would
        // put a value in the box that cannot be submitted.
        document_id: (preset?.document_id && pdfs.some(d => d.id === preset.document_id)) ? preset.document_id : f.document_id,
      }));
    });
  }, [detailId]);

  // Forecast dates already in the past. On a re-issue this is the normal case, and it is the
  // thing most worth catching before the SOW goes back out.
  const stalemilestones = milestones.filter(m => {
    const val = msDates[m.id] ?? (m.forecast_date || '');
    return val && val < todayStr;
  });

  // A version that has been superseded is history, not a number in use: re-issuing the
  // same number again is exactly what superseding it allowed.
  const liveVersions = (history || []).filter(v => !v.superseded_at);
  const usedVersions = new Set(liveVersions.map(v => v.version));
  const parsedVersion = parseInt(form.version);
  const versionConflict = !isNaN(parsedVersion) && usedVersions.has(parsedVersion);
  const conflictingIssue = versionConflict ? liveVersions.find(v => v.version === parsedVersion) : null;

  const canOverride = req?.canOverride;
  // The admin correction for "the wrong file was attached": issue the same version
  // again rather than bumping the SOW to a version that was never sent to anybody.
  // Draft only — replacing what sits under a live Issued SOW is how two people end up
  // holding different documents carrying the same version number.
  const canReissueSame = !!canOverride && sow.status === 'Draft';
  const reissuing = versionConflict && canReissueSame && form.reissue_same;
  const conflictBlocks = versionConflict && !reissuing;
  const ready = req !== null && documents !== null && history !== null;

  const sowTotal = sow.total_excl_vat || 0;
  const showMismatchBanner = milestoneSumActive > 0 && !mismatchFixed && Math.abs(milestoneSumActive - sowTotal) >= 1.00;
  const fixMismatch = async () => {
    setFixingMismatch(true);
    try {
      await api.put(`sows/${detailId}`, { total_excl_vat: milestoneSumActive });
      await api.post('commentary', {
        entity_type: 'sow',
        entity_id: detailId,
        content: `Data validation resolution: SOW total updated from ${formatMoney(sowTotal, 2)} to ${formatMoney(milestoneSumActive, 2)} to match active milestone sum.`,
      });
      setMismatchFixed(true);
    } finally {
      setFixingMismatch(false);
    }
  };

  const blockers = ready && !form.override ? [
    ...(req?.qa?.pending?.length ? [`${req.qa.pending.length} QA step(s) pending`] : []),
    ...(!req?.approvalSatisfied ? [`Approval required (${req?.band?.label || 'value band'})`] : []),
  ] : [];

  const handleSubmit = async () => {
    if (!form.version || parsedVersion < 1) { setError('Version must be 1 or higher'); return; }
    if (conflictBlocks) { setError(`Version ${parsedVersion} has already been issued. Use a new version number.`); return; }
    if (reissuing && !form.reissue_reason.trim()) { setError(`A reason is required to re-issue v${parsedVersion}`); return; }
    if (!form.document_id && !form.override) { setError('Select a PDF Statement of Work, or use an admin override'); return; }
    if (form.override && !form.override_reason.trim()) { setError('Override reason is required'); return; }
    setSaving(true); setError('');
    try {
      // The milestone dates first. If issuing then fails on a gate, the dates the person
      // corrected are still saved — which is what they wanted either way, and re-typing
      // them after a refusal is how somebody gives up and issues with the old ones.
      const changed = Object.entries(msDates).filter(([id, v]) => {
        const m = milestones.find(x => x.id === id);
        return v && m && v !== (m.forecast_date || '');
      });
      for (const [id, forecast_date] of changed) {
        await api.put(`milestones/${id}`, { forecast_date }).catch(() => {});
      }
      await api.post(`sows/${detailId}/issue`, {
        version: parsedVersion,
        document_id: form.document_id || undefined,
        override: form.override || undefined,
        override_reason: form.override_reason || undefined,
        reissue_same_version: reissuing || undefined,
        reissue_reason: reissuing ? form.reissue_reason : undefined,
        notes: form.notes || undefined,
        issued_date: form.issued_date || undefined,
        expiry_date: form.expiry_date || undefined,
      });
      toast(changed.length
        ? `SOW issued, and ${changed.length} milestone date${changed.length === 1 ? '' : 's'} updated`
        : 'SOW issued successfully', 'success');
      onIssued();
    } catch (e) {
      setError(e.message || 'Failed to issue SOW');
      setSaving(false);
    }
  };

  return (
    <Modal title="Issue Opportunity" onClose={onClose} wide footer={
      <>
        <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
        <button onClick={handleSubmit} disabled={saving || !ready || conflictBlocks || (reissuing && !form.reissue_reason.trim()) || (blockers.length > 0 && !form.override)}
          className={`px-3 py-1 text-[10px] font-semibold text-white rounded disabled:opacity-40 ${form.override ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}`}>
          {saving ? 'Issuing…' : form.override ? 'Issue (Override)' : reissuing ? `Re-issue v${form.version} →` : `Issue as v${form.version || '?'} →`}
        </button>
      </>
    }>
      <div className="flex gap-5" style={{ minWidth: 560 }}>

        {/* ── Left: form ── */}
        <div className="flex-1 space-y-3 min-w-0">
          {showMismatchBanner && (
            <div className="flex items-center gap-3 px-3 py-2 bg-amber-50 border border-amber-300 rounded text-[9px]">
              <span className="text-amber-700">⚠ Active milestone sum <strong>{formatMoney(milestoneSumActive, 2)}</strong> differs from SOW total <strong>{formatMoney(sowTotal, 2)}</strong></span>
              <button type="button" onClick={fixMismatch} disabled={fixingMismatch}
                className="ml-auto whitespace-nowrap px-2.5 py-1 text-[9px] font-semibold bg-amber-100 border border-amber-400 text-amber-800 rounded hover:bg-amber-200 disabled:opacity-50">
                {fixingMismatch ? 'Fixing…' : `Fix — set to ${formatMoney(milestoneSumActive, 2)}`}
              </button>
              <button type="button" onClick={() => setMismatchFixed(true)} className="text-[9px] text-gray-400 hover:text-gray-600">Dismiss</button>
            </div>
          )}
          {!ready && <div className="text-center py-4 text-[10px] text-gray-400">Loading…</div>}
          {ready && <>
            {/* Requirements checklist */}
            {(req?.qa?.total > 0 || req?.requiredKey) && (
              <div className="bg-gray-50 border border-gray-200 rounded p-2.5 space-y-1">
                {req.qa?.total > 0 && (
                  <div className={`flex items-center gap-1.5 text-[9px] ${req.qa.complete ? 'text-emerald-700' : 'text-red-600'}`}>
                    <span>{req.qa.complete ? '✓' : '✗'}</span>
                    <span>QA Review — {req.qa.complete ? 'all steps approved' : `${req.qa.pending.length} step(s) pending: ${req.qa.pending.join(', ')}`}</span>
                  </div>
                )}
                {req.requiredKey && (
                  <div className={`flex items-center gap-1.5 text-[9px] ${req.approvalSatisfied ? 'text-emerald-700' : 'text-amber-700'}`}>
                    <span>{req.approvalSatisfied ? '✓' : '⚠'}</span>
                    <span>Approval — {req.approvalSatisfied
                      ? (req.approval ? `Approved by ${req.approval.by_name}` : 'You may approve this value band')
                      : `Requires authorisation for the '${req.band?.label || req.band?.id}' value band`}</span>
                  </div>
                )}
              </div>
            )}

            {/* Version */}
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Issue Version *</label>
              <div className="flex items-center gap-2">
                <input type="number" min="1" className={`form-input text-[10px] w-24 ${conflictBlocks ? 'border-red-400 bg-red-50' : reissuing ? 'border-amber-400 bg-amber-50' : ''}`} value={form.version} onChange={e => set('version', e.target.value)} />
                {/* Where the admin correction below is available it explains the conflict
                    and offers the way out, so this would only repeat it in red. */}
                {conflictBlocks && !canReissueSame && (
                  <span className="text-[9px] text-red-600 font-semibold">
                    Already used — choose a different number
                    {canOverride && sow.status !== 'Draft' ? ' (or move this SOW back to Draft to re-issue it)' : ''}
                  </span>
                )}
              </div>

              {/* ── RE-ISSUING THE SAME VERSION ────────────────────────────────
                  "The wrong file was attached (V1 instead of V2), so it needs re-issuing
                   as V2 with the V2 file, without bumping the whole SOW to V3."

                  Bumping to v3 to fix v2 puts a number on the client's desk that nothing
                  was ever sent at, and leaves the v2 they are holding looking current. So
                  an admin can issue the same number again against the right document. The
                  issue it replaces is kept and shown as Replaced, because what actually
                  went out and when is the part nobody may rewrite. */}
              {versionConflict && canReissueSame && (
                <div className="mt-2 border border-amber-300 rounded p-2.5 bg-amber-50">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="checkbox" className="mt-0.5" checked={form.reissue_same} onChange={e => set('reissue_same', e.target.checked)} />
                    <span className="text-[9px] font-bold text-amber-800">
                      Admin correction — re-issue v{parsedVersion} instead of bumping the version
                    </span>
                  </label>
                  <div className="text-[8px] text-amber-700 mt-1 ml-6">
                    v{parsedVersion} was issued
                    {conflictingIssue?.issued_at ? ` on ${formatDate(conflictingIssue.issued_at)}` : ''}
                    {conflictingIssue?.issued_by_name ? ` by ${conflictingIssue.issued_by_name}` : ''}
                    {conflictingIssue?.document_title ? `, with ${conflictingIssue.document_title}` : ''}.
                    That issue is kept in the history, marked Replaced, and this one takes its place at v{parsedVersion}.
                  </div>
                  {form.reissue_same && (
                    <div className="mt-2 ml-6">
                      <label className="block text-[8px] font-bold text-amber-700 uppercase mb-1">Reason for re-issuing this version *</label>
                      <textarea rows={2} className="form-input text-[10px] resize-none w-full border-amber-300" value={form.reissue_reason}
                        onChange={e => set('reissue_reason', e.target.value)}
                        placeholder="e.g. v1 of the Statement of Work was attached in error; re-issuing v2 with the correct document." />
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Issued date + validity window */}
            <div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Issued Date</label>
                  <input type="date" className="form-input text-[10px] w-full" value={form.issued_date} onChange={e => onIssuedDate(e.target.value)} />
                </div>
                <div>
                  <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Days to Expire</label>
                  <input type="number" min="1" className="form-input text-[10px] w-full" value={form.days_to_expire} onChange={e => onDaysToExpire(e.target.value)} />
                </div>
                <div>
                  <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Expiry Date</label>
                  <input type="date" className="form-input text-[10px] w-full" value={form.expiry_date} onChange={e => onExpiryDate(e.target.value)} />
                </div>
              </div>
              <div className="text-[8px] text-gray-400 mt-1">Defaults to 56 days after the issued date. Shorten it for fast-moving quotes (e.g. hardware whose cost can rise quickly).</div>
            </div>

            {/* Document */}
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Statement of Work (PDF) {!form.override && '*'}</label>
              {documents.length === 0
                ? <div className="text-[9px] text-amber-600">No uploaded PDFs found. Upload a PDF in the Documents tab first{canOverride ? ', or use an admin override' : ''}.</div>
                : <select className="form-select text-[10px]" value={form.document_id} onChange={e => set('document_id', e.target.value)}>
                    <option value="">— Select document —</option>
                    {documents.map(d => <option key={d.id} value={d.id}>{d.title || d.url?.split('/').pop()}{d.version ? ` (${d.version})` : ''}</option>)}
                  </select>
              }
            </div>

            {/* ── THE MILESTONES, CONFIRMED AT THE SAME TIME ──────────────────────
                "Clarify the SOW version, dates, and the milestone details so that they can
                be updated if required."

                On a first issuance the forecast dates were set days ago and are fine. On a
                RE-ISSUE they were set for the last issuance, so they are usually in the past
                — and a milestone forecast in the past is what makes every dashboard in this
                app wrong at once: it reads as overdue, it asks its PM for a reason, and it
                pulls the expected income into a month that has already closed.

                So they are listed here, the stale ones are called out, and they can be
                changed without leaving the issue. */}
            {milestones.length > 0 && (
              <div>
                <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">
                  Milestone dates
                  {stalemilestones.length > 0 && (
                    <span className="ml-1 font-bold text-amber-600 normal-case">
                      — {stalemilestones.length} forecast in the past
                    </span>
                  )}
                </label>
                <div className="border border-gray-200 rounded divide-y divide-gray-100 max-h-40 overflow-y-auto">
                  {milestones.map(m => {
                    const val = msDates[m.id] ?? (m.forecast_date || '');
                    const stale = val && val < todayStr;
                    return (
                      <div key={m.id} className="flex items-center gap-2 px-2 py-1">
                        <span className="text-[9px] font-semibold text-navy w-10 flex-shrink-0">{m.milestone_code || '—'}</span>
                        <span className="text-[9px] text-gray-600 flex-1 truncate" title={m.description}>{m.description}</span>
                        <span className="text-[9px] text-gray-500 w-16 text-right flex-shrink-0">
                          {m.invoice_amount_excl !== undefined && formatMoney(m.invoice_amount_excl || 0)}
                        </span>
                        <input type="date" value={val}
                          onChange={e => setMsDates(d => ({ ...d, [m.id]: e.target.value }))}
                          className={`form-input text-[9px] w-28 flex-shrink-0 ${stale ? 'border-amber-400 bg-amber-50' : ''}`} />
                      </div>
                    );
                  })}
                </div>
                <div className="text-[8px] text-gray-400 mt-1">
                  Anything you change here is saved when the SOW is issued. Amounts and everything
                  else stay on the Milestones tab.
                </div>
              </div>
            )}

            {/* Notes */}
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Notes (optional)</label>
              <textarea rows={2} className="form-input text-[10px] resize-none w-full" value={form.notes} onChange={e => set('notes', e.target.value)} placeholder="Any notes to record against this issuance…" />
            </div>

            {/* Admin override */}
            {canOverride && (
              <div className="border border-red-200 rounded p-2.5 bg-red-50">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={form.override} onChange={e => set('override', e.target.checked)} />
                  <span className="text-[9px] font-bold text-red-700">Admin override — bypass QA, approval, and document requirements</span>
                </label>
                {form.override && (
                  <div className="mt-2">
                    <label className="block text-[8px] font-bold text-red-600 uppercase mb-1">Override reason *</label>
                    <textarea rows={2} className="form-input text-[10px] resize-none w-full border-red-300" value={form.override_reason} onChange={e => set('override_reason', e.target.value)} placeholder="Reason for bypassing controls…" />
                  </div>
                )}
              </div>
            )}

            {error && <div className="text-[9px] text-red-600">{error}</div>}
          </>}
        </div>

        {/* ── Right: version history ── */}
        <div className="w-52 flex-shrink-0">
          <div className="text-[8px] font-bold text-gray-500 uppercase mb-2">Issue History</div>
          {history === null && <div className="text-[9px] text-gray-400">Loading…</div>}
          {history !== null && history.length === 0 && (
            <div className="text-[9px] text-gray-400 italic">No previous versions — this will be the first issuance.</div>
          )}
          {history !== null && history.length > 0 && (
            <div className="space-y-2">
              {history.map((v, i) => (
                <div key={v.id} className={`border rounded p-2 ${v.superseded_at ? 'border-amber-200 bg-amber-50 opacity-70' : i === 0 ? 'border-blue-200 bg-blue-50' : 'border-gray-200 bg-gray-50 opacity-70'}`}>
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-[9px] font-bold text-navy">v{v.version}</span>
                    {v.superseded_at
                      ? <span className="text-[7px] font-bold px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Replaced</span>
                      : i === 0
                        ? <span className="text-[7px] font-bold px-1.5 py-0.5 rounded bg-blue-100 text-blue-700">Current</span>
                        : <span className="text-[7px] font-bold px-1.5 py-0.5 rounded bg-gray-200 text-gray-500">Superseded</span>
                    }
                    {v.is_override ? <span className="text-[7px] font-bold px-1 py-0.5 rounded bg-amber-100 text-amber-700">Override</span> : null}
                  </div>
                  {v.issued_at && <div className="text-[8px] text-gray-500">{formatDate(v.issued_at)}</div>}
                  {v.issued_by_name && <div className="text-[8px] text-gray-500">by {v.issued_by_name}</div>}
                  {v.document_title && (
                    <div className="text-[8px] text-gray-400 truncate mt-0.5" title={v.document_title}>
                      {v.document_url ? <DocumentLink kind="sow" id={v.document_id} url={v.document_url} title={v.document_title} className="text-blue-500 hover:underline">{v.document_title}</DocumentLink> : v.document_title}
                    </div>
                  )}
                  {v.notes && <div className="text-[7px] text-gray-400 italic mt-0.5 truncate" title={v.notes}>{v.notes}</div>}
                  {v.superseded_at && (
                    <div className="text-[7px] text-amber-700 mt-0.5" title={v.supersede_reason || ''}>
                      Replaced {formatDate(v.superseded_at)}{v.supersede_reason ? <span className="italic"> — {v.supersede_reason}</span> : null}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </Modal>
  );
}

// Shown when an Issued SOW has a PO confirmed — captures mandatory commercial terms
// and activates the SOW.
function POReceivedModal({ sow, milestones, onClose, onSave }) {
  const [step, setStep] = useState(1);
  // A child SOW almost always sits under the parent's PO, so pre-fill it from
  // the parent rather than making the PM re-type it. Left editable for the
  // edge case where a call-off carries a PO of its own.
  const parentPoNumber = (sow.parent_sow?.po_number || '').trim();
  const [form, setForm] = useState({
    po_number:        sow.po_number || parentPoNumber || '',
    payment_terms:    sow.payment_terms || '',
    po_received_date: '',
    ses_applicable:   false,
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const [milDates, setMilDates] = useState({});

  const addWeeks = (dateStr, weeks) => {
    if (!dateStr || !weeks) return '';
    const d = new Date(dateStr + 'T12:00:00');
    d.setDate(d.getDate() + Math.round(Number(weeks) * 7));
    return d.toISOString().split('T')[0];
  };

  const step1Valid = form.po_number.trim() && form.po_received_date && form.payment_terms !== '';

  const goToStep2 = () => {
    const init = {};
    for (const m of milestones) {
      const calc = m.weeks_after_po ? addWeeks(form.po_received_date, m.weeks_after_po) : '';
      init[m.id] = {
        contract_date:    m.contract_date  || calc,
        forecast_date:    m.forecast_date  || calc,
        po_calculated_date: calc || null,
      };
    }
    setMilDates(init);
    setStep(2);
  };

  const setMD = (id, field, val) => setMilDates(p => ({ ...p, [id]: { ...p[id], [field]: val } }));
  const applyCalc = (id) => {
    const calc = milDates[id]?.po_calculated_date;
    if (calc) setMilDates(p => ({ ...p, [id]: { ...p[id], contract_date: calc, forecast_date: calc } }));
  };

  const step2Valid = milestones.length === 0 || milestones.every(m => milDates[m.id]?.contract_date && milDates[m.id]?.forecast_date);

  const handleActivate = () => {
    const milestoneDates = milestones.map(m => ({
      id: m.id,
      contract_date:     milDates[m.id]?.contract_date     || null,
      forecast_date:     milDates[m.id]?.forecast_date     || null,
      po_calculated_date: milDates[m.id]?.po_calculated_date || null,
    }));
    onSave({ ...form, milestoneDates });
  };

  const hasWeeksSet = milestones.some(m => m.weeks_after_po);

  return (
    <Modal title={`PO Received — Activate SOW${step === 2 ? ': Milestone Dates' : ''}`} onClose={onClose} wide footer={
      step === 1 ? (
        <><button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
          {milestones.length > 0
            ? <button onClick={goToStep2} disabled={!step1Valid} className="px-3 py-1 text-[10px] font-bold bg-navy text-white rounded disabled:opacity-40">Next: Milestone Dates →</button>
            : <button onClick={() => { if (step1Valid) onSave({ ...form, milestoneDates: [] }); }} disabled={!step1Valid} className="px-3 py-1 text-[10px] font-bold bg-navy text-white rounded disabled:opacity-40">Activate SOW →</button>
          }
        </>
      ) : (
        <><button onClick={() => setStep(1)} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">← Back</button>
          <button onClick={handleActivate} disabled={!step2Valid} className="px-3 py-1 text-[10px] font-bold bg-navy text-white rounded disabled:opacity-40">Activate SOW →</button>
        </>
      )
    }>
      {step === 1 && (<>
        <div className="mb-3 px-3 py-2 bg-blue-50 border border-blue-200 rounded text-[9px] text-blue-700">
          Completing this form will move <strong>{sow.reference}</strong> from <strong>Issued → Active</strong>.
          {milestones.length > 0 && ' Step 2 will let you set or confirm milestone dates.'}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">PO Number *</label>
            <input className="form-input" value={form.po_number} onChange={e => set('po_number', e.target.value)} placeholder="e.g. PO-2026-00123" />
            {parentPoNumber && !sow.po_number && (
              form.po_number.trim() === parentPoNumber
                ? <div className="text-[7px] text-blue-600 mt-0.5">Pre-filled from parent <span className="font-mono font-semibold">{sow.parent_sow.reference}</span> — edit it if this SOW has its own PO.</div>
                : <div className="text-[7px] text-gray-400 mt-0.5">Parent <span className="font-mono font-semibold">{sow.parent_sow.reference}</span> holds <span className="font-mono font-semibold">{parentPoNumber}</span>.</div>
            )}
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">PO Received Date *</label>
            <input className="form-input" type="date" value={form.po_received_date} onChange={e => set('po_received_date', e.target.value)} />
          </div>
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Payment Terms (days) *</label>
            <input className="form-input" type="number" min="0" value={form.payment_terms} onChange={e => set('payment_terms', e.target.value)} placeholder="e.g. 30" />
            <div className="text-[7px] text-gray-400 mt-0.5">Days from invoice date until payment is due</div>
          </div>
          <div className="col-span-2">
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">SES Numbers Applicable to Invoices? *</label>
            <div className="flex gap-3">
              {[true, false].map(val => (
                <label key={String(val)} className={`flex items-center gap-2 px-3 py-2 rounded border cursor-pointer text-[9px] font-semibold transition-all ${form.ses_applicable === val ? val ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'border-gray-400 bg-gray-50 text-gray-600' : 'border-gray-200 text-gray-400 hover:border-gray-300'}`}>
                  <input type="radio" name="ses_applicable" className="sr-only" checked={form.ses_applicable === val} onChange={() => set('ses_applicable', val)} />
                  <span className={`w-3 h-3 rounded-full border-2 flex-shrink-0 ${form.ses_applicable === val ? val ? 'border-emerald-500 bg-emerald-500' : 'border-gray-500 bg-gray-500' : 'border-gray-300'}`} />
                  {val ? 'Yes — SES numbers required on invoices' : 'No — SES numbers not required'}
                </label>
              ))}
            </div>
            {form.ses_applicable && (
              <div className="mt-2 text-[8px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1.5 rounded">
                ⚠ With SES numbers enabled, each milestone must have a SES number entered before it can be moved to Invoiced status.
              </div>
            )}
          </div>
        </div>
      </>)}

      {step === 2 && (<>
        <div className="mb-3 px-3 py-2 bg-amber-50 border border-amber-300 rounded text-[9px] text-amber-800">
          Contract and forecast dates are <strong>required</strong> for all milestones before the SOW can go Active.
          {hasWeeksSet && <> Dates have been pre-filled from each milestone's <em>weeks after PO</em> offset where set.</>}
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-[10px]">
            <thead>
              <tr className="text-[8px] font-bold text-gray-400 uppercase border-b border-gray-200">
                <th className="pb-1.5 text-left px-1">Milestone</th>
                <th className="pb-1.5 text-center px-1 whitespace-nowrap">Weeks after PO</th>
                <th className="pb-1.5 text-center px-1 whitespace-nowrap">Calculated date</th>
                <th className="pb-1.5 text-left px-1">Contract date *</th>
                <th className="pb-1.5 text-left px-1">Forecast date *</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {milestones.map(m => {
                const d = milDates[m.id] || {};
                const calc = d.po_calculated_date;
                const missing = !d.contract_date || !d.forecast_date;
                return (
                  <tr key={m.id} className={missing ? 'bg-red-50' : ''}>
                    <td className="py-1.5 px-1">
                      <div className="font-bold text-navy font-mono">{m.milestone_code}</div>
                      <div className="text-[9px] text-gray-500 truncate max-w-[160px]">{m.description}</div>
                    </td>
                    <td className="py-1.5 px-1 text-center">
                      {m.weeks_after_po ? <span className="text-blue-600 font-semibold">{m.weeks_after_po}w</span> : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="py-1.5 px-1 text-center">
                      {calc
                        ? <div>
                            <div className="text-emerald-700 font-semibold">{formatDate(calc)}</div>
                            {(d.contract_date !== calc || d.forecast_date !== calc) && (
                              <button type="button" onClick={() => applyCalc(m.id)} className="text-[8px] text-blue-500 hover:underline mt-0.5">Use this</button>
                            )}
                          </div>
                        : <span className="text-gray-300">—</span>}
                    </td>
                    <td className="py-1.5 px-1">
                      <input type="date" value={d.contract_date || ''}
                        onChange={e => setMD(m.id, 'contract_date', e.target.value)}
                        className={`form-input text-[9px] py-0.5 px-1.5 w-32 ${!d.contract_date ? 'border-red-300' : ''}`} />
                    </td>
                    <td className="py-1.5 px-1">
                      <input type="date" value={d.forecast_date || ''}
                        onChange={e => setMD(m.id, 'forecast_date', e.target.value)}
                        className={`form-input text-[9px] py-0.5 px-1.5 w-32 ${!d.forecast_date ? 'border-red-300' : ''}`} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!step2Valid && <div className="mt-2 text-[9px] text-red-600 font-semibold">⚠ All milestones must have both a contract date and a forecast date before activating.</div>}
      </>)}
    </Modal>
  );
}

// ── AI DOC DIFF MODAL ─────────────────────────────────────────────────────────
function AiDocDiffModal({ doc1, doc2, onClose }) {
  const [loading, setLoading] = useState(false);
  const [diff, setDiff]       = useState(null); // { sections, overall }
  const [error, setError]     = useState('');

  const runDiff = async () => {
    setLoading(true); setError(''); setDiff(null);
    try {
      const result = await api.post(`sow-documents/${doc1.id}/ai-diff`, { compare_with: doc2.id });
      setDiff({ sections: result.sections || [], overall: result.overall || '' });
    } catch (e) {
      setError(e.message || 'AI comparison failed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { runDiff(); }, []);

  const STATUS = {
    modified:  { left: 'bg-orange-50 border-l-2 border-orange-300', right: 'bg-sky-50 border-l-2 border-sky-300',       badge: 'bg-orange-100 text-orange-700' },
    removed:   { left: 'bg-red-50 border-l-2 border-red-300',       right: 'bg-gray-50',                                badge: 'bg-red-100 text-red-700' },
    added:     { left: 'bg-gray-50',                                 right: 'bg-emerald-50 border-l-2 border-emerald-300', badge: 'bg-emerald-100 text-emerald-700' },
    unchanged: { left: 'bg-white',                                   right: 'bg-white',                                  badge: 'bg-gray-100 text-gray-500' },
  };

  return (
    <Modal title="AI Document Comparison" onClose={onClose} xl footer={
      <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Close</button>
    }>
      {/* Column headers */}
      <div className="grid grid-cols-2 gap-2 mb-3">
        {[doc1, doc2].map((doc, i) => (
          <div key={doc.id} className={`p-2 rounded-lg border ${i === 0 ? 'border-red-200 bg-red-50' : 'border-emerald-200 bg-emerald-50'}`}>
            <div className={`text-[7px] font-bold uppercase mb-0.5 ${i === 0 ? 'text-red-500' : 'text-emerald-600'}`}>
              {i === 0 ? '← Document A · original' : 'Document B · revised →'}
            </div>
            <div className="text-[9px] font-semibold">{doc.title}</div>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className="text-[7px] text-gray-400">{doc.document_type}</span>
              {doc.version && (
                <span className={`text-[7px] font-bold px-1 py-0.5 rounded ${i === 0 ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'}`}>
                  {doc.version}
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {loading && (
        <div className="flex items-center justify-center gap-2 py-10 text-[9px] text-gray-400">
          <div className="w-4 h-4 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
          Asking AI to compare documents…
        </div>
      )}

      {error && <div className="p-2 bg-red-50 border border-red-200 rounded text-[9px] text-red-700">{error}</div>}

      {diff && diff.sections.length > 0 && (
        <div>
          {/* Legend */}
          <div className="flex items-center gap-4 mb-2 px-0.5">
            <span className="text-[7px] font-bold uppercase text-gray-400">Key:</span>
            {[
              { cls: 'bg-red-100 border-red-300',     label: 'Removed (only in A)' },
              { cls: 'bg-emerald-100 border-emerald-300', label: 'Added (only in B)' },
              { cls: 'bg-orange-100 border-orange-300',   label: 'Changed' },
            ].map(({ cls, label }) => (
              <span key={label} className="flex items-center gap-1 text-[7px] text-gray-500">
                <span className={`w-2.5 h-2.5 rounded-sm border inline-block ${cls}`} />
                {label}
              </span>
            ))}
          </div>

          {/* Diff table */}
          <div className="rounded border border-gray-200 overflow-hidden divide-y divide-gray-200">
            {/* Sticky column headers */}
            <div className="grid grid-cols-2 divide-x divide-gray-200 bg-gray-100">
              <div className="px-3 py-1.5 text-[7.5px] font-bold uppercase text-red-500">Document A · extra</div>
              <div className="px-3 py-1.5 text-[7.5px] font-bold uppercase text-emerald-600">Document B · new / changed</div>
            </div>

            {diff.sections.map((section, idx) => {
              const meta = STATUS[section.status] || STATUS.unchanged;
              return (
                <div key={idx}>
                  {/* Section heading */}
                  <div className="flex items-center justify-between px-3 py-1 bg-gray-50 border-b border-gray-200">
                    <span className="text-[8px] font-bold text-gray-700">{section.title}</span>
                    <span className={`text-[6.5px] font-bold uppercase px-1.5 py-0.5 rounded ${meta.badge}`}>{section.status}</span>
                  </div>
                  {/* Content row */}
                  <div className="grid grid-cols-2 divide-x divide-gray-200">
                    <div className={`px-3 py-2 text-[8px] leading-relaxed ${meta.left}`}>
                      {section.left
                        ? <span className="text-gray-700 whitespace-pre-wrap">{section.left}</span>
                        : <span className="text-gray-400 italic">— not present —</span>}
                    </div>
                    <div className={`px-3 py-2 text-[8px] leading-relaxed ${meta.right}`}>
                      {section.right
                        ? <span className="text-gray-700 whitespace-pre-wrap">{section.right}</span>
                        : <span className="text-gray-400 italic">— not present —</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Overall summary */}
          {diff.overall && (
            <div className="mt-3 p-2.5 bg-purple-50 border border-purple-200 rounded">
              <div className="text-[7px] font-bold uppercase text-purple-600 mb-1">Overall Assessment</div>
              <div className="text-[8.5px] text-gray-700">{diff.overall}</div>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

// ── AI REDACT MODAL ───────────────────────────────────────────────────────────
function AiRedactModal({ doc, onClose, onDone }) {
  const [loading, setLoading] = useState(false);
  const [result, setResult]   = useState(null); // { id, title, version }
  const [error, setError]     = useState('');

  const runRedact = async () => {
    setLoading(true); setError('');
    try {
      const res = await api.post(`sow-documents/${doc.id}/ai-redact`, {});
      setResult(res);
    } catch (e) {
      setError(e.message || 'AI redaction failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal title="AI Redacted SOW" onClose={onClose} footer={
      result
        ? <button onClick={onDone} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded">Done</button>
        : <><button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
           {!loading && !result && <button onClick={runRedact} className="px-3 py-1 text-[10px] font-semibold bg-purple-600 text-white rounded">✦ Generate Redacted Version</button>}</>
    }>
      <div className="space-y-3">
        <div className="p-2 bg-navy/5 border border-navy/10 rounded">
          <div className="text-[7px] font-bold uppercase text-gray-400 mb-0.5">Source Document</div>
          <div className="text-[9px] font-semibold">{doc.title}</div>
          {doc.version && <div className="text-[8px] text-gray-400">Version: {doc.version}</div>}
        </div>
        <div className="text-[8px] text-gray-500">
          AI will remove all <strong>currency values, day rates, unit costs, subtotals and totals</strong> from the SOW and replace them with [REDACTED]. The redacted version will be saved as a new document version and can be shared with the client.
        </div>
        {loading && (
          <div className="flex items-center justify-center gap-2 py-6 text-[9px] text-gray-400">
            <div className="w-4 h-4 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin" />
            Generating redacted version…
          </div>
        )}
        {error && <div className="p-2 bg-red-50 border border-red-200 rounded text-[9px] text-red-700">{error}</div>}
        {result && (
          <div className="p-3 bg-green-50 border border-green-200 rounded">
            <div className="text-[8px] font-bold text-green-700 mb-1">Redacted version saved</div>
            <div className="text-[9px] font-semibold">{result.title}</div>
            <div className="text-[8px] text-gray-500">Version: {result.version}</div>
          </div>
        )}
        {!loading && !result && !error && (
          <div className="text-[8px] text-gray-400 italic">Click "Generate Redacted Version" to begin. This works best with plain text or Markdown documents uploaded to the system.</div>
        )}
      </div>
    </Modal>
  );
}

// ── ADD DOCUMENT MODAL ────────────────────────────────────────────────────────
function AddDocumentModal({ onClose, onSave }) {
  const [docTypes, setDocTypes]   = useState([]);
  const [uploadMode, setUploadMode] = useState(true); // true = upload file, false = enter URL
  const [form, setForm] = useState({ document_type: '', title: '', url: '', version: '', notes: '', _uploadedFilename: '' });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get('config-options', { category: 'document_type' }).then(setDocTypes).catch(() => setDocTypes([]));
  }, []);

  const valid = form.document_type && form.title.trim() && form.url.trim();

  return (
    <Modal title="Add Document" onClose={onClose} footer={
      <><button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
        <button onClick={() => { if (valid) onSave(form); }} disabled={!valid} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40">Add</button></>
    }>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Document Type *</label>
          <select className="form-select" value={form.document_type} onChange={e => set('document_type', e.target.value)}>
            <option value="">Select type...</option>
            {docTypes.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Version / Reference</label>
          <input className="form-input" placeholder="e.g. v1.2, Issue 3..." value={form.version} onChange={e => set('version', e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Title *</label>
          <input className="form-input" placeholder="e.g. SOW v2 Requirements, Initial Scope..." value={form.title} onChange={e => set('title', e.target.value)} autoFocus />
        </div>

        {/* Upload / URL toggle */}
        <div className="col-span-2">
          <div className="flex items-center gap-0 mb-2 border border-gray-200 rounded overflow-hidden w-fit">
            <button type="button" onClick={() => { setUploadMode(true); setForm(p => ({ ...p, url: '', _uploadedFilename: '' })); }}
              className={`px-3 py-1 text-[8px] font-semibold transition-colors ${uploadMode ? 'bg-navy text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
              ↑ Upload File
            </button>
            <button type="button" onClick={() => { setUploadMode(false); setForm(p => ({ ...p, url: '', _uploadedFilename: '' })); }}
              className={`px-3 py-1 text-[8px] font-semibold transition-colors ${!uploadMode ? 'bg-navy text-white' : 'bg-white text-gray-500 hover:bg-gray-50'}`}>
              🔗 Enter URL / Path
            </button>
          </div>

          {uploadMode ? (
            <FileUpload
              label="Drag a file here or click to browse"
              value={form._uploadedFilename || null}
              onUpload={({ url, filename }) => setForm(p => ({
                ...p,
                url,
                _uploadedFilename: filename,
                title: p.title || filename,
              }))}
              onClear={() => setForm(p => ({ ...p, url: '', _uploadedFilename: '' }))}
            />
          ) : (
            <div>
              <input className="form-input" type="text" placeholder="https://... or \\server\share\file.pdf" value={form.url} onChange={e => set('url', e.target.value)} />
              <div className="text-[8px] text-gray-400 mt-0.5">Paste a SharePoint / M365 URL, or a UNC / local file path</div>
            </div>
          )}
        </div>

        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Notes</label>
          <input className="form-input" placeholder="Optional notes..." value={form.notes} onChange={e => set('notes', e.target.value)} />
        </div>
      </div>
    </Modal>
  );
}

// ── CCN TAB ───────────────────────────────────────────────────────────────────
const CCN_TYPES = ['schedule','budget','scope'];
const CCN_TYPE_LABELS = { schedule: 'Schedule', budget: 'Budget', scope: 'Scope' };
const CCN_TYPE_COLORS = { schedule: 'bg-blue-100 text-blue-700', budget: 'bg-emerald-100 text-emerald-700', scope: 'bg-purple-100 text-purple-700' };
const CCN_IMPACT_LABELS = { cancel: 'Cancel Milestone', amend: 'Amend Milestone', new: 'New Milestone' };
const CCN_IMPACT_COLORS = { cancel: 'bg-red-100 text-red-700', amend: 'bg-amber-100 text-amber-700', new: 'bg-emerald-100 text-emerald-700' };

const EMPTY_CCN_FORM = (sowTotal) => ({
  title: '', description: '',
  change_types: ['schedule'],
  budget_before: sowTotal ?? '',
  budget_after: '',
  impacts: [],
});

function CcnFormModal({ sow, milestones, initial, onClose, onSave }) {
  const seesMoney = canAccess('sow:see-financials');
  const [form, setForm] = useState(initial || EMPTY_CCN_FORM(sow?.total_excl_vat));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const toggleType = (t) => {
    setForm(p => {
      const types = p.change_types.includes(t) ? p.change_types.filter(x => x !== t) : [...p.change_types, t];
      return { ...p, change_types: types.length ? types : [t] }; // at least one
    });
  };

  const addImpact = () => setForm(p => ({ ...p, impacts: [...p.impacts, { _key: Math.random(), impact_type: 'amend', milestone_id: '', milestone_code: '', description: '', old_value: '', new_value: '', old_contract_date: '', new_contract_date: '' }] }));
  const removeImpact = (idx) => setForm(p => ({ ...p, impacts: p.impacts.filter((_, i) => i !== idx) }));
  const updateImpact = (idx, patch) => setForm(p => { const imp = [...p.impacts]; imp[idx] = { ...imp[idx], ...patch }; return { ...p, impacts: imp }; });

  const onMilestoneSelect = (idx, milId) => {
    const mil = milestones.find(m => m.id === milId);
    updateImpact(idx, {
      milestone_id: milId,
      milestone_code: mil?.milestone_code || '',
      old_value: mil ? (mil.invoice_amount_excl ?? '') : '',
      old_contract_date: mil?.contract_date || '',
    });
  };

  const handleSave = async () => {
    if (!form.title.trim()) { setError('Title is required'); return; }
    if (!form.change_types.length) { setError('Select at least one change type'); return; }
    setSaving(true); setError('');
    try {
      // Somebody not shown SOW values sends none, rather than blanks that
      // would clear the ones already recorded.
      if (!seesMoney) {
        const { budget_before, budget_after, ...rest } = form; // eslint-disable-line no-unused-vars
        await onSave({
          ...rest,
          change_types: form.change_types.join(','),
          impacts: form.impacts.map(({ old_value, new_value, ...imp }) => imp), // eslint-disable-line no-unused-vars
        });
        return;
      }
      await onSave({
        ...form,
        change_types: form.change_types.join(','),
        budget_before: form.budget_before !== '' && form.budget_before != null ? parseFloat(form.budget_before) : null,
        budget_after: form.budget_after !== '' && form.budget_after != null ? parseFloat(form.budget_after) : null,
        impacts: form.impacts.map(imp => ({
          ...imp,
          old_value: imp.old_value !== '' ? parseFloat(imp.old_value) : null,
          new_value: imp.new_value !== '' ? parseFloat(imp.new_value) : null,
        })),
      });
    } catch (e) { setError(e?.data?.error || e?.message || 'Save failed'); setSaving(false); }
  };

  const hasBudget = form.change_types.includes('budget');

  return (
    <Modal title={initial ? `Edit ${initial.ccn_number}` : 'New Contract Change Note'} onClose={onClose} width="max-w-3xl">
      <div className="space-y-4">
        {/* Title + description */}
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="form-label">Title *</label>
            <input className="form-input" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="Brief description of the change" />
          </div>
          <div className="col-span-2">
            <label className="form-label">Detailed Description</label>
            <textarea className="form-textarea" rows="2" value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Background / context for this change…" />
          </div>
        </div>

        {/* Change types */}
        <div>
          <label className="form-label">Change Type(s) *</label>
          <div className="flex gap-2">
            {CCN_TYPES.filter(t => seesMoney || t !== 'budget').map(t => (
              <button key={t} type="button" onClick={() => toggleType(t)}
                className={`px-3 py-1.5 text-[9px] font-bold rounded border transition-all ${form.change_types.includes(t) ? CCN_TYPE_COLORS[t] + ' border-current' : 'bg-white border-gray-200 text-gray-500 hover:border-gray-400'}`}>
                {CCN_TYPE_LABELS[t]}
              </button>
            ))}
          </div>
        </div>

        {/* Budget Before / After — only shown when Budget type selected */}
        {hasBudget && seesMoney && (
          <div className="border border-gray-100 rounded-lg p-3 bg-gray-50">
            <div className="text-[8px] font-bold text-gray-500 uppercase tracking-wider mb-3">Budget Change</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="form-label text-amber-700">SOW Value — Before (£) <span className="text-gray-400 font-normal normal-case">(from SOW total)</span></label>
                <input type="number" readOnly className="form-input bg-gray-100 text-gray-600 cursor-not-allowed" value={form.budget_before ?? ''} />
              </div>
              <div>
                <label className="form-label text-emerald-700">SOW Value — After (£)</label>
                <input type="number" className="form-input" value={form.budget_after} onChange={e => setForm(p => ({ ...p, budget_after: e.target.value }))} placeholder="0.00" />
              </div>
            </div>
          </div>
        )}

        {/* Milestone impacts */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="form-label mb-0">Milestone Impacts</label>
            <button type="button" onClick={addImpact} className="px-2 py-1 text-[8px] font-bold bg-navy text-white rounded hover:bg-navy/80">+ Add Impact</button>
          </div>
          {form.impacts.length === 0 && <div className="text-center py-4 text-[9px] text-gray-400 border border-dashed border-gray-200 rounded">No milestone impacts — add one if this CCN changes existing milestones or adds new ones</div>}
          <div className="space-y-2">
            {form.impacts.map((imp, idx) => (
              <div key={imp._key || idx} className="border border-gray-200 rounded-lg p-3 bg-white">
                <div className="flex items-start gap-2">
                  <div className="flex-1 grid grid-cols-2 gap-2">
                    {/* Impact type */}
                    <div>
                      <label className="form-label">Type</label>
                      <select className="form-select" value={imp.impact_type} onChange={e => updateImpact(idx, { impact_type: e.target.value })}>
                        <option value="amend">Amend Milestone</option>
                        <option value="cancel">Cancel Milestone</option>
                        <option value="new">New Milestone</option>
                      </select>
                    </div>
                    {/* Milestone picker (not for new) */}
                    {imp.impact_type !== 'new' ? (
                      <div>
                        <label className="form-label">Milestone</label>
                        <select className="form-select" value={imp.milestone_id || ''} onChange={e => onMilestoneSelect(idx, e.target.value)}>
                          <option value="">— select milestone —</option>
                          {milestones.map(m => <option key={m.id} value={m.id}>{m.milestone_code} — {m.description}</option>)}
                        </select>
                      </div>
                    ) : (
                      <div>
                        <label className="form-label">New Milestone Code</label>
                        <input className="form-input" value={imp.milestone_code || ''} onChange={e => updateImpact(idx, { milestone_code: e.target.value })} placeholder="e.g. M7" />
                      </div>
                    )}

                    {/* Description */}
                    <div className="col-span-2">
                      <label className="form-label">Notes / Change Description</label>
                      <input className="form-input" value={imp.description || ''} onChange={e => updateImpact(idx, { description: e.target.value })} placeholder="Describe what is changing…" />
                    </div>

                    {/* Value fields for amend/new */}
                    {imp.impact_type !== 'cancel' && (<>
                      {seesMoney && <>
                      <div>
                        <label className="form-label text-amber-700">Value Before (£)</label>
                        <input type="number" className="form-input" value={imp.old_value ?? ''} onChange={e => updateImpact(idx, { old_value: e.target.value })} placeholder="0.00" />
                      </div>
                      <div>
                        <label className="form-label text-emerald-700">Value After (£)</label>
                        <input type="number" className="form-input" value={imp.new_value ?? ''} onChange={e => updateImpact(idx, { new_value: e.target.value })} placeholder="0.00" />
                      </div>
                      </>}
                      <div>
                        <label className="form-label text-amber-700">Contract Date Before</label>
                        <input type="date" className="form-input" value={imp.old_contract_date || ''} onChange={e => updateImpact(idx, { old_contract_date: e.target.value })} />
                      </div>
                      <div>
                        <label className="form-label text-emerald-700">Contract Date After</label>
                        <input type="date" className="form-input" value={imp.new_contract_date || ''} onChange={e => updateImpact(idx, { new_contract_date: e.target.value })} />
                      </div>
                    </>)}
                  </div>
                  <button type="button" onClick={() => removeImpact(idx)} className="text-[9px] text-gray-400 hover:text-red-500 p-1 mt-4 flex-shrink-0">✕</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {error && <div className="text-[9px] text-red-600 font-semibold">{error}</div>}

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-[9px] border border-gray-200 rounded hover:bg-gray-50">Cancel</button>
          <button type="button" onClick={handleSave} disabled={saving} className="px-4 py-1.5 text-[9px] font-bold bg-navy text-white rounded hover:bg-navy/80 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save CCN'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

function CcnCard({ ccn, milestones, currentUser, onAction, onEdit, onRecordPo, onApplyImpacts }) {
  const { toast } = useToast();
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [showPoForm, setShowPoForm] = useState(false);
  const [poForm, setPoForm] = useState({ po_number: ccn.po_number || '', po_amount: ccn.po_amount || '' });
  const [acting, setActing] = useState(false);

  const userRoles = parseRoles(currentUser?.user_role);
  const isAdmin = userRoles.includes('admin');
  const isPmo = userRoles.includes('pmo') || isAdmin;
  const types = (ccn.change_types || '').split(',').filter(Boolean);
  const hasImpacts = (ccn.impacts || []).length > 0;

  const act = async (action, extra = {}) => {
    setActing(true);
    try { await onAction(ccn.id, action, extra); }
    catch (e) { toast.error(e?.data?.error || e?.message || 'Action failed'); }
    finally { setActing(false); }
  };

  const savePo = async () => {
    setActing(true);
    try { await onRecordPo(ccn.id, canAccess('sow:see-financials') ? poForm : { po_number: poForm.po_number }); setShowPoForm(false); }
    catch (e) { toast.error(e?.data?.error || e?.message || 'Failed to record PO'); }
    finally { setActing(false); }
  };

  return (
    <div className={`bg-white border rounded-lg overflow-hidden ${ccn.status === 'approved' ? 'border-emerald-200' : ccn.status === 'rejected' ? 'border-red-200' : ccn.status === 'pending_approval' ? 'border-amber-200' : 'border-gray-200'}`}>
      {/* Header */}
      <div className="px-4 py-3 flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-[10px] font-extrabold text-navy">{ccn.ccn_number}</span>
            <span className={`text-[7px] font-bold px-1.5 py-0.5 rounded ${CCN_STATUS_COLORS[ccn.status] || 'bg-gray-100 text-gray-600'}`}>{CCN_STATUS_LABELS[ccn.status] || ccn.status}</span>
            {types.map(t => (
              <span key={t} className={`text-[7px] font-bold px-1.5 py-0.5 rounded ${CCN_TYPE_COLORS[t] || 'bg-gray-100 text-gray-600'}`}>{CCN_TYPE_LABELS[t] || t}</span>
            ))}
          </div>
          <div className="text-[10px] font-semibold text-gray-800">{ccn.title}</div>
          {ccn.description && <div className="text-[8px] text-gray-500 mt-0.5">{ccn.description}</div>}
          <div className="text-[7px] text-gray-400 mt-1">Created by {ccn.created_by_name} · {fmtDateOnly(ccn.created_at)}</div>
        </div>

        {/* Actions */}
        <div className="flex flex-col items-end gap-1 flex-shrink-0">
          {ccn.status === 'draft' && isPmo && (
            <>
              <button onClick={() => onEdit(ccn)} className="px-2 py-1 text-[8px] font-semibold border border-gray-200 rounded hover:bg-gray-50">✏ Edit</button>
              <button disabled={acting} onClick={() => act('issue')} className="px-2 py-1 text-[8px] font-bold bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50">Issue to Client →</button>
            </>
          )}
          {ccn.status === 'pending_approval' && isPmo && (<>
            <button disabled={acting} onClick={() => act('approve')} className="px-2 py-1 text-[8px] font-bold bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50">✓ Approve</button>
            <button disabled={acting} onClick={() => setShowRejectForm(p => !p)} className="px-2 py-1 text-[8px] font-semibold border border-red-300 text-red-600 rounded hover:bg-red-50">✕ Reject</button>
          </>)}
          {ccn.status === 'approved' && !!ccn.po_required && !ccn.po_number && isPmo && (
            <button onClick={() => setShowPoForm(p => !p)} className="px-2 py-1 text-[8px] font-bold bg-navy text-white rounded hover:bg-navy/80">Record Updated PO</button>
          )}
          {/* Apply milestone impacts — shown when approved, has impacts, not yet applied */}
          {ccn.status === 'approved' && (ccn.impacts || []).length > 0 && !ccn.impacts_applied && isPmo && (
            <button onClick={() => onApplyImpacts(ccn)} className="px-2 py-1 text-[8px] font-bold bg-purple-600 text-white rounded hover:bg-purple-700">
              Apply Milestone Changes →
            </button>
          )}
          {ccn.status === 'approved' && (ccn.impacts || []).length > 0 && !!ccn.impacts_applied && (
            <span className="text-[7px] font-bold text-emerald-600 bg-emerald-50 px-1.5 py-0.5 rounded">✓ Milestone changes applied</span>
          )}
        </div>
      </div>

      {/* Reject form */}
      {showRejectForm && (
        <div className="px-4 pb-3 border-t border-gray-100 pt-2">
          <label className="form-label">Rejection Reason</label>
          <div className="flex gap-2">
            <input className="form-input flex-1" value={rejectReason} onChange={e => setRejectReason(e.target.value)} placeholder="Reason for rejection…" />
            <button disabled={acting} onClick={() => { act('reject', { rejection_reason: rejectReason }); setShowRejectForm(false); }} className="px-3 py-1 text-[8px] font-bold bg-red-600 text-white rounded disabled:opacity-50">Confirm Reject</button>
            <button onClick={() => setShowRejectForm(false)} className="px-2 py-1 text-[8px] border border-gray-200 rounded">Cancel</button>
          </div>
        </div>
      )}

      {/* PO form */}
      {showPoForm && (
        <div className="px-4 pb-3 border-t border-gray-100 pt-2">
          <div className="text-[8px] font-bold text-gray-700 mb-2">Record Updated Purchase Order</div>
          <div className="grid grid-cols-2 gap-2 mb-2">
            <div><label className="form-label">PO Number</label><input className="form-input" value={poForm.po_number} onChange={e => setPoForm(p => ({ ...p, po_number: e.target.value }))} /></div>
            {canAccess('sow:see-financials') && <div><label className="form-label">PO Amount (£)</label><input type="number" className="form-input" value={poForm.po_amount} onChange={e => setPoForm(p => ({ ...p, po_amount: e.target.value }))} /></div>}
          </div>
          <div className="flex gap-2">
            <button disabled={acting} onClick={savePo} className="px-3 py-1 text-[8px] font-bold bg-navy text-white rounded disabled:opacity-50">Save PO</button>
            <button onClick={() => setShowPoForm(false)} className="px-2 py-1 text-[8px] border border-gray-200 rounded">Cancel</button>
          </div>
        </div>
      )}

      {/* PO received badge */}
      {ccn.status === 'approved' && ccn.po_required ? (
        <div className={`px-4 py-2 border-t flex items-center gap-2 ${ccn.po_number ? 'bg-emerald-50 border-emerald-100' : 'bg-amber-50 border-amber-100'}`}>
          <span className={`text-[7px] font-bold ${ccn.po_number ? 'text-emerald-700' : 'text-amber-700'}`}>
            {ccn.po_number ? `✓ Updated PO received: ${ccn.po_number}${ccn.po_amount ? ` · £${Number(ccn.po_amount).toLocaleString()}` : ''}` : '⏳ Awaiting updated Purchase Order from client'}
          </span>
        </div>
      ) : null}

      {/* Approval / rejection trail */}
      {(ccn.status === 'approved' || ccn.status === 'rejected') && (
        <div className={`px-4 py-2 border-t text-[7px] ${ccn.status === 'approved' ? 'border-emerald-100 text-emerald-700' : 'border-red-100 text-red-600'}`}>
          {ccn.status === 'approved'
            ? `✓ Approved by ${ccn.approved_by_name || '—'} · ${ccn.approved_at ? new Date(ccn.approved_at).toLocaleDateString('en-GB') : ''}`
            : `✕ Rejected by ${ccn.rejected_by_name || '—'} · ${ccn.rejected_at ? new Date(ccn.rejected_at).toLocaleDateString('en-GB') : ''}${ccn.rejection_reason ? ` — ${ccn.rejection_reason}` : ''}`
          }
        </div>
      )}

      {/* Budget Before / After */}
      {(ccn.budget_before != null || ccn.budget_after != null) && (
        <div className="border-t border-gray-100 px-4 py-3">
          <div className="text-[8px] font-bold text-gray-500 uppercase tracking-wider mb-2">Budget Change</div>
          <div className="grid grid-cols-2 gap-3">
            <div className="bg-amber-50 rounded p-2">
              <div className="text-[7px] font-bold text-amber-600 uppercase mb-1">SOW Value Before</div>
              <div className="text-[12px] font-bold text-gray-800">{ccn.budget_before != null ? `£${Number(ccn.budget_before).toLocaleString()}` : '—'}</div>
            </div>
            <div className="bg-emerald-50 rounded p-2">
              <div className="text-[7px] font-bold text-emerald-600 uppercase mb-1">SOW Value After</div>
              <div className="text-[12px] font-bold text-emerald-700">{ccn.budget_after != null ? `£${Number(ccn.budget_after).toLocaleString()}` : '—'}</div>
              {ccn.budget_before != null && ccn.budget_after != null && (
                <div className={`text-[8px] font-semibold mt-0.5 ${Number(ccn.budget_after) > Number(ccn.budget_before) ? 'text-red-500' : 'text-emerald-600'}`}>
                  {Number(ccn.budget_after) > Number(ccn.budget_before) ? '+' : ''}{`£${(Number(ccn.budget_after) - Number(ccn.budget_before)).toLocaleString()}`}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Milestone impacts */}
      {hasImpacts && (
        <div className="border-t border-gray-100 px-4 py-3">
          <div className="text-[8px] font-bold text-gray-500 uppercase tracking-wider mb-2">Milestone Impacts</div>
          <div className="space-y-2">
            {(ccn.impacts || []).map((imp, idx) => (
              <div key={idx} className={`flex items-start gap-3 rounded-lg p-2.5 ${imp.impact_type === 'cancel' ? 'bg-red-50' : imp.impact_type === 'new' ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                <span className={`text-[7px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 ${CCN_IMPACT_COLORS[imp.impact_type]}`}>{CCN_IMPACT_LABELS[imp.impact_type]}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-[9px] font-semibold text-gray-800">
                    {imp.milestone_code || imp.existing_code || '—'}
                    {(imp.existing_desc || imp.description) && <span className="text-gray-500 font-normal"> · {imp.existing_desc || imp.description}</span>}
                  </div>
                  {imp.impact_type !== 'cancel' && (imp.old_value != null || imp.new_value != null) && (
                    <div className="flex items-center gap-3 text-[8px] mt-0.5">
                      {imp.old_value != null && <span className="text-amber-700">£{Number(imp.old_value).toLocaleString()}</span>}
                      {imp.old_value != null && imp.new_value != null && <span className="text-gray-400">→</span>}
                      {imp.new_value != null && <span className="text-emerald-700 font-semibold">£{Number(imp.new_value).toLocaleString()}</span>}
                      {imp.old_contract_date && <><span className="text-gray-300">|</span><span className="text-amber-700">{new Date(imp.old_contract_date).toLocaleDateString('en-GB')}</span></>}
                      {imp.old_contract_date && imp.new_contract_date && <span className="text-gray-400">→</span>}
                      {imp.new_contract_date && <span className="text-emerald-700">{new Date(imp.new_contract_date).toLocaleDateString('en-GB')}</span>}
                    </div>
                  )}
                  {imp.description && imp.impact_type !== 'new' && <div className="text-[7px] text-gray-500 mt-0.5">{imp.description}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function CcnTab({ sowId, sow, milestones, ccns, loading, currentUser, onReload, showAdd, setShowAdd, editCcn, setEditCcn }) {
  const { toast } = useToast();
  const userRoles = parseRoles(currentUser?.user_role);
  const isPmo = userRoles.includes('pmo') || userRoles.includes('admin');
  const [confirmApply, setConfirmApply] = useState(null); // CCN object pending apply confirmation
  const [applying, setApplying] = useState(false);

  const handleAction = async (ccnId, action, extra = {}) => {
    await api.post(`sows/${sowId}/ccns/${ccnId}/action`, { action, ...extra });
    onReload();
  };

  const handleRecordPo = async (ccnId, poForm) => {
    await api.patch(`sows/${sowId}/ccns/${ccnId}/po`, poForm);
    onReload();
  };

  const handleSave = async (formData) => {
    if (editCcn && editCcn.id) {
      await api.put(`sows/${sowId}/ccns/${editCcn.id}`, formData);
      setEditCcn(null);
    } else {
      await api.post(`sows/${sowId}/ccns`, formData);
      setShowAdd(false);
    }
    onReload();
  };

  const handleApplyImpacts = async () => {
    if (!confirmApply) return;
    setApplying(true);
    try {
      const res = await api.post(`sows/${sowId}/ccns/${confirmApply.id}/apply-impacts`, {});
      if (res?.sow_reopened) toast.success('SOW is back in delivery: the CCN has added a milestone that still has to be invoiced.');
      setConfirmApply(null);
      onReload();
    } catch (e) {
      toast.error(e?.data?.error || e?.message || 'Failed to apply impacts');
    } finally {
      setApplying(false);
    }
  };

  // Summary counts
  const pending = ccns.filter(c => c.status === 'pending_approval').length;
  const approved = ccns.filter(c => c.status === 'approved').length;
  const draft = ccns.filter(c => c.status === 'draft').length;

  return (
    <div>
      {/* Summary strip */}
      <div className="flex items-center gap-3 mb-4">
        <div className="flex-1">
          <div className="flex gap-2">
            {draft > 0 && <span className="text-[8px] font-bold px-2 py-1 rounded bg-gray-100 text-gray-600">{draft} Draft</span>}
            {pending > 0 && <span className="text-[8px] font-bold px-2 py-1 rounded bg-amber-100 text-amber-700">{pending} Pending Approval</span>}
            {approved > 0 && <span className="text-[8px] font-bold px-2 py-1 rounded bg-emerald-100 text-emerald-700">{approved} Approved</span>}
            {ccns.length === 0 && <span className="text-[8px] text-gray-400">No contract change notes yet</span>}
          </div>
        </div>
        {isPmo && (
          <button onClick={() => setShowAdd(true)} className="px-3 py-1.5 text-[9px] font-bold bg-navy text-white rounded hover:bg-navy/80">+ New CCN</button>
        )}
      </div>

      {loading && <div className="text-center py-10 text-[10px] text-gray-400">Loading…</div>}

      {!loading && ccns.length === 0 && (
        <div className="text-center py-16 text-[10px] text-gray-400">
          <div className="text-4xl mb-3">📋</div>
          <div className="font-semibold text-gray-500">No Contract Change Notes</div>
          <div className="text-[9px] mt-1">CCNs track agreed changes to schedule, budget, or scope on this SOW.</div>
          {isPmo && <button onClick={() => setShowAdd(true)} className="mt-3 px-4 py-1.5 text-[9px] font-bold bg-navy text-white rounded hover:bg-navy/80">+ Create first CCN</button>}
        </div>
      )}

      {!loading && ccns.length > 0 && (
        <div className="space-y-4">
          {ccns.map(ccn => (
            <CcnCard
              key={ccn.id}
              ccn={ccn}
              milestones={milestones}
              currentUser={currentUser}
              onAction={handleAction}
              onEdit={(c) => setEditCcn(c)}
              onRecordPo={handleRecordPo}
              onApplyImpacts={(c) => setConfirmApply(c)}
            />
          ))}
        </div>
      )}

      {/* Add / Edit modal */}
      {(showAdd || editCcn) && (
        <CcnFormModal
          sow={sow}
          milestones={milestones}
          initial={editCcn ? {
            ...editCcn,
            change_types: (editCcn.change_types || 'schedule').split(','),
            impacts: (editCcn.impacts || []).map(i => ({ ...i, _key: Math.random() })),
          } : null}
          onClose={() => { setShowAdd(false); setEditCcn(null); }}
          onSave={handleSave}
        />
      )}

      {/* Apply impacts confirmation modal */}
      {confirmApply && (
        <Modal title="Apply Milestone Changes" onClose={() => setConfirmApply(null)}>
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="text-[9px] font-bold text-amber-800 mb-1">⚠ This will make permanent changes to milestones under this SOW.</div>
              <div className="text-[8px] text-amber-700">Once applied, milestone changes cannot be automatically undone.</div>
            </div>

            <div>
              <div className="text-[9px] font-bold text-gray-700 mb-2">The following changes will be made:</div>
              <div className="space-y-2">
                {(confirmApply.impacts || []).map((imp, idx) => (
                  <div key={idx} className={`flex items-start gap-3 rounded-lg p-2.5 border ${
                    imp.impact_type === 'cancel' ? 'bg-red-50 border-red-200' :
                    imp.impact_type === 'new' ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-200'
                  }`}>
                    <span className={`text-[7px] font-bold px-1.5 py-0.5 rounded flex-shrink-0 ${CCN_IMPACT_COLORS[imp.impact_type]}`}>
                      {CCN_IMPACT_LABELS[imp.impact_type]}
                    </span>
                    <div className="text-[9px]">
                      <span className="font-semibold">{imp.milestone_code || imp.existing_code || '—'}</span>
                      {imp.impact_type === 'cancel' && <span className="text-red-600"> — will be set to Cancelled</span>}
                      {imp.impact_type === 'amend' && (
                        <span className="text-amber-700">
                          {imp.new_value != null && ` · value → £${Number(imp.new_value).toLocaleString()}`}
                          {imp.new_contract_date && ` · contract date → ${new Date(imp.new_contract_date).toLocaleDateString('en-GB')}`}
                        </span>
                      )}
                      {imp.impact_type === 'new' && (
                        <span className="text-emerald-700">
                          {imp.description ? ` — ${imp.description}` : ''}
                          {imp.new_value != null && ` · £${Number(imp.new_value).toLocaleString()}`}
                          {imp.new_contract_date && ` · due ${new Date(imp.new_contract_date).toLocaleDateString('en-GB')}`}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
              <button onClick={() => setConfirmApply(null)} className="px-3 py-1.5 text-[9px] border border-gray-200 rounded hover:bg-gray-50">Cancel</button>
              <button onClick={handleApplyImpacts} disabled={applying}
                className="px-4 py-1.5 text-[9px] font-bold bg-purple-600 text-white rounded hover:bg-purple-700 disabled:opacity-50">
                {applying ? 'Applying…' : 'Confirm — Apply Changes'}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ── CHECKLIST TAB ─────────────────────────────────────────────────────────────
const TASK_CATS = ['All','security','documentation','testing','governance','deployment','general'];

function RepeatableTasksTab({ tasks, magnitude, onUpdate, readOnly = false }) {
  const [catFilter, setCatFilter] = useState('All');
  const [teams, setTeams] = useState([]);
  const filtered = catFilter === 'All' ? tasks : tasks.filter(t => t.category === catFilter);

  // Pick the right est days based on the SOW's magnitude
  const estDays = (t) => {
    if (magnitude === 'Small'  && t.estimated_days_small  != null) return t.estimated_days_small;
    if (magnitude === 'Medium' && t.estimated_days_medium != null) return t.estimated_days_medium;
    if (magnitude === 'Large'  && t.estimated_days_large  != null) return t.estimated_days_large;
    return t.default_estimated_days;
  };
  const applicable = tasks.filter(t => t.is_applicable);
  const done = applicable.filter(t => t.status === 'completed').length;

  useEffect(() => {
    api.get('teams').then(setTeams).catch(() => setTeams([]));
  }, []);

  return (
    <div>
      <div className="flex gap-1 mb-2 flex-wrap">
        {TASK_CATS.map(c => (
          <button key={c} onClick={() => setCatFilter(c)}
            className={`px-2 py-0.5 text-[9px] rounded capitalize ${catFilter === c ? 'bg-navy text-white' : 'bg-white border border-gray-200 text-gray-500 hover:bg-gray-50'}`}>
            {c}
          </button>
        ))}
        <div className="ml-auto text-[8px] text-gray-400 self-center">{done}/{applicable.length} complete</div>
      </div>
      <div className="bg-white border border-gray-200 rounded overflow-x-auto">
        <table className="w-full">
          <thead><tr>
            <th className="px-2 py-1 text-center text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50 w-8">App?</th>
            <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Deliverable</th>
            <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Category</th>
            <th className="px-2 py-1 text-right text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">
              Est. Days{magnitude ? <span className="ml-1 font-normal text-gray-400 normal-case">({magnitude})</span> : ''}
            </th>
            <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Team</th>
            <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Anticipated Date</th>
            <th className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold border-b bg-gray-50">Status</th>
          </tr></thead>
          <tbody>
            {filtered.map(t => {
              const tid = t.template_id || t.id;
              return (
                <tr key={tid} className={`border-b border-gray-100 ${t.is_applicable ? '' : 'opacity-40'}`}>
                  <td className="px-2 py-1 text-center">
                    <input type="checkbox" checked={!!t.is_applicable} disabled={readOnly} onChange={e => onUpdate(tid, { is_applicable: e.target.checked ? 1 : 0 })} className="w-3 h-3 cursor-pointer disabled:cursor-not-allowed" />
                  </td>
                  <td className="px-2 py-1 text-[10px] font-semibold">{t.name}<div className="text-[8px] text-gray-400 font-normal">{t.description}</div></td>
                  <td className="px-2 py-1 text-[9px] text-gray-500 capitalize">{t.category}</td>
                  <td className="px-2 py-1 text-[10px] text-right">{estDays(t) ?? '—'}d</td>
                  <td className="px-2 py-1">
                    <select
                      disabled={readOnly || !t.is_applicable}
                      value={t.team_id || ''}
                      onChange={e => onUpdate(tid, { team_id: e.target.value || null })}
                      className="form-select text-[9px] py-0.5 disabled:bg-gray-50 disabled:cursor-not-allowed"
                      style={{ minWidth: '100px' }}
                    >
                      <option value="">No team</option>
                      {teams.map(tm => <option key={tm.id} value={tm.id}>{tm.name}</option>)}
                    </select>
                  </td>
                  <td className="px-2 py-1">
                    <input type="date" disabled={readOnly || !t.is_applicable} value={t.due_date || ''} onChange={e => onUpdate(tid, { due_date: e.target.value })} className="form-input text-[9px] py-0.5 disabled:bg-gray-50 disabled:cursor-not-allowed" />
                  </td>
                  <td className="px-2 py-1">
                    <select disabled={readOnly || !t.is_applicable} value={t.status || 'not_started'} onChange={e => onUpdate(tid, { status: e.target.value })} className="form-select text-[9px] py-0.5 disabled:bg-gray-50 disabled:cursor-not-allowed">
                      <option value="not_started">Not Started</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="n_a">N/A</option>
                    </select>
                  </td>
                </tr>
              );
            })}
            {filtered.length === 0 && <tr><td colSpan="7" className="text-center py-6 text-[10px] text-gray-400">No tasks in this category</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Risk constants (inline for SOW tab) ────────────────────────────────────
const RISK_CATEGORIES = [
  { value: 'delivery',    label: 'Delivery' },
  { value: 'technical',   label: 'Technical' },
  { value: 'resource',    label: 'Resource' },
  { value: 'commercial',  label: 'Commercial' },
  { value: 'security',    label: 'Security' },
  { value: 'vendor',      label: 'Vendor' },
  { value: 'regulatory',  label: 'Regulatory' },
  { value: 'operational', label: 'Operational' },
];
const RISK_STATUSES = [
  // 'open' shows as "New": a risk nobody has reviewed or mitigated yet. The stored
  // value is unchanged, and the RAID register labels it the same way.
  { value: 'open',        label: 'New',         cls: 'bg-red-100 text-red-700' },
  { value: 'mitigating',  label: 'Mitigating',  cls: 'bg-amber-100 text-amber-700' },
  { value: 'accepted',    label: 'Accepted',    cls: 'bg-blue-100 text-blue-700' },
  { value: 'transferred', label: 'Transferred', cls: 'bg-purple-100 text-purple-700' },
  { value: 'closed',      label: 'Closed',      cls: 'bg-gray-100 text-gray-500' },
];
const PROB_LBL  = { 1: 'Rare', 2: 'Unlikely', 3: 'Possible', 4: 'Likely', 5: 'Almost Certain' };
const IMPACT_LBL = { 1: 'Negligible', 2: 'Minor', 3: 'Moderate', 4: 'Significant', 5: 'Critical' };
function riskScoreColor(s) {
  if (s >= 15) return 'bg-red-500 text-white';
  if (s >= 8)  return 'bg-amber-400 text-amber-950';
  if (s >= 4)  return 'bg-yellow-300 text-gray-800';
  return 'bg-emerald-400 text-emerald-950';
}
function riskScoreLabel(s) {
  if (s >= 15) return 'HIGH';
  if (s >= 8)  return 'MEDIUM';
  if (s >= 4)  return 'LOW';
  return 'MINIMAL';
}

function SowRisksTab({ sowId, sow, people }) {
  const [risks, setRisks]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showAdd, setShowAdd]     = useState(false);
  const [editRisk, setEditRisk]   = useState(null);
  const [expanded, setExpanded]   = useState(null);

  const load = () => {
    setLoading(true);
    api.get('risks', { sow_id: sowId }).then(r => { setRisks(r || []); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(load, [sowId]);

  const saveRisk = async (data) => {
    if (editRisk) {
      await api.put(`risks/${editRisk.id}`, { ...data, sow_id: sowId });
    } else {
      await api.post('risks', { ...data, sow_id: sowId });
    }
    setShowAdd(false); setEditRisk(null); load();
  };

  const closeRisk = async (id) => {
    if (!confirm('Close this risk?')) return;
    await api.delete(`risks/${id}`);
    load();
  };

  const open = risks.filter(r => r.status !== 'closed' && r.status !== 'transferred');
  const closed = risks.filter(r => r.status === 'closed' || r.status === 'transferred');

  if (loading) return <div className="text-center py-8 text-gray-400 text-sm">Loading risks...</div>;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[9px] text-gray-500">{open.length} open · {closed.length} closed · {risks.length} total</div>
        </div>
        <button onClick={() => setShowAdd(true)} className="px-3 py-1.5 bg-red-500 text-white text-[10px] font-semibold rounded hover:bg-red-600">+ Add Risk</button>
      </div>

      {risks.length === 0 && (
        <div className="bg-white border border-gray-200 rounded p-8 text-center text-[10px] text-gray-400">No risks logged for this SOW. Click + Add Risk to record one.</div>
      )}

      {risks.length > 0 && (
        <div className="bg-white border border-gray-200 rounded overflow-hidden">
          <table className="w-full">
            <thead><tr className="bg-gray-50 border-b border-gray-100">
              <th className="px-2 py-1.5 text-left text-[7.5px] uppercase text-gray-400 font-bold w-16">Ref</th>
              <th className="px-2 py-1.5 text-left text-[7.5px] uppercase text-gray-400 font-bold">Title</th>
              <th className="px-2 py-1.5 text-left text-[7.5px] uppercase text-gray-400 font-bold">Category</th>
              <th className="px-2 py-1.5 text-center text-[7.5px] uppercase text-gray-400 font-bold">Score</th>
              <th className="px-2 py-1.5 text-left text-[7.5px] uppercase text-gray-400 font-bold">Status</th>
              <th className="px-2 py-1.5 text-left text-[7.5px] uppercase text-gray-400 font-bold">Owner</th>
              <th className="px-2 py-1.5 w-16"></th>
            </tr></thead>
            <tbody>
              {risks.map(r => {
                const statusDef = RISK_STATUSES.find(s => s.value === r.status) || RISK_STATUSES[0];
                const catDef = RISK_CATEGORIES.find(c => c.value === r.category);
                const isExp = expanded === r.id;
                return (
                  <>
                    <tr key={r.id} onClick={() => setExpanded(isExp ? null : r.id)} className="border-b border-gray-50 hover:bg-gray-50/50 cursor-pointer">
                      <td className="px-2 py-1.5 text-[8px] font-mono text-gray-500">{r.reference}</td>
                      <td className="px-2 py-1.5">
                        <div className="text-[9px] font-semibold text-gray-800 flex items-center gap-1">
                          <span className="text-[8px] text-gray-400">{isExp ? '▾' : '▸'}</span>
                          {r.title}
                          {r.pre_staged ? <span className="text-amber-500" title="Pre-staged — awaiting PM expansion of details">⚠</span> : null}
                        </div>
                      </td>
                      <td className="px-2 py-1.5 text-[9px] text-gray-500 capitalize">{catDef?.label || r.category}</td>
                      <td className="px-2 py-1.5 text-center">
                        <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[8px] font-bold ${riskScoreColor(r.risk_score)}`}>
                          {r.risk_score} <span className="text-[6px]">{riskScoreLabel(r.risk_score)}</span>
                        </span>
                      </td>
                      <td className="px-2 py-1.5">
                        <span className={`text-[7.5px] px-1.5 py-0.5 rounded font-semibold ${statusDef.cls}`}>{statusDef.label}</span>
                      </td>
                      <td className="px-2 py-1.5 text-[9px] text-gray-500">{r.owner_name || '—'}</td>
                      <td className="px-2 py-1.5 text-right whitespace-nowrap">
                        <button onClick={e => { e.stopPropagation(); setEditRisk(r); }} className="text-[8px] text-gold hover:underline mr-1">Edit</button>
                        {r.status !== 'closed' && <button onClick={e => { e.stopPropagation(); closeRisk(r.id); }} className="text-[8px] text-gray-400 hover:text-red-500">Close</button>}
                      </td>
                    </tr>
                    {isExp && (
                      <tr key={`${r.id}-exp`} className="border-b border-gray-100 bg-gray-50/70">
                        <td colSpan="7" className="px-4 py-2">
                          <div className="grid grid-cols-2 gap-3 text-[8px]">
                            <div>
                              <div className="font-bold text-gray-500 uppercase mb-0.5">Probability</div>
                              <div>{r.probability} — {PROB_LBL[r.probability]}</div>
                            </div>
                            <div>
                              <div className="font-bold text-gray-500 uppercase mb-0.5">Impact</div>
                              <div>{r.impact} — {IMPACT_LBL[r.impact]}</div>
                            </div>
                            {r.description && (
                              <div className="col-span-2">
                                <div className="font-bold text-gray-500 uppercase mb-0.5">Description</div>
                                <div className="text-gray-700">{r.description}</div>
                              </div>
                            )}
                            {r.mitigation_plan && (
                              <div>
                                <div className="font-bold text-gray-500 uppercase mb-0.5">Mitigation</div>
                                <div className="text-gray-700">{r.mitigation_plan}</div>
                              </div>
                            )}
                            {r.contingency_plan && (
                              <div>
                                <div className="font-bold text-gray-500 uppercase mb-0.5">Contingency</div>
                                <div className="text-gray-700">{r.contingency_plan}</div>
                              </div>
                            )}
                            {r.review_date && (
                              <div>
                                <div className="font-bold text-gray-500 uppercase mb-0.5">Review Date</div>
                                <div>{new Date(r.review_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}</div>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {(showAdd || editRisk) && (
        <SowRiskModal
          risk={editRisk}
          people={people}
          onClose={() => { setShowAdd(false); setEditRisk(null); }}
          onSave={saveRisk}
        />
      )}
    </div>
  );
}

function SowRiskModal({ risk, people, onClose, onSave }) {
  const [form, setForm] = useState({
    title:             risk?.title || '',
    description:       risk?.description || '',
    category:          risk?.category || 'delivery',
    probability:       risk?.probability ?? 3,
    impact:            risk?.impact ?? 3,
    status:            risk?.status || 'open',
    owner_id:          risk?.owner_id || '',
    mitigation_plan:   risk?.mitigation_plan || '',
    contingency_plan:  risk?.contingency_plan || '',
    review_date:       risk?.review_date || '',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const score = (form.probability || 1) * (form.impact || 1);

  return (
    <Modal title={risk ? `Edit Risk: ${risk.reference}` : 'Log New Risk'} onClose={onClose} footer={
      <><button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
        <button onClick={() => { if (form.title.trim()) onSave({ ...form, probability: parseInt(form.probability), impact: parseInt(form.impact), owner_id: form.owner_id || null, review_date: form.review_date || null }); }}
          disabled={!form.title.trim()} className="px-3 py-1 text-[10px] font-semibold bg-red-500 text-white rounded disabled:opacity-40">Save Risk</button></>
    }>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Title *</label>
          <input className="form-input" value={form.title} onChange={e => set('title', e.target.value)} autoFocus />
        </div>
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Description</label>
          <textarea className="form-textarea" rows="2" value={form.description} onChange={e => set('description', e.target.value)} />
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Category</label>
          <select className="form-select" value={form.category} onChange={e => set('category', e.target.value)}>
            {RISK_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Status</label>
          <select className="form-select" value={form.status} onChange={e => set('status', e.target.value)}>
            {RISK_STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Probability (1–5)</label>
          <select className="form-select" value={form.probability} onChange={e => set('probability', e.target.value)}>
            {[1,2,3,4,5].map(n => <option key={n} value={n}>{n} — {PROB_LBL[n]}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Impact (1–5)</label>
          <select className="form-select" value={form.impact} onChange={e => set('impact', e.target.value)}>
            {[1,2,3,4,5].map(n => <option key={n} value={n}>{n} — {IMPACT_LBL[n]}</option>)}
          </select>
        </div>
        <div className="col-span-2 flex items-center gap-2">
          <span className={`px-2 py-1 rounded text-[9px] font-bold ${riskScoreColor(score)}`}>{score} — {riskScoreLabel(score)}</span>
          <span className="text-[8px] text-gray-400">P{form.probability} × I{form.impact}</span>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Owner</label>
          <select className="form-select" value={form.owner_id} onChange={e => set('owner_id', e.target.value)}>
            <option value="">— unassigned —</option>
            {people.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Review Date</label>
          <input className="form-input" type="date" value={form.review_date} onChange={e => set('review_date', e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Mitigation Plan</label>
          <textarea className="form-textarea" rows="2" value={form.mitigation_plan} onChange={e => set('mitigation_plan', e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Contingency Plan</label>
          <textarea className="form-textarea" rows="2" value={form.contingency_plan} onChange={e => set('contingency_plan', e.target.value)} />
        </div>
      </div>
    </Modal>
  );
}

// ── MILESTONE FORM (shared between Add and Edit) ───────────────────────────────
// initialStatus: if provided, only show valid transition targets (edit mode)
function MilestoneForm({ form, set, people, sow, initialStatus }) {
  const availableStatuses = initialStatus
    ? (MILESTONE_STATUS_TRANSITIONS[initialStatus] || ALL_MILESTONE_STATUSES)
    : ALL_MILESTONE_STATUSES;
  // SES required: when SES applicable and moving to Ready to bill or Invoiced
  const sesRequired = !!sow?.ses_applicable && (form.status === 'Ready to bill' || form.status === 'Invoiced');
  const sesValid    = !sesRequired || (form.ses_number && form.ses_number.trim());
  return (
    <div className="grid grid-cols-2 gap-2">
      <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Milestone Code *</label><input className="form-input" value={form.milestone_code} onChange={e => set('milestone_code', e.target.value)} placeholder="AVM-01" /></div>
      <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Status</label>
        <select className="form-select" value={form.status} onChange={e => set('status', e.target.value)}>
          {availableStatuses.map(s => <option key={s}>{s}</option>)}
        </select>
        {initialStatus && availableStatuses.length < ALL_MILESTONE_STATUSES.length && (
          <div className="text-[7px] text-gray-400 mt-0.5">Only valid transitions from {initialStatus} are shown</div>
        )}
      </div>
      <div className="col-span-2"><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Description *</label><input className="form-input" value={form.description} onChange={e => set('description', e.target.value)} /></div>
      <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Contract Date</label><input className="form-input" type="date" value={form.contract_date} onChange={e => set('contract_date', e.target.value)} /></div>
      <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Forecast Date</label><input className="form-input" type="date" value={form.forecast_date} onChange={e => set('forecast_date', e.target.value)} /></div>
      <div>
        <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">
          Weeks after PO <span className="font-normal normal-case text-gray-400">(optional — used to auto-suggest dates when PO is received)</span>
        </label>
        <div className="flex items-center gap-2">
          <input className="form-input w-20" type="number" min="1" step="1" placeholder="e.g. 8"
            value={form.weeks_after_po || ''}
            onChange={e => set('weeks_after_po', e.target.value ? parseInt(e.target.value) : '')} />
          {form.weeks_after_po > 0 && <span className="text-[9px] text-blue-600 font-semibold">{form.weeks_after_po}w after PO received</span>}
        </div>
      </div>
      {/* The amount is SOW money: not offered without the grant, and the save leaves it out. */}
      {canAccess('sow:see-financials') && <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Amount excl VAT (£)</label><input className="form-input" type="number" step="0.01" value={form.invoice_amount_excl} onChange={e => set('invoice_amount_excl', parseFloat(e.target.value) || '')} /></div>}
      <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Project Manager</label>
        <select className="form-select" value={form.project_manager_id || ''} onChange={e => set('project_manager_id', e.target.value)}>
          <option value="">Select PM...</option>
          {people.map(p => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>)}
        </select>
      </div>
      <div>
        <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">
          PO Line Item <span className="font-normal normal-case text-gray-400">(optional, the client PO line this milestone bills)</span>
        </label>
        <input className="form-input w-32" value={form.po_line_item || ''} onChange={e => set('po_line_item', e.target.value)} placeholder="e.g. 10" />
      </div>
      {/* SES Number — shown always if SOW has SES applicable; mandatory when Ready to Bill or Invoiced */}
      {!!sow?.ses_applicable ? (
        <div className="col-span-2">
          <label className={`block text-[8px] font-bold uppercase mb-0.5 ${sesRequired ? 'text-red-500' : 'text-gray-500'}`}>
            SES Number {sesRequired ? '* (Required for this status)' : '(SES Applicable)'}
          </label>
          <input className={`form-input ${sesRequired && !sesValid ? 'border-red-400 bg-red-50' : ''}`}
            value={form.ses_number || ''}
            onChange={e => set('ses_number', e.target.value)}
            placeholder="e.g. 5000123456"
          />
          {sesRequired && !sesValid && (
            <div className="text-[8px] text-red-500 mt-0.5">SES number is mandatory before this milestone can move to {form.status}.</div>
          )}
        </div>
      ) : null}
    </div>
  );
}

// ── MILESTONE ROW (with inline date editing for PMO/Admin) ───────────────────
const HEALTH_DELIVERY_LABELS = { on_track: 'On Track', at_risk: 'At Risk', delayed: 'Delayed', blocked: 'Blocked', issued: 'Issued', on_hold: 'On Hold', uat: 'UAT', complete: 'Complete' };
const HEALTH_DELIVERY_COLORS = { on_track: 'text-emerald-700 bg-emerald-50', at_risk: 'text-amber-700 bg-amber-50', delayed: 'text-red-700 bg-red-50', blocked: 'text-red-700 bg-red-50', issued: 'text-red-700 bg-red-50', on_hold: 'text-gray-600 bg-gray-100', uat: 'text-blue-700 bg-blue-50', complete: 'text-teal-700 bg-teal-50' };

// rollup is undefined on an ordinary SOW, and on a framework is this pot's line
// of the call-off roll-up (null for a framework milestone that is not a pot).
function MilestoneRow({ milestone: m, highlight, slipped, overdue, canEdit, onEdit, onDelete, onHistory, onInlineSave, rollup, seesMoney, canRecordCoupa }) {
  const [editingField, setEditingField] = useState(null);
  const [tempVal, setTempVal] = useState('');
  const [showHealthNotes, setShowHealthNotes] = useState(false);
  const [showCallOffs, setShowCallOffs] = useState(false);
  // One more than the cells drawn when the amount is not shown; a span past the
  // last column is harmless and keeps the row full width either way.
  const cols = 9 + (canEdit ? 1 : 0) + (rollup !== undefined ? 3 : 0);

  const hasHealthNotes = m.health_recent_progress || m.health_next_period || m.health_risks_issues;

  const startInline = (field) => {
    if (!canEdit) return;
    setEditingField(field);
    setTempVal(m[field] || '');
  };
  const commitInline = async () => {
    if (editingField && tempVal !== m[editingField]) await onInlineSave(m.id, { [editingField]: tempVal || null });
    setEditingField(null);
  };
  const cancelInline = () => setEditingField(null);

  return (
    <>
    <tr id={`ms-${m.id}`} className={`hover:bg-gold/5 border-b border-gray-100 transition-colors ${highlight ? 'bg-gold/20 ring-2 ring-gold ring-inset' : ''}`}>
      <td className="px-2 py-1 text-[10px] font-bold">{m.milestone_code}</td>
      <td className="px-2 py-1">
        <Tag status={m.status} />
        {(m.status === 'Invoiced' || m.status === 'Paid') && m.invoice_number && (
          <div className="text-[8px] text-gray-600 font-medium mt-0.5 whitespace-nowrap">{m.invoice_number}</div>
        )}
        {(m.status === 'Invoiced' || m.status === 'Paid') && m.formal_invoice_date && (
          <div className="text-[8px] text-gray-400 mt-0.5 whitespace-nowrap">inv {formatDate(m.formal_invoice_date)}</div>
        )}
        {m.status === 'Paid' && m.paid_date && (
          <div className="text-[8px] text-emerald-600 mt-0.5 whitespace-nowrap font-semibold">pd {formatDate(m.paid_date)}</div>
        )}
      </td>
      <td className="px-2 py-1 text-[9px]">
        {editingField === 'contract_date' ? (
          <input type="date" autoFocus value={tempVal} onChange={e => setTempVal(e.target.value)}
            onBlur={commitInline} onKeyDown={e => { if (e.key === 'Enter') commitInline(); if (e.key === 'Escape') cancelInline(); }}
            className="form-input text-[9px] py-0 px-1 w-32" />
        ) : (
          <span className={canEdit ? 'cursor-pointer border-b border-dashed border-gray-300 hover:border-gold hover:text-gold' : ''}
            title={canEdit ? 'Contract date — PMO only' : 'Contract date — PMO only (raise a CCN)'} onClick={() => { if (canEdit) startInline('contract_date'); }}>
            {formatDate(m.contract_date) || '—'}
          </span>
        )}
        {m.weeks_after_po > 0 && <div className="text-[8px] text-blue-500 mt-0.5 whitespace-nowrap">{m.weeks_after_po}w after PO</div>}
      </td>
      <td className={`px-2 py-1 text-[9px] ${overdue ? 'text-red-600 font-bold' : slipped ? 'text-amber-600 font-semibold' : ''}`}>
        {editingField === 'forecast_date' ? (
          <input type="date" autoFocus value={tempVal} onChange={e => setTempVal(e.target.value)}
            onBlur={commitInline} onKeyDown={e => { if (e.key === 'Enter') commitInline(); if (e.key === 'Escape') cancelInline(); }}
            className="form-input text-[9px] py-0 px-1 w-32" />
        ) : (
          <span className={canEdit ? 'cursor-pointer border-b border-dashed border-gray-300 hover:border-gold hover:text-gold' : ''}
            title={canEdit ? 'Click to edit' : ''} onClick={() => startInline('forecast_date')}>
            {formatDate(m.forecast_date) || '—'}{(slipped || overdue) && ' ⚠'}
          </span>
        )}
      </td>
      <td className="px-2 py-1 text-[10px]">{m.description}</td>
      {seesMoney && <td className="px-2 py-1 text-[10px] font-semibold text-right whitespace-nowrap">{formatMoney(m.invoice_amount_excl, 2)}</td>}
      {rollup !== undefined && <RollupCells line={rollup} pot={m.invoice_amount_excl || 0} open={showCallOffs} onToggle={() => setShowCallOffs(v => !v)} />}
      <CoupaCell milestone={m} canRecord={canRecordCoupa} onSave={onInlineSave} />
      <td className="px-2 py-1 text-[9px]">{m.pm_name || '-'}</td>
      <td className="px-2 py-1">
        <div className="flex gap-1.5 items-center">
          <button onClick={onHistory} className="text-[7px] text-blue-500 hover:underline whitespace-nowrap">📋 History</button>
          {hasHealthNotes && (
            <button
              onClick={() => setShowHealthNotes(v => !v)}
              className={`text-[7px] font-semibold whitespace-nowrap hover:underline ${showHealthNotes ? 'text-purple-600' : 'text-purple-400'}`}
              title="Show/hide PM health notes"
            >
              {showHealthNotes ? '▲ Notes' : '▼ Notes'}
            </button>
          )}
        </div>
      </td>
      {canEdit && (
        <td className="px-2 py-1 text-center whitespace-nowrap">
          <button onClick={onEdit} className="text-[8px] text-gold hover:underline mr-1">Edit</button>
          <button onClick={onDelete} className="text-[8px] text-red-400 hover:text-red-600">×</button>
        </td>
      )}
    </tr>
    {showCallOffs && rollup && <CallOffList items={rollup.items} colSpan={cols} />}
    {showHealthNotes && hasHealthNotes && (
      <tr className="bg-purple-50 border-b border-purple-100">
        <td colSpan={cols} className="px-3 py-2">
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-[7px] font-bold uppercase text-purple-600 tracking-wide">PM Health Notes</span>
            {m.health_week_commencing && <span className="text-[7px] text-gray-400">w/c {formatDate(m.health_week_commencing)}</span>}
            {m.health_updated_by && <span className="text-[7px] text-gray-400">— {m.health_updated_by}</span>}
            {m.health_delivery_status && (
              <span className={`text-[7px] font-bold px-1.5 py-px rounded ${HEALTH_DELIVERY_COLORS[m.health_delivery_status] || 'text-gray-600 bg-gray-100'}`}>
                {HEALTH_DELIVERY_LABELS[m.health_delivery_status] || m.health_delivery_status}
              </span>
            )}
          </div>
          <div className="grid grid-cols-3 gap-3">
            {m.health_recent_progress && (
              <div>
                <div className="text-[7px] font-bold uppercase text-gray-500 mb-0.5">Recent Progress</div>
                <div className="text-[8px] text-gray-700 leading-relaxed">{m.health_recent_progress}</div>
              </div>
            )}
            {m.health_next_period && (
              <div>
                <div className="text-[7px] font-bold uppercase text-gray-500 mb-0.5">Next Period</div>
                <div className="text-[8px] text-gray-700 leading-relaxed">{m.health_next_period}</div>
              </div>
            )}
            {m.health_risks_issues && (
              <div>
                <div className="text-[7px] font-bold uppercase text-gray-500 mb-0.5">Risks / Issues</div>
                <div className="text-[8px] text-gray-700 leading-relaxed">{m.health_risks_issues}</div>
              </div>
            )}
          </div>
        </td>
      </tr>
    )}
    </>
  );
}

// ── COUPA APPROVED (recorded by Finance) ─────────────────────────────────────
// The date the client approved the milestone's invoice on Coupa, which is on no
// schedule, so Finance type it in. Only an invoiced or paid milestone on a
// Coupa PO (one starting 6000, utils/coupa.js) has one to record; any other is
// left blank unless a date was recorded anyway. Two weeks after invoicing with
// no date, Finance get an Action to chase it (src/lib/coupaChaseActions.js),
// and the cell says so. A refusal or a bad
// date is shown in the cell, where the date was typed.
const COUPA_CHASE_DAYS = 14;
const daysSince = (iso) => Math.floor((Date.now() - new Date(`${String(iso).slice(0, 10)}T00:00:00Z`).getTime()) / 86400000);

function CoupaCell({ milestone: m, canRecord, onSave }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const recorded = String(m.coupa_approved || '').trim();
  const applies = !!recorded || ((m.status === 'Invoiced' || m.status === 'Paid') && isCoupaMilestone(m));
  if (!applies) return <td className="px-2 py-1" />;

  const today = new Date().toISOString().split('T')[0];
  const isDate = /^\d{4}-\d{2}-\d{2}$/.test(recorded);
  const start = () => { if (!canRecord || saving) return; setValue(isDate ? recorded : today); setError(''); setEditing(true); };
  const commit = async () => {
    const next = value.trim();
    if (next === recorded) { setEditing(false); return; }
    setSaving(true); setError('');
    try {
      await onSave(m.id, { coupa_approved: next || null });
      setEditing(false);
    } catch (e) {
      setError(e?.message || 'That could not be saved. Please try again.');
    } finally { setSaving(false); }
  };

  if (editing) {
    return (
      <td className="px-2 py-1 text-[9px]">
        <input type="date" autoFocus max={today} value={value} disabled={saving}
          onChange={e => setValue(e.target.value)} onBlur={commit}
          onKeyDown={e => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setEditing(false); setError(''); } }}
          className="form-input text-[9px] py-0 px-1 w-32" />
        {saving && <div className="text-[8px] text-gray-400 mt-0.5">Saving…</div>}
        {error && <div className="text-[8px] text-red-600 mt-0.5 max-w-[160px]">{error}</div>}
      </td>
    );
  }

  // Invoiced with nothing recorded: say how long it has waited, amber once Finance are chasing.
  const waited = !recorded && m.status === 'Invoiced' && m.invoiced_at ? daysSince(m.invoiced_at) : null;
  const chasing = waited != null && waited >= COUPA_CHASE_DAYS;
  const editable = canRecord ? 'cursor-pointer border-b border-dashed border-gray-300 hover:border-gold hover:text-gold' : '';
  return (
    <td className="px-2 py-1 text-[9px] whitespace-nowrap">
      {recorded ? (
        <span className={`text-emerald-700 font-semibold ${editable}`} onClick={start}
          title={canRecord ? 'Approved on Coupa. Click to change the date, or clear it' : 'Approved on Coupa'}>
          {isDate ? formatDate(recorded) : recorded}
        </span>
      ) : m.status === 'Paid' ? (
        <span className={`text-gray-300 ${editable}`} onClick={start} title={canRecord ? 'No Coupa date recorded. Click to add it' : 'No Coupa date recorded'}>—</span>
      ) : (
        <span className={`${chasing ? 'text-amber-600 font-semibold' : 'text-gray-400'} ${editable}`} onClick={start}
          title={chasing
            ? `Invoiced ${waited} days ago and not yet approved on Coupa. Finance have an Action to chase it.${canRecord ? ' Click to record the approval date.' : ''}`
            : canRecord ? 'Not yet approved on Coupa. Click to record the approval date' : 'Not yet approved on Coupa'}>
          {canRecord ? '+ add' : 'Awaiting'}{chasing && <> · {waited}d</>}
        </span>
      )}
      {error && <div className="text-[8px] text-red-600 mt-0.5 max-w-[160px] whitespace-normal">{error}</div>}
    </td>
  );
}

// Milestones table headings over right-aligned figures.
const MONEY_HEADINGS = ['Amount', 'Called off', 'Invoiced', 'Left'];

// ── FRAMEWORK ROLL-UP (call-off milestones against each pot) ─────────────────
// Called off / Invoiced / Left for one pot. pot is null on the unmatched row,
// which has nothing to draw down; line is null on a framework milestone that is
// not a pot (cancelled, say), which gets empty cells.
function RollupCells({ line, pot, open, onToggle }) {
  if (!line) return <><td /><td /><td /></>;
  const left = pot == null ? null : pot - line.called_off;
  const pct = (v) => `${Math.min(100, Math.max(0, (v / pot) * 100))}%`;
  const count = line.items.length;
  return (
    <>
      <td className="px-2 py-1 text-[10px] text-right whitespace-nowrap">
        {formatMoney(line.called_off, 2)}
        {count > 0 && (
          <button onClick={onToggle} className="block ml-auto text-[7px] text-blue-500 hover:underline whitespace-nowrap">
            {open ? '▲' : '▼'} {count} call-off milestone{count === 1 ? '' : 's'}
          </button>
        )}
      </td>
      <td className="px-2 py-1 text-[10px] text-right whitespace-nowrap">{formatMoney(line.invoiced, 2)}</td>
      <td className={`px-2 py-1 text-[10px] font-semibold text-right whitespace-nowrap ${left < 0 ? 'text-red-600' : ''}`}>
        {left == null ? '—' : formatMoney(left, 2)}
        {pot > 0 && (
          <div className="flex h-1 w-20 ml-auto mt-0.5 rounded overflow-hidden bg-gray-200"
            title={`${Math.round((line.invoiced / pot) * 100)}% invoiced, ${Math.round((line.called_off / pot) * 100)}% called off`}>
            <div className="bg-emerald-500" style={{ width: pct(line.invoiced) }} />
            <div className="bg-emerald-500/40" style={{ width: pct(line.called_off - line.invoiced) }} />
          </div>
        )}
      </td>
    </>
  );
}

// The call-off milestones behind a roll-up figure; each opens its own SOW.
function CallOffList({ items, colSpan }) {
  const { navigate } = useApp();
  return (
    <tr className="bg-gray-50 border-b border-gray-100">
      <td colSpan={colSpan} className="px-3 py-2">
        <table className="w-full text-[9px]">
          <tbody>
            {items.map(i => (
              <tr key={i.id} className="border-b border-gray-100 hover:bg-blue-50/40 cursor-pointer" onClick={() => navigate('sow-detail', i.sow_id)}>
                <td className="px-2 py-1 font-mono font-bold text-navy whitespace-nowrap">{i.sow_reference}</td>
                <td className="px-2 py-1 text-gray-700 max-w-[220px] truncate" title={i.sow_detail}>{i.sow_detail || '—'}</td>
                <td className="px-2 py-1 font-bold whitespace-nowrap">{i.milestone_code}</td>
                <td className="px-2 py-1 text-gray-600">{i.description}</td>
                <td className="px-2 py-1"><Tag status={i.status} /></td>
                <td className="px-2 py-1 text-right font-semibold whitespace-nowrap">{formatMoney(i.amount, 2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </td>
    </tr>
  );
}

// Call-off milestones that match no pot, kept on the table so the totals
// still reconcile and someone can see which descriptions to correct.
function UnmatchedCallOffsRow({ line, canEdit }) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <tr className="border-b border-gray-100 bg-amber-50">
        <td className="px-2 py-1 text-[10px] font-bold text-amber-700">Unmatched</td>
        <td colSpan="4" className="px-2 py-1 text-[9px] text-amber-700">Call-off milestones whose description does not start with Hardware or Services</td>
        <td />
        <RollupCells line={line} pot={null} open={open} onToggle={() => setOpen(v => !v)} />
        <td colSpan={canEdit ? 3 : 2} />
      </tr>
      {open && <CallOffList items={line.items} colSpan={11 + (canEdit ? 1 : 0)} />}
    </>
  );
}

// A milestone form from somebody not shown its amount does not send one.
const withoutHiddenAmount = (form) => {
  if (canAccess('sow:see-financials')) return form;
  const { invoice_amount_excl, ...rest } = form; // eslint-disable-line no-unused-vars
  return rest;
};

function AddMilestoneModal({ sowRef, sow, people, nextCode, onClose, onSave }) {
  const [form, setForm] = useState({ milestone_code: nextCode || 'AVM-01', description: '', contract_date: '', forecast_date: '', invoice_amount_excl: '', status: 'Awaiting', payment_terms: 30, project_manager_id: sow?.owner_id || '', ses_number: '', weeks_after_po: '', po_line_item: '' });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const sesRequired = !!sow?.ses_applicable && (form.status === 'Ready to bill' || form.status === 'Invoiced');
  const canSave = !(sesRequired && !form.ses_number?.trim());
  return (
    <Modal title="Add Milestone" onClose={onClose} wide footer={
      <><button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
        <button onClick={() => { if (canSave) onSave(withoutHiddenAmount({ ...form, reference: sowRef })); }}
          disabled={!canSave} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40">Add</button></>
    }>
      <MilestoneForm form={form} set={set} people={people} sow={sow} />
    </Modal>
  );
}

function EditMilestoneModal({ milestone, sow, people, onClose, onSave }) {
  const [form, setForm] = useState({
    milestone_code: milestone.milestone_code || '',
    description: milestone.description || '',
    contract_date: milestone.contract_date || '',
    forecast_date: milestone.forecast_date || '',
    invoice_amount_excl: milestone.invoice_amount_excl || '',
    status: milestone.status || 'Awaiting',
    payment_terms: milestone.payment_terms || 45,
    project_manager_id: milestone.project_manager_id || '',
    ses_number: milestone.ses_number || '',
    weeks_after_po: milestone.weeks_after_po || '',
    po_line_item: milestone.po_line_item || '',
  });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));
  const sesRequired = !!sow?.ses_applicable && (form.status === 'Ready to bill' || form.status === 'Invoiced');
  const canSave = !(sesRequired && !form.ses_number?.trim());
  return (
    <Modal title={`Edit ${milestone.milestone_code}`} onClose={onClose} wide footer={
      <><button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
        <button onClick={() => { if (canSave) onSave(milestone.id, withoutHiddenAmount(form)); }}
          disabled={!canSave} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40">Save</button></>
    }>
      <MilestoneForm form={form} set={set} people={people} sow={sow} initialStatus={milestone.status} />
    </Modal>
  );
}

// ── ADD RESOURCE LINE MODAL ───────────────────────────────────────────────────
const DEFAULT_SECTIONS = ['Network','Security','Project Management','Testing','Infrastructure','Development'];

// ── EDIT RESOURCE LINE MODAL ──────────────────────────────────────────────────
function EditResourceModal({ line, existingSections, onClose, onSave }) {
  const { toast } = useToast();
  const [rateBands, setRateBands] = useState([]);
  const [days, setDays]           = useState(String(line.days || ''));
  const [description, setDescription] = useState(line.description || '');
  const [section, setSection]     = useState(line.work_package || '');
  const [selectedBand, setSelectedBand] = useState(null);
  const [saving, setSaving]       = useState(false);

  useEffect(() => {
    api.get('rate-cards').then(cards => {
      const active = cards.find(c => c.is_active) || cards[0];
      if (active) {
        setRateBands(active.bands || []);
        const match = (active.bands || []).find(b => b.band_code === line.band_code);
        setSelectedBand(match || null);
      }
    });
  }, []);

  const lineTotal = selectedBand && days ? selectedBand.day_rate * parseFloat(days) : 0;
  const sections  = [...new Set([...existingSections])].sort();

  const handleSave = async () => {
    if (!selectedBand || !days || !section) return;
    setSaving(true);
    try {
      await onSave({
        band_code: selectedBand.band_code,
        band_name: selectedBand.band_name,
        day_rate: selectedBand.day_rate,
        days: parseFloat(days),
        work_package: section,
        description: description || null,
      });
    } catch (e) { toast.error('Failed: ' + e.message); }
    setSaving(false);
  };

  return (
    <Modal title="Edit Resource Line" onClose={onClose} footer={
      <><button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
        <button onClick={handleSave} disabled={saving || !selectedBand || !days || !section} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40">{saving ? 'Saving...' : 'Save Changes'}</button></>
    }>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Section *</label>
          <input className="form-input" list="edit-section-list" value={section} onChange={e => setSection(e.target.value)} />
          <datalist id="edit-section-list">{sections.map(s => <option key={s} value={s} />)}</datalist>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Rate Card Band *</label>
          <select className="form-select" value={selectedBand?.band_code || ''} onChange={e => setSelectedBand(rateBands.find(b => b.band_code === e.target.value) || null)}>
            <option value="">Select band...</option>
            {rateBands.map(b => <option key={b.band_code} value={b.band_code}>{b.band_code} — {b.band_name} (£{Number(b.day_rate).toFixed(2)}/d)</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Day Rate</label>
          <div className="form-input bg-gray-50 font-semibold">{selectedBand ? `£${Number(selectedBand.day_rate).toFixed(2)}` : '—'}</div>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Days *</label>
          <input className="form-input" type="number" min="0.5" step="0.5" value={days} onChange={e => setDays(e.target.value)} />
        </div>
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Description</label>
          <input className="form-input" value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional..." />
        </div>
        {lineTotal > 0 && (
          <div className="col-span-2 bg-navy/5 border border-navy/10 rounded p-2 flex items-center justify-between">
            <span className="text-[9px] text-gray-600">{days}d × £{Number(selectedBand?.day_rate || 0).toFixed(2)}</span>
            <span className="text-sm font-extrabold text-navy">{formatMoney(lineTotal)}</span>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── ADD RESOURCE LINE MODAL ───────────────────────────────────────────────────
function AddResourceModal({ existingSections, defaultSection, onClose, onSave }) {
  const { toast } = useToast();
  const [rateBands, setRateBands]       = useState([]);
  const [resourceTypes, setResourceTypes] = useState([]);
  const [section, setSection]           = useState(defaultSection || '');
  const [selectedBand, setSelectedBand] = useState(null);
  const [days, setDays]                 = useState('');
  const [description, setDescription]   = useState('');
  const [saving, setSaving]             = useState(false);

  useEffect(() => {
    Promise.all([api.get('rate-cards'), api.get('resource-types')]).then(([cards, types]) => {
      const active = cards.find(c => c.is_active) || cards[0];
      if (active) setRateBands(active.bands || []);
      setResourceTypes(types);
    });
  }, []);

  const lineTotal = selectedBand && days ? selectedBand.day_rate * parseFloat(days) : 0;
  const sections  = [...new Set([...existingSections, ...DEFAULT_SECTIONS])].sort();

  const handleSave = async () => {
    if (!selectedBand || !days || !section) return;
    setSaving(true);
    try { await onSave({ band_code: selectedBand.band_code, band_name: selectedBand.band_name, day_rate: selectedBand.day_rate, days: parseFloat(days), work_package: section, description: description || null, sort_order: 0 }); }
    catch (e) { toast.error('Failed: ' + e.message); }
    setSaving(false);
  };

  return (
    <Modal title="Add Resource Line" onClose={onClose} footer={
      <><button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
        <button onClick={handleSave} disabled={saving || !selectedBand || !days || !section} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40">{saving ? 'Adding...' : 'Add Line'}</button></>
    }>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Section *</label>
          <input className="form-input" list="section-list" value={section} onChange={e => setSection(e.target.value)} placeholder="Network, Security..." />
          <datalist id="section-list">{sections.map(s => <option key={s} value={s} />)}</datalist>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Resource Type <span className="font-normal text-gray-400">(hints band)</span></label>
          <select className="form-select" onChange={e => { const rt = resourceTypes.find(r => r.id === e.target.value); if (rt?.default_band_code) { const b = rateBands.find(b => b.band_code === rt.default_band_code); if (b) setSelectedBand(b); } }} defaultValue="">
            <option value="">Select type...</option>
            {resourceTypes.map(rt => <option key={rt.id} value={rt.id}>{rt.name}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Rate Card Band *</label>
          <select className="form-select" value={selectedBand?.band_code || ''} onChange={e => setSelectedBand(rateBands.find(b => b.band_code === e.target.value) || null)}>
            <option value="">Select band...</option>
            {rateBands.map(b => <option key={b.band_code} value={b.band_code}>{b.band_code} — {b.band_name} (£{Number(b.day_rate).toFixed(2)}/d)</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Day Rate</label>
          <div className="form-input bg-gray-50 font-semibold">{selectedBand ? `£${Number(selectedBand.day_rate).toFixed(2)}` : '—'}</div>
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Days *</label>
          <input className="form-input" type="number" min="0.5" step="0.5" value={days} onChange={e => setDays(e.target.value)} placeholder="5" />
        </div>
        <div className="col-span-2">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Description</label>
          <input className="form-input" value={description} onChange={e => setDescription(e.target.value)} placeholder="Optional..." />
        </div>
        {lineTotal > 0 && (
          <div className="col-span-2 bg-navy/5 border border-navy/10 rounded p-2 flex items-center justify-between">
            <span className="text-[9px] text-gray-600">{days}d × £{Number(selectedBand.day_rate).toFixed(2)}</span>
            <span className="text-sm font-extrabold text-navy">{formatMoney(lineTotal)}</span>
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── LEGACY ZOHO CUTOVER HISTORY (read-only) ──────────────────────────────────
const LEGACY_STATUS_COLOR = {
  'On Track': 'bg-emerald-100 text-emerald-700', 'At Risk': 'bg-amber-100 text-amber-700',
  'Delayed': 'bg-red-100 text-red-700', 'Blocked': 'bg-red-100 text-red-700',
  'On Hold': 'bg-gray-100 text-gray-600',
};
function LegacyHistoryModal({ rows, reference, onClose }) {
  // rows arrive oldest-first from the API; show newest-first with "Latest" pinned.
  const ordered = [...rows].sort((a, b) => (a.week_commencing < b.week_commencing ? 1 : -1));
  const latest = ordered[0];
  return (
    <Modal title={`Legacy History (Zoho) — ${reference}`} onClose={onClose} xl>
      <div className="text-[9px] text-gray-500 mb-3">
        Read-only weekly snapshots imported from the pre-go-live Zoho delivery tracker — {rows.length} week{rows.length === 1 ? '' : 's'} of history, oldest {ordered[ordered.length - 1]?.week_commencing} to latest {latest?.week_commencing}.
      </div>

      {latest && (
        <div className="bg-navy/5 border border-navy/20 rounded-lg p-3 mb-4">
          <div className="flex items-center justify-between mb-1">
            <span className="text-[8px] font-bold text-navy uppercase tracking-wide">Latest — week of {formatDate(latest.week_commencing)}</span>
            {latest.status && <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded ${LEGACY_STATUS_COLOR[latest.status] || 'bg-gray-100 text-gray-600'}`}>{latest.status}</span>}
          </div>
          <div className="grid grid-cols-2 gap-2 text-[9px] text-gray-600 mt-1">
            {latest.forecast_completion_date && <div><span className="font-semibold text-gray-400">Forecast:</span> {formatDate(latest.forecast_completion_date)}</div>}
            {latest.end_date && <div><span className="font-semibold text-gray-400">End date:</span> {formatDate(latest.end_date)}</div>}
          </div>
          {latest.achievements && <div className="text-[9px] text-gray-700 mt-2"><span className="font-semibold">Achievements: </span>{latest.achievements}</div>}
          {latest.planned_next && <div className="text-[9px] text-gray-700 mt-1"><span className="font-semibold">Planned next: </span>{latest.planned_next}</div>}
          {(latest.dependency_delay || latest.dependency_comment) && (
            <div className="text-[9px] text-amber-700 mt-1"><span className="font-semibold">Dependency: </span>{[latest.dependency_delay, latest.dependency_comment].filter(Boolean).join(' — ')}</div>
          )}
          {latest.risks && <div className="text-[9px] text-red-600 mt-1"><span className="font-semibold">Risks: </span>{latest.risks}</div>}
          {latest.rr_actions && <div className="text-[9px] text-gray-700 mt-1"><span className="font-semibold">RR actions: </span>{latest.rr_actions}</div>}
          {latest.rr_comment && <div className="text-[9px] text-gray-700 mt-1"><span className="font-semibold">RR comment: </span>{latest.rr_comment}</div>}
        </div>
      )}

      <div className="text-[8px] font-bold text-gray-400 uppercase mb-1">Full timeline (newest first)</div>
      <div className="overflow-x-auto border border-gray-200 rounded">
        <table className="w-full min-w-[640px]">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-2 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold">Week</th>
              <th className="px-2 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold">Status</th>
              <th className="px-2 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold">Achievements</th>
              <th className="px-2 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold">Planned next</th>
              <th className="px-2 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold">Dependency / Risk</th>
              <th className="px-2 py-1.5 text-left text-[8px] uppercase text-gray-500 font-bold">RR actions / comment</th>
            </tr>
          </thead>
          <tbody>
            {ordered.map(r => (
              <tr key={r.id} className="border-b border-gray-100 align-top">
                <td className="px-2 py-1.5 text-[9px] font-semibold text-navy whitespace-nowrap">{formatDate(r.week_commencing)}</td>
                <td className="px-2 py-1.5">
                  {r.status && <span className={`text-[8px] font-bold px-1.5 py-0.5 rounded whitespace-nowrap ${LEGACY_STATUS_COLOR[r.status] || 'bg-gray-100 text-gray-600'}`}>{r.status}</span>}
                </td>
                <td className="px-2 py-1.5 text-[9px] text-gray-600 max-w-[220px]">{r.achievements || '—'}</td>
                <td className="px-2 py-1.5 text-[9px] text-gray-600 max-w-[220px]">{r.planned_next || '—'}</td>
                <td className="px-2 py-1.5 text-[9px] text-gray-500 max-w-[180px]">
                  {[r.dependency_delay, r.dependency_comment].filter(Boolean).join(' — ') || r.risks || '—'}
                </td>
                <td className="px-2 py-1.5 text-[9px] text-gray-500 max-w-[220px]">
                  {[r.rr_actions, r.rr_comment].filter(Boolean).join(' — ') || '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Modal>
  );
}

// ── PROCUREMENT PO STATUS BADGE ──────────────────────────────────────────────
const PO_STATUS_CFG = {
  draft:          { label: 'Draft',      cls: 'bg-gray-100 text-gray-500' },
  issued:         { label: 'Issued',     cls: 'bg-blue-100 text-blue-700' },
  acknowledged:   { label: "Ack'd",     cls: 'bg-indigo-100 text-indigo-700' },
  part_delivered: { label: 'Part Del',  cls: 'bg-amber-100 text-amber-700' },
  delivered:      { label: 'Delivered', cls: 'bg-emerald-100 text-emerald-700' },
  closed:         { label: 'Closed',    cls: 'bg-violet-100 text-violet-700' },
  cancelled:      { label: 'Cancelled', cls: 'bg-red-100 text-red-600' },
};
function ProcurementPOStatusBadge({ status }) {
  const { label, cls } = PO_STATUS_CFG[status] || PO_STATUS_CFG.draft;
  return <span className={`text-[7px] font-bold px-1 py-0 rounded ${cls}`}>{label}</span>;
}

// ── PO QUICK VIEW MODAL ───────────────────────────────────────────────────────
function POQuickViewModal({ poId, onClose, onNavigate }) {
  // Open to staff for delivery; what the PO cost is the Purchase Orders page or
  // sow:see-financials, which is when the API sends the prices.
  const seesPrices = canAccess('pos') || canAccess('sow:see-financials');
  const [po, setPo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get(`purchase-orders/${poId}`)
      .then(d => { setPo(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [poId]);

  const total = (po?.lines || []).reduce((s, l) => s + (l.line_total || 0), 0);

  return (
    <Modal title="Purchase Order" onClose={onClose} wide footer={
      <>
        <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Close</button>
        {canAccess('pos') && (
        <button onClick={() => { onClose(); onNavigate(poId); }} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded hover:bg-navy/80">
          Open Full PO →
        </button>
        )}
      </>
    }>
      {loading ? (
        <div className="py-8 text-center text-[10px] text-gray-400">Loading…</div>
      ) : !po ? (
        <div className="py-8 text-center text-[10px] text-red-500">Could not load PO.</div>
      ) : (
        <div className="space-y-3">
          <div className="flex items-center gap-3">
            <span className="text-[15px] font-extrabold tracking-wide text-navy">{po.po_number}</span>
            <ProcurementPOStatusBadge status={po.status} />
          </div>
          <div className="grid grid-cols-2 gap-2 text-[9px]">
            {[
              ['Supplier', po.supplier_name || '—'],
              ['SOW', po.sow_reference || '—'],
              ['Order Date', po.order_date || '—'],
              ['Exp. Delivery', po.expected_delivery || '—'],
            ].map(([label, value]) => (
              <div key={label} className="bg-gray-50 rounded px-2 py-1.5">
                <div className="text-[7px] font-bold uppercase text-gray-400 mb-0.5">{label}</div>
                <div className="font-semibold text-navy">{value}</div>
              </div>
            ))}
          </div>
          {(po.lines || []).length > 0 && (
            <div>
              <div className="text-[8px] font-bold uppercase text-gray-400 mb-1">Order Lines ({po.lines.length})</div>
              <div className="border border-gray-200 rounded divide-y divide-gray-100 max-h-48 overflow-y-auto">
                {po.lines.map(l => (
                  <div key={l.id} className="flex items-center justify-between px-2 py-1.5 text-[9px]">
                    <div className="min-w-0">
                      <div className="font-semibold text-navy truncate">{l.description}</div>
                      {l.part_number && <div className="text-[7px] text-gray-400 font-mono">{l.part_number}</div>}
                    </div>
                    <div className="ml-3 flex-shrink-0 text-right">
                      <div className="text-gray-500">×{l.quantity}</div>
                      {seesPrices && <div className="font-semibold">{formatMoney(l.line_total)}</div>}
                    </div>
                  </div>
                ))}
              </div>
              {seesPrices && (
              <div className="flex justify-between text-[9px] font-bold text-navy px-1 pt-1.5">
                <span>Total (ex VAT)</span>
                <span>{formatMoney(total)}</span>
              </div>
              )}
            </div>
          )}
          {po.notes && (
            <div className="px-2 py-1.5 bg-amber-50 border border-amber-100 rounded text-[8px] text-amber-700">
              <span className="font-bold mr-1">Notes:</span>{po.notes}
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}

// ── CREATE PO FROM HARDWARE MODAL ─────────────────────────────────────────────
function CreatePOFromHardwareModal({ sow, lines, onClose, onCreated }) {
  const { toast } = useToast();
  const [suppliers,        setSuppliers]        = useState([]);
  const [addresses,        setAddresses]        = useState([]);
  const [supplierId,       setSupplierId]       = useState('');
  const [addressId,        setAddressId]        = useState('');
  const [expectedDelivery, setExpectedDelivery] = useState('');
  const [notes,            setNotes]            = useState('');
  const [saving,           setSaving]           = useState(false);
  const [gate,             setGate]             = useState(null);
  const [qaReason,         setQaReason]         = useState('');
  const [error,            setError]            = useState('');
  const [bundleData,       setBundleData]       = useState({}); // bundle_id → items[]

  useEffect(() => {
    const bundleIds = [...new Set(lines.filter(l => l.bundle_id).map(l => l.bundle_id))];
    Promise.all([
      api.get('suppliers').catch(() => []),
      api.get('delivery-addresses').catch(() => []),
      ...bundleIds.map(id => api.get(`product-bundles/${id}`).catch(() => null)),
    ]).then(([s, a, ...bundles]) => {
      setSuppliers((s || []).filter(x => x.is_active));
      setAddresses(a || []);
      const bd = {};
      bundleIds.forEach((id, i) => { if (bundles[i]?.items) bd[id] = bundles[i].items; });
      setBundleData(bd);
    });
  }, []);

  const sel = suppliers.find(s => s.id === supplierId);
  const qaReq = sel?.qa_required || 0, qaVer = sel?.qa_verified || 0, qaExp = sel?.qa_expired || 0;
  const nonCompliant = !!sel && qaReq > 0 && (qaVer < qaReq || qaExp > 0);
  const gated = nonCompliant || !!gate;
  const needReason = gated && !qaReason.trim();

  const handleSubmit = async () => {
    setSaving(true); setError('');
    try {
      const po = await api.post('purchase-orders', {
        supplier_id: supplierId || null,
        sow_id: sow.id,
        delivery_address_id: addressId || null,
        expected_delivery: expectedDelivery || null,
        notes: notes || null,
        qa_override_reason: qaReason.trim() || undefined,
      });
      let sortOrder = 1;
      for (const line of lines) {
        if (line.bundle_id) {
          const bundle = await api.get(`product-bundles/${line.bundle_id}`).catch(() => null);
          const bundleItems = (bundle?.items || []).filter(it => !it.is_optional);
          const storedQtys = (() => { try { return JSON.parse(line.bundle_items_json || '[]'); } catch { return []; } })();
          if (bundleItems.length > 0) {
            for (const item of bundleItems) {
              const storedEntry = storedQtys.find(s => s.product_catalog_id === item.product_catalog_id);
              const perBundleQty = storedEntry ? (parseInt(storedEntry.quantity, 10) || 1) : (parseInt(item.quantity, 10) || 1);
              const qty = perBundleQty * (parseInt(line.quantity, 10) || 1);
              await api.post(`purchase-orders/${po.id}/lines`, {
                sow_hardware_line_id: line.id,
                description: item.description,
                part_number: item.code || item.manufacturer_part_number || null,
                quantity: qty,
                unit_price: item.min_price ?? item.unit_cost ?? 0,
                product_catalog_id: item.product_catalog_id || null,
                sort_order: sortOrder++,
              });
            }
          } else {
            await api.post(`purchase-orders/${po.id}/lines`, {
              sow_hardware_line_id: line.id,
              description: line.item_description,
              part_number: line.part_number || null,
              quantity: line.quantity,
              unit_price: line.unit_cost,
              sort_order: sortOrder++,
            });
          }
        } else {
          await api.post(`purchase-orders/${po.id}/lines`, {
            sow_hardware_line_id: line.id,
            description: line.item_description,
            part_number: line.part_number || null,
            quantity: line.quantity,
            unit_price: line.unit_cost,
            sort_order: sortOrder++,
          });
        }
      }
      for (const line of lines) {
        await api.put(`sows/${sow.id}/hardware-lines/${line.id}`, {
          delivery_status: 'ordered',
          procurement_po_id: po.id,
        });
      }
      await onCreated(po);
    } catch (e) {
      if (e.data?.qa_gate) setGate(e.data);
      else setError(e.message || 'Failed to create PO');
    } finally {
      setSaving(false);
    }
  };

  const totalCost = lines.reduce((s, l) => s + (l.line_cost || 0), 0);

  return (
    <Modal title="Create Purchase Order" onClose={onClose} footer={
      <>
        <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
        <button onClick={handleSubmit} disabled={saving || needReason} className={`px-3 py-1 text-[10px] font-semibold text-white rounded disabled:opacity-40 ${gated ? 'bg-amber-600' : 'bg-navy'}`}>
          {saving ? 'Creating…' : gated ? `Raise PO anyway · ${lines.length} line${lines.length !== 1 ? 's' : ''}` : `Create PO · ${lines.length} line${lines.length !== 1 ? 's' : ''}`}
        </button>
      </>
    }>
      <div className="space-y-3" style={{ minWidth: 360 }}>
        <div>
          <div className="text-[8px] font-bold text-gray-500 uppercase mb-1">Items to order ({lines.length})</div>
          <div className="bg-gray-50 border border-gray-200 rounded divide-y divide-gray-100 max-h-48 overflow-y-auto">
            {lines.map(line => {
              const items = line.bundle_id ? (bundleData[line.bundle_id] || []).filter(i => !i.is_optional) : null;
              if (items && items.length > 0) {
                return (
                  <div key={line.id} className="px-2 py-1">
                    <div className="flex justify-between items-center mb-0.5">
                      <span className="text-[9px] font-semibold truncate max-w-[200px] text-violet-700">{line.item_description}</span>
                      <span className="text-[8px] text-violet-500 ml-2 whitespace-nowrap">×{line.quantity} bundle</span>
                    </div>
                    {items.map(item => {
                      const sq = (() => { try { return JSON.parse(line.bundle_items_json || '[]'); } catch { return []; } })().find(s => s.product_catalog_id === item.product_catalog_id);
                      const perBundleQty = sq ? (parseInt(sq.quantity, 10) || 1) : (parseInt(item.quantity, 10) || 1);
                      const qty = perBundleQty * (parseInt(line.quantity, 10) || 1);
                      return (
                        <div key={item.id} className="flex justify-between items-center pl-3 py-0.5">
                          <span className="text-[8px] text-gray-600 truncate max-w-[180px]">
                            <span className="font-mono text-gray-400 mr-1">{item.code}</span>{item.description}
                          </span>
                          <span className="text-[8px] text-gray-500 ml-2 whitespace-nowrap font-semibold">×{qty}</span>
                        </div>
                      );
                    })}
                  </div>
                );
              }
              return (
                <div key={line.id} className="flex justify-between items-center px-2 py-1">
                  <span className="text-[9px] font-semibold truncate max-w-[200px]">{line.item_description}</span>
                  <span className="text-[9px] text-gray-500 ml-2 whitespace-nowrap">×{line.quantity}{line.unit_cost !== undefined && <> · {formatMoney(line.unit_cost, 2)}</>}</span>
                </div>
              );
            })}
          </div>
          {/* Trade cost comes only to holders of sow:see-financials and trade-cost:see. */}
          {lines.some(l => l.unit_cost !== undefined) && (
          <div className="flex justify-between text-[8px] px-1 pt-1 font-semibold text-navy">
            <span>Total cost (ex VAT)</span>
            <span>{formatMoney(totalCost)}</span>
          </div>
          )}
        </div>

        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Supplier</label>
          <select value={supplierId} onChange={e => setSupplierId(e.target.value)} className="form-select text-[10px]">
            <option value="">— Select supplier —</option>
            {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Delivery Address</label>
          <select value={addressId} onChange={e => setAddressId(e.target.value)} className="form-select text-[10px]">
            <option value="">— None —</option>
            {addresses.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Expected Delivery</label>
          <input type="date" value={expectedDelivery} onChange={e => setExpectedDelivery(e.target.value)} className="form-input text-[10px]" />
        </div>

        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Notes</label>
          <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={2} className="form-input text-[10px] resize-none w-full" placeholder="Optional notes for this PO…" />
        </div>
        {gated && (
          <div className="p-2.5 bg-amber-50 border border-amber-300 rounded">
            <div className="text-[10px] font-bold text-amber-800">⚠ Supplier QA incomplete</div>
            <div className="text-[9px] text-amber-700 mt-0.5">
              {gate?.outstanding?.length
                ? <>Outstanding: {gate.outstanding.join(', ')}.</>
                : <>{qaVer}/{qaReq} required QA steps verified{qaExp > 0 ? `, ${qaExp} expired` : ''}.</>}
              {' '}You can still raise this PO, but must record why.
            </div>
            <label className="block text-[8px] font-bold text-amber-700 uppercase mt-2 mb-0.5">Acknowledgement reason *</label>
            <textarea rows={2} className="form-input text-[10px] resize-none w-full" placeholder="e.g. Urgent requirement; insurance renewal in progress." value={qaReason} onChange={e => setQaReason(e.target.value)} />
          </div>
        )}
        {error && <div className="text-[9px] text-red-600">{error}</div>}
      </div>
    </Modal>
  );
}

// ── ADD HARDWARE MODAL ────────────────────────────────────────────────────────
const HW_STATUS_META = {
  not_ordered: { label: 'Not Ordered',  cls: 'bg-gray-100 text-gray-600',        needsReason: false },
  ordered:     { label: 'Ordered',       cls: 'bg-blue-100 text-blue-700',        needsReason: true,  hint: 'Provide a PO reference or reason for marking as ordered without one.' },
  in_stock:    { label: 'In Stock',      cls: 'bg-emerald-100 text-emerald-700',  needsReason: true,  hint: 'Tip: allocate directly from inventory for full traceability.' },
  in_transit:  { label: 'In Transit',    cls: 'bg-cyan-100 text-cyan-700',        needsReason: true,  hint: 'Confirm the item is physically in transit.' },
  delivered:   { label: 'Delivered',     cls: 'bg-violet-100 text-violet-700',    needsReason: true,  hint: 'Confirm delivery has been received.' },
  installed:   { label: 'Installed',     cls: 'bg-navy text-white',               needsReason: true,  hint: 'Confirm the item has been installed on site.' },
};

function ApplyTemplateModal({ sow, onClose, onApplied }) {
  const { toast } = useToast();
  const [templates, setTemplates] = useState([]);
  const [selected, setSelected]   = useState(null);  // template with revision
  const [applying, setApplying]   = useState(false);

  useEffect(() => {
    api.get(`sow-templates/for-type/${encodeURIComponent(sow.type || '')}`)
      .then(t => { if (t) { setTemplates([t]); setSelected(t); } else setTemplates([]); })
      .catch(() => setTemplates([]));
    api.get('sow-templates').then(all => setTemplates(all || [])).catch(() => {});
  }, [sow.type]);

  const handle = async () => {
    if (!selected?.revision?.id) return;
    setApplying(true);
    try {
      const result = await api.post(`sows/${sow.id}/apply-template`, { revision_id: selected.revision.id });
      const { created } = result;
      toast.success(`Template applied! ${created.milestones} milestones, ${created.resource_lines} resource lines, ${created.project_tasks} project tasks, ${created.hardware} hardware items.`);
      onApplied();
    } catch (e) { toast.error('Failed: ' + e.message); }
    setApplying(false);
  };

  const grouped = templates.reduce((g, t) => { (g[t.sow_type] = g[t.sow_type] || []).push(t); return g; }, {});

  return (
    <Modal title="Apply SOW Template" onClose={onClose} footer={
      <>
        <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
        <button onClick={handle} disabled={applying || !selected?.revision} className="px-3 py-1 text-[10px] font-semibold bg-purple-600 text-white rounded disabled:opacity-40">
          {applying ? 'Applying…' : 'Apply Template'}
        </button>
      </>
    }>
      <div className="space-y-3">
        {sow.template_applied_at && (
          <div className="p-2 bg-amber-50 border border-amber-200 rounded text-[8px] text-amber-800">
            ⚠ A template was already applied on {new Date(sow.template_applied_at).toLocaleDateString('en-GB')}. Applying again will add additional milestones, tasks, and resources — it does not replace existing ones.
          </div>
        )}
        <div className="text-[8px] text-gray-500 bg-blue-50 border border-blue-100 rounded p-2">
          SOW type: <strong>{sow.type || 'Not set'}</strong>. Templates matching this type are shown first. You can select any template below.
        </div>
        <div className="space-y-1 max-h-64 overflow-y-auto">
          {templates.length === 0 && <div className="text-[9px] text-gray-400 text-center py-4">No templates found. Create one in Admin → SOW Templates.</div>}
          {Object.entries(grouped).map(([type, tpls]) => (
            <div key={type}>
              <div className="text-[7px] font-bold uppercase text-gray-400 px-1 py-0.5">{type}</div>
              {tpls.filter(t => t.is_active).map(t => (
                <div key={t.id} onClick={() => setSelected(t)} className={`px-3 py-2 border rounded cursor-pointer hover:bg-purple-50 ${selected?.id === t.id ? 'border-purple-400 bg-purple-50' : 'border-gray-200'}`}>
                  <div className="flex items-center justify-between">
                    <div className="text-[10px] font-semibold">{t.name}</div>
                    <span className="text-[7px] text-gray-400">v{t.latest_revision || '—'}</span>
                  </div>
                  {t.description && <div className="text-[8px] text-gray-500 mt-0.5">{t.description}</div>}
                  {t.latest_change_notes && <div className="text-[7.5px] text-gray-400 italic">{t.latest_change_notes}</div>}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}

// Interactive multi-step AI SOW generator.
// Step 1: Pick SOR (if multiple) and confirm intent.
// Step 2: Show progress while we call /sow-ai-plan to fetch questions + recommendations.
// Step 3: User answers questions and accepts/edits recommendations.
// Step 4: Show progress while we call /generate-sow-doc to apply + build the .docx.
// Step 5: Done — show download link.
function GenerateSOWModal({ sow, documents, onClose, onGenerated, sowId }) {
  const [step, setStep] = useState('intro');     // intro | planning | review | generating | done | error
  const [sorDocId, setSorDocId] = useState('');
  const [plan, setPlan] = useState(null);        // result of /sow-ai-plan
  const [planMeta, setPlanMeta] = useState(null);// { template, sow_type_label, sor }
  const [answers, setAnswers] = useState({});    // { qId: text }
  const [pickResources, setPickResources] = useState(true);
  const [pickHardware, setPickHardware]   = useState(true);
  const [pickMilestones, setPickMilestones] = useState(true);
  const [editResources, setEditResources] = useState([]);
  const [editHardware, setEditHardware]   = useState([]);
  const [editMilestones, setEditMilestones] = useState([]);
  const [progress, setProgress] = useState([]);   // [{ label, state: 'pending'|'active'|'done' }]
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);     // { document_id, url, filename }
  const [elapsedSecs, setElapsedSecs] = useState(0);
  const timerRef = useRef(null);

  const startTimer = () => {
    setElapsedSecs(0);
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setElapsedSecs(s => s + 1), 1000);
  };
  const stopTimer = () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };
  useEffect(() => () => stopTimer(), []);

  const sorDocs = documents.filter(d => d.document_type === 'sor' && d.url);
  useEffect(() => { if (sorDocs.length === 1) setSorDocId(sorDocs[0].id); }, [documents]);

  const setStepProgress = (steps, idx) => {
    setProgress(steps.map((label, i) => ({ label, state: i < idx ? 'done' : i === idx ? 'active' : 'pending' })));
  };

  // Stage 1 — fetch the plan
  const startPlanning = async () => {
    setStep('planning'); setError('');
    startTimer();
    const steps = ['Reading SOR', 'Asking Claude for questions & recommendations'];
    setStepProgress(steps, 0);
    try {
      // Cosmetic step pacing — backend does these in one call but we want the user to see motion
      setTimeout(() => setStepProgress(steps, 1), 400);
      setTimeout(() => setStepProgress(steps, 2), 900);
      const res = await api.post(`sows/${sowId}/sow-ai-plan`, {
        sor_document_id: sorDocId || null,
      });
      stopTimer();
      setPlan(res.plan);
      setPlanMeta({ template: res.template, sow_type: res.sow_type, sow_type_label: res.sow_type_label, sor: res.sor, diagnostics: res.diagnostics });
      // Pre-populate answers with suggested values
      const a = {};
      (res.plan?.questions || []).forEach(q => { if (q.suggested_answer) a[q.id] = q.suggested_answer; });
      setAnswers(a);
      setEditResources(res.plan?.recommended_resources || []);
      setEditHardware(res.plan?.recommended_hardware || []);
      setEditMilestones(res.plan?.recommended_milestones || []);
      setStepProgress(steps, steps.length);
      setTimeout(() => setStep('review'), 200);
    } catch (e) {
      stopTimer();
      setError(e.message);
      setStep('error');
    }
  };

  // Stage 2 — generate the document
  const startGenerating = async () => {
    setStep('generating'); setError('');
    startTimer();
    const steps = ['Applying recommendations to SOW', 'Asking Claude to draft sections', 'Generating document', 'Saving document'];
    setStepProgress(steps, 0);
    try {
      setTimeout(() => setStepProgress(steps, 1), 500);
      setTimeout(() => setStepProgress(steps, 2), 12000);
      setTimeout(() => setStepProgress(steps, 3), 18000);
      const res = await api.post(`sows/${sowId}/generate-sow-doc`, {
        sor_document_id: sorDocId || null,
        answers,
        accepted: { resources: editResources, hardware: editHardware, milestones: editMilestones },
        apply: { resources: pickResources, hardware: pickHardware, milestones: pickMilestones },
      });
      stopTimer();
      setResult(res);
      setStepProgress(steps, steps.length);
      setStep('done');
    } catch (e) {
      stopTimer();
      setError(e.message);
      setStep('error');
    }
  };

  const finish = () => { onGenerated?.(); onClose(); };

  return (
    <Modal title="Generate SOW with AI" onClose={step === 'planning' || step === 'generating' ? () => {} : onClose} wide
      footer={
        step === 'intro' ? (
          <>
            <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
            <button onClick={startPlanning} disabled={sorDocs.length === 0 && !sow.detail} className="px-3 py-1 text-[10px] font-semibold bg-purple-600 text-white rounded disabled:opacity-40">✦ Start AI planning</button>
          </>
        ) : step === 'review' ? (
          <>
            <button onClick={() => setStep('intro')} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Back</button>
            <button onClick={startGenerating} className="px-3 py-1 text-[10px] font-semibold bg-purple-600 text-white rounded">✦ Generate SOW document</button>
          </>
        ) : step === 'done' ? (
          <button onClick={finish} className="px-3 py-1 text-[10px] font-semibold bg-emerald-600 text-white rounded">Done</button>
        ) : step === 'error' ? (
          <>
            <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Close</button>
            <button onClick={() => setStep('intro')} className="px-3 py-1 text-[10px] font-semibold bg-purple-600 text-white rounded">Try again</button>
          </>
        ) : null
      }>
      {step === 'intro' && (
        <div className="space-y-3">
          <div className="p-2 bg-purple-50 border border-purple-200 rounded text-[8px] text-purple-800 space-y-1">
            <div className="font-bold">Claude AI will draft a Statement of Work for you</div>
            <div>It reads the SOR, asks you a few clarifying questions, suggests resources / hardware / milestones based on the engagement, then produces a <strong>.docx</strong> you can open in Word — using your CoWork reference documents as style and structure guides.</div>
          </div>
          <div className="grid grid-cols-2 gap-2 text-[9px]">
            <InfoRow label="SOW type" value={sow.type || '—'} />
            <InfoRow label="Client" value={sow.client_name || '—'} />
            <InfoRow label="Description" value={sow.detail || '—'} full />
          </div>

          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Statement of Requirements (SOR)</label>
            {sorDocs.length === 0 ? (
              <div className="p-2 bg-red-50 border border-red-200 rounded text-[8px] text-red-700">No SOR uploaded. Add a document of type <strong>sor</strong> on this tab for best results.</div>
            ) : (
              <select className="form-select text-[10px]" value={sorDocId} onChange={e => setSorDocId(e.target.value)}>
                <option value="">None — work from SOW description only</option>
                {sorDocs.map(d => <option key={d.id} value={d.id}>{d.title}</option>)}
              </select>
            )}
            <div className="text-[7.5px] text-gray-400 mt-1">Text/Markdown SORs give the richest context. PDF/SharePoint links are read by reference only.</div>
          </div>
        </div>
      )}

      {(step === 'planning' || step === 'generating') && (
        <ProgressList
          steps={progress}
          title={step === 'planning' ? 'Building plan…' : 'Generating SOW document…'}
          subtitle={step === 'generating' ? 'Claude AI is drafting and building your document — typically takes 30–60 seconds.' : null}
          elapsedSecs={elapsedSecs}
        />
      )}

      {step === 'review' && plan && (
        <div className="space-y-3 max-h-[60vh] overflow-y-auto">
          <div className="p-2 bg-purple-50 border border-purple-200 rounded text-[8px] text-purple-800">
            <div className="font-bold mb-0.5">AI's understanding</div>
            <div>{plan.summary}</div>
          </div>

          {(plan.questions || []).length > 0 && (
            <div>
              <div className="text-[8px] font-bold uppercase text-gray-500 mb-1">Clarifying Questions</div>
              <div className="space-y-2">
                {plan.questions.map(q => (
                  <div key={q.id} className="border border-gray-200 rounded p-2 bg-white">
                    <div className="text-[10px] font-semibold text-navy">{q.question}</div>
                    {q.why && <div className="text-[7.5px] text-gray-400 italic mb-1">{q.why}</div>}
                    <textarea
                      className="form-input text-[9px] w-full"
                      rows={2}
                      value={answers[q.id] || ''}
                      onChange={e => setAnswers(p => ({ ...p, [q.id]: e.target.value }))}
                      placeholder={q.suggested_answer ? `Suggested: ${q.suggested_answer}` : ''}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          <RecommendationSection
            label="Recommended Resources"
            items={editResources}
            checked={pickResources}
            onCheck={setPickResources}
            onChange={setEditResources}
            renderItem={(it, i, update, remove) => (
              <div className="grid grid-cols-12 gap-1 items-center">
                <input className="form-input text-[8px] col-span-3" value={it.band_name || ''} onChange={e => update({ ...it, band_name: e.target.value })} placeholder="Band name" />
                <input className="form-input text-[8px] col-span-2" value={it.band_code || ''} onChange={e => update({ ...it, band_code: e.target.value })} placeholder="Code" />
                <input className="form-input text-[8px] col-span-1" type="number" value={it.days || 0} onChange={e => update({ ...it, days: Number(e.target.value) })} placeholder="Days" />
                <input className="form-input text-[8px] col-span-2" type="number" value={it.day_rate || 0} onChange={e => update({ ...it, day_rate: Number(e.target.value) })} placeholder="Rate" />
                <input className="form-input text-[8px] col-span-3" value={it.work_package || ''} onChange={e => update({ ...it, work_package: e.target.value })} placeholder="Work package" />
                <button onClick={remove} className="col-span-1 text-red-400 hover:text-red-600 text-[12px]">×</button>
              </div>
            )}
          />

          <RecommendationSection
            label="Recommended Hardware"
            items={editHardware}
            checked={pickHardware}
            onCheck={setPickHardware}
            onChange={setEditHardware}
            renderItem={(it, i, update, remove) => (
              <div className="grid grid-cols-12 gap-1 items-center">
                <input className="form-input text-[8px] col-span-7" value={it.description || ''} onChange={e => update({ ...it, description: e.target.value })} placeholder="Description" />
                <input className="form-input text-[8px] col-span-1" type="number" value={it.quantity || 1} onChange={e => update({ ...it, quantity: Number(e.target.value) })} />
                <input className="form-input text-[8px] col-span-3" value={it.category || ''} onChange={e => update({ ...it, category: e.target.value })} placeholder="Category" />
                <button onClick={remove} className="col-span-1 text-red-400 hover:text-red-600 text-[12px]">×</button>
              </div>
            )}
          />

          <RecommendationSection
            label="Recommended Milestones"
            items={editMilestones}
            checked={pickMilestones}
            onCheck={setPickMilestones}
            onChange={setEditMilestones}
            renderItem={(it, i, update, remove) => (
              <div className="grid grid-cols-12 gap-1 items-center">
                <input className="form-input text-[8px] col-span-2" value={it.milestone_code || ''} onChange={e => update({ ...it, milestone_code: e.target.value })} placeholder="Code" />
                <input className="form-input text-[8px] col-span-9" value={it.description || ''} onChange={e => update({ ...it, description: e.target.value })} placeholder="Description" />
                <button onClick={remove} className="col-span-1 text-red-400 hover:text-red-600 text-[12px]">×</button>
              </div>
            )}
          />

          {(plan.risks?.length || plan.assumptions?.length) ? (
            <div className="grid grid-cols-2 gap-2 text-[8px]">
              {plan.risks?.length ? (
                <div className="bg-amber-50 border border-amber-200 rounded p-2">
                  <div className="font-bold uppercase text-amber-700 mb-1">Risks the AI noted</div>
                  <ul className="list-disc pl-3 text-amber-800 space-y-0.5">{plan.risks.map((r, i) => <li key={i}>{r}</li>)}</ul>
                </div>
              ) : null}
              {plan.assumptions?.length ? (
                <div className="bg-blue-50 border border-blue-200 rounded p-2">
                  <div className="font-bold uppercase text-blue-700 mb-1">Assumptions</div>
                  <ul className="list-disc pl-3 text-blue-800 space-y-0.5">{plan.assumptions.map((a, i) => <li key={i}>{a}</li>)}</ul>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      )}

      {step === 'done' && result && (
        <FeedbackPanel result={result} sowId={sowId} sowType={sow.type} />
      )}

      {step === 'error' && (
        <div className="p-3 bg-red-50 border border-red-200 rounded text-[9px] text-red-700">
          <div className="font-bold mb-1">Generation failed</div>
          <div className="whitespace-pre-wrap">{error}</div>
        </div>
      )}
    </Modal>
  );
}

function TemplateMatchDiagnostics({ diagnostics }) {
  if (!diagnostics) return null;
  const d = diagnostics;
  return (
    <div className="text-[8px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2 text-left space-y-0.5">
      <div className="font-bold">Why no Word template was used</div>
      <div>SOW type stored on this SOW: <code>{d.sow_type || '(empty)'}</code></div>
      <div>Template matched: <strong>{d.template_matched ? 'Yes' : 'No'}</strong>{d.template_id ? ` (id ${d.template_id.slice(0, 8)})` : ''}</div>
      {d.revision_id && <div>Revision picked: v{d.revision} ({d.revision_id.slice(0, 8)})</div>}
      <div>Stored Word file URL: <code>{d.template_file_url || '(none)'}</code></div>
      {d.template_file_url && !d.file_url_format_ok && (
        <div className="text-red-700 font-bold">File URL doesn't start with /api/uploads/ — re-upload the .docx in Admin → SOW Templates.</div>
      )}
      <div className="pt-1 italic">For a full dump: open <code>/api/sow-template-diagnostics?sow_type={encodeURIComponent(d.sow_type || '')}</code> in your browser (admins only).</div>
    </div>
  );
}

function FeedbackPanel({ result, sowId, sowType }) {
  const { toast } = useToast();
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [title, setTitle] = useState('');
  const [savingFb, setSavingFb] = useState(false);
  const [saved, setSaved] = useState(false);

  const submit = async () => {
    if (!feedback.trim()) return;
    setSavingFb(true);
    try {
      await api.post('ai-knowledge', {
        category: 'sow_generation',
        title: title || `Feedback from SOW ${sowId.slice(0, 8)}`,
        content: feedback + (sowType ? `\n\n_(captured from a ${sowType} SOW)_` : ''),
        source: 'sow_feedback',
        source_ref: sowId,
      });
      setSaved(true);
      setTimeout(() => { setShowFeedback(false); setFeedback(''); setTitle(''); setSaved(false); }, 1500);
    } catch (e) { toast.error('Failed: ' + e.message); }
    setSavingFb(false);
  };

  return (
    <div className="space-y-3 py-2">
      <div className="text-center space-y-2">
        <div className="text-emerald-600 text-[40px]">✓</div>
        <div className="text-[12px] font-bold text-navy">SOW document generated</div>
        {result.render_mode === 'template' ? (
          (() => {
            const ph = result.placeholders || {};
            const filled = ph.filled?.length || 0;
            const detected = ph.detected?.length || 0;
            return (
              <div className="text-[8px] text-emerald-700">Filled <strong>{filled}</strong> of <strong>{detected}</strong> placeholders in <strong>{result.template_name}</strong>.</div>
            );
          })()
        ) : result.render_mode === 'template-auto' ? (
          (() => {
            const ph = result.placeholders || {};
            const filled = ph.filled?.length || 0;
            return (
              <div className="p-2 bg-emerald-50 border border-emerald-200 rounded text-[8px] text-emerald-800 text-left">
                <div className="font-bold mb-0.5">Template branding preserved</div>
                <div>Your <strong>{result.template_name}</strong> template was used as-is — the AI automatically detected and filled <strong>{filled}</strong> section(s) while keeping your fonts, colours, logo, headers and page layout intact.</div>
              </div>
            );
          })()
        ) : (
          (() => {
            const detected = result.placeholders?.detected?.length || 0;
            // 'fresh' render mode: docx built from scratch by the AI
            if (result.template_name && detected === 0) {
              return (
                <div className="p-2 bg-amber-50 border border-amber-200 rounded text-[8px] text-amber-800 text-left">
                  <div className="font-bold mb-0.5">Built a full Word document from scratch</div>
                  <div>The matched template <strong>{result.template_name}</strong> had no recognised section headings or <code>{'{placeholder}'}</code> tokens. To use your branded template in future, ensure section headings (e.g. "Executive Summary") use a Word Heading style, or add tokens like <code>{'{executive_summary}'}</code> to the .docx in <em>Admin → SOW Templates</em>.</div>
                </div>
              );
            }
            return (
              <div className="p-2 bg-amber-50 border border-amber-200 rounded text-[8px] text-amber-800 text-left">
                <div className="font-bold mb-0.5">Built from scratch — no branded template found</div>
                <div>No Word template is configured for this SOW type. To preserve your header, footer, fonts and branding: upload a <code>.docx</code> in <em>Admin → SOW Templates</em> for this type, or set a <em>Default SOW Template</em> in <em>Admin → Integrations</em> as a fallback for all types.</div>
              </div>
            );
          })()
        )}

        <div className="flex flex-col items-center gap-1.5 pt-1">
          <a href={result.url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-[10px] font-semibold rounded hover:bg-blue-700">
            ↓ {result.filename}
          </a>
          {result.ai_content_url && (
            <a href={result.ai_content_url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-3 py-1.5 border border-blue-300 text-blue-700 text-[10px] font-semibold rounded hover:bg-blue-50">
              ↓ AI content ({result.ai_content_filename})
            </a>
          )}
        </div>

        {result.placeholders?.detected?.length > 0 && (
          <details className="text-left text-[8px] mt-2">
            <summary className="cursor-pointer text-gray-500">Placeholders detected vs filled</summary>
            <div className="mt-1 grid grid-cols-2 gap-2">
              <div>
                <div className="font-bold text-emerald-700 mb-0.5">Filled ({result.placeholders.filled.length})</div>
                <div className="font-mono text-[7px] text-emerald-700 break-all">{result.placeholders.filled.join(', ') || '—'}</div>
              </div>
              <div>
                <div className="font-bold text-amber-700 mb-0.5">In template but not filled ({result.placeholders.unfilled.length})</div>
                <div className="font-mono text-[7px] text-amber-700 break-all">{result.placeholders.unfilled.join(', ') || '—'}</div>
              </div>
            </div>
          </details>
        )}
      </div>

      {/* Saving goes into AI Knowledge, which the server refuses without that
          page (pen test AD25996, AD16), so only its holders are offered it. */}
      {canAccess('ai-knowledge') && <div className="border-t border-gray-200 pt-3">
        {!showFeedback ? (
          <button onClick={() => setShowFeedback(true)} className="w-full px-3 py-2 text-[10px] font-semibold border border-purple-200 text-purple-700 bg-purple-50 hover:bg-purple-100 rounded">
            ✎ Capture an improvement for next time
          </button>
        ) : saved ? (
          <div className="p-3 bg-emerald-50 border border-emerald-200 rounded text-[9px] text-emerald-700 text-center font-semibold">
            ✓ Saved — the AI will use this from now on.
          </div>
        ) : (
          <div className="space-y-2">
            <div className="text-[8px] text-purple-700 italic">Write what the AI got wrong, or a rule it should always follow. This goes into <strong>AI Knowledge → SOW Generation</strong> and is injected into every future generation.</div>
            <input className="form-input text-[9px]" value={title} onChange={e => setTitle(e.target.value)} placeholder="Short title (optional) e.g. 'Always quote in £ GBP'" />
            <textarea
              className="form-input text-[9px] h-32 resize-y"
              value={feedback}
              onChange={e => setFeedback(e.target.value)}
              placeholder="What should be different next time? Be specific."
              autoFocus
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => { setShowFeedback(false); setFeedback(''); setTitle(''); }} className="px-2 py-1 text-[9px] border border-gray-200 rounded">Cancel</button>
              <button onClick={submit} disabled={savingFb || !feedback.trim()} className="px-2 py-1 text-[9px] font-semibold bg-purple-600 text-white rounded disabled:opacity-40">
                {savingFb ? 'Saving…' : 'Save to AI Knowledge'}
              </button>
            </div>
          </div>
        )}
      </div>}
    </div>
  );
}

function InfoRow({ label, value, full }) {
  return (
    <div className={full ? 'col-span-2' : ''}>
      <div className="text-[7.5px] font-bold uppercase text-gray-400">{label}</div>
      <div className="text-[10px] text-gray-700">{value || '—'}</div>
    </div>
  );
}

function ProgressList({ steps, title, subtitle, elapsedSecs }) {
  return (
    <div className="space-y-2 py-2">
      <div className="text-[10px] font-bold text-navy">{title}</div>
      {subtitle && <div className="text-[8.5px] text-gray-500 italic">{subtitle}</div>}
      <div className="space-y-1.5">
        {steps.map((s, i) => (
          <div key={i} className="flex items-center gap-2">
            {s.state === 'done' ? (
              <span className="w-4 h-4 flex items-center justify-center bg-emerald-500 text-emerald-950 rounded-full text-[10px]">✓</span>
            ) : s.state === 'active' ? (
              <svg className="w-4 h-4 text-purple-500 animate-spin shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"/>
              </svg>
            ) : (
              <span className="w-4 h-4 border-2 border-gray-200 rounded-full shrink-0" />
            )}
            <span className={`text-[9px] ${s.state === 'done' ? 'text-emerald-700' : s.state === 'active' ? 'text-purple-700 font-semibold' : 'text-gray-400'}`}>
              {s.label}
              {s.state === 'active' && elapsedSecs > 0 && (
                <span className="ml-1 text-purple-400 font-normal">{elapsedSecs}s…</span>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function RecommendationSection({ label, items, checked, onCheck, onChange, renderItem }) {
  if (!items || items.length === 0) return null;
  const update = (i, newItem) => onChange(items.map((it, idx) => idx === i ? newItem : it));
  const remove = (i) => onChange(items.filter((_, idx) => idx !== i));
  return (
    <div className="border border-gray-200 rounded p-2">
      <label className="flex items-center gap-2 text-[9px] font-bold text-navy mb-2">
        <input type="checkbox" checked={checked} onChange={e => onCheck(e.target.checked)} />
        Apply {label.toLowerCase()} ({items.length})
      </label>
      {checked && (
        <div className="space-y-1.5">
          {items.map((it, i) => (
            <div key={i} className="bg-gray-50 rounded p-1.5">
              {renderItem(it, i, (n) => update(i, n), () => remove(i))}
              {it.rationale && <div className="text-[7px] text-gray-500 italic mt-0.5">{it.rationale}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function HardwareStatusCell({ line: h, canEdit, onUnallocate, onChangeStatus, onDetachPO }) {
  const meta = HW_STATUS_META[h.delivery_status] || HW_STATUS_META.not_ordered;
  const needsOrder = h.delivery_status === 'not_ordered' && h.product_catalog_id && h.available_in_stock === 0;

  if (h.delivery_status === 'allocated') {
    return (
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-1 flex-wrap">
          <span className="text-[8px] px-1.5 py-0.5 bg-emerald-100 text-emerald-700 rounded font-semibold">Allocated</span>
          {canEdit && (
            <button onClick={onUnallocate} className="px-1.5 py-0.5 text-[7px] font-semibold bg-amber-100 text-amber-700 rounded hover:bg-amber-200 border border-amber-200" title="Return to available stock">Unallocate</button>
          )}
        </div>
      </div>
    );
  }

  if (h.procurement_po_id) {
    const { label: poLabel, cls: poCls } = PO_STATUS_CFG[h.procurement_po_status] || PO_STATUS_CFG.draft;
    const canDetach = canEdit && ['cancelled', 'closed'].includes(h.procurement_po_status);
    return (
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-1 flex-wrap">
          <span className={`text-[8px] px-1.5 py-0.5 rounded font-semibold ${poCls}`}>{poLabel}</span>
          {canDetach && (
            <button onClick={onDetachPO} className="px-1.5 py-0.5 text-[7px] font-semibold bg-gray-100 text-gray-500 rounded hover:bg-red-50 hover:text-red-600 border border-gray-200 hover:border-red-200" title="Clear PO association">Detach PO</button>
          )}
        </div>
        {h.status_override_reason && (
          <span className="text-[7px] text-gray-400 italic truncate max-w-[120px]" title={`Override: ${h.status_override_reason}`}>↳ {h.status_override_reason}</span>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-0.5">
      <div className="flex items-center gap-1 flex-wrap">
        <span className={`text-[8px] px-1.5 py-0.5 rounded font-semibold ${meta.cls}`}>{meta.label}</span>
        {canEdit && (
          <button onClick={() => onChangeStatus(h.id)} className="px-1.5 py-0.5 text-[7px] font-semibold bg-gray-100 text-gray-500 rounded hover:bg-gray-200 border border-gray-200">Change</button>
        )}
      </div>
      {needsOrder && (
        <span className="text-[7px] font-bold text-red-600 flex items-center gap-0.5">
          <span>⚠</span><span>Needs ordering</span>
        </span>
      )}
      {h.status_override_reason && (
        <span className="text-[7px] text-gray-400 italic truncate max-w-[120px]" title={`Override: ${h.status_override_reason}`}>↳ {h.status_override_reason}</span>
      )}
    </div>
  );
}

// ── SERVICE RENEWALS MODAL ────────────────────────────────────────────────────
// A hardware/services line can carry several independently-renewing services
// (e.g. leased lines for multiple buildings, each ending on its own date). This
// lists and maintains those renewals, which feed the Service Renewals screen
// (Commercial > Service Renewals) and the 30-day reminder.
const RENEWAL_STATUS_CLS = { pending: 'bg-amber-100 text-amber-700', renewing: 'bg-emerald-100 text-emerald-700', not_renewing: 'bg-gray-100 text-gray-500' };
function ServiceRenewalsModal({ line, sowId, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: line.item_description || '', service_type: '', start_date: '', end_date: '', notes: '' });
  const setF = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const load = () => {
    setLoading(true);
    api.get('service-renewals', { sow_hardware_line_id: line.id }).then(r => { setRows(r || []); setLoading(false); }).catch(() => setLoading(false));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      await api.post('service-renewals', {
        sow_id: sowId, sow_hardware_line_id: line.id, name: form.name.trim(),
        service_type: form.service_type || null, start_date: form.start_date || null,
        end_date: form.end_date || null, notes: form.notes || null,
      });
      setForm({ name: '', service_type: '', start_date: '', end_date: '', notes: '' });
      setAdding(false); load();
    } finally { setSaving(false); }
  };
  const del = async (id) => { if (!confirm('Delete this renewal?')) return; await api.delete(`service-renewals/${id}`); load(); };

  return (
    <Modal title="Service renewals" onClose={onClose} wide footer={
      <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Done</button>
    }>
      <div className="space-y-3">
        <div className="text-[9px] text-gray-500">{line.item_description} — a line can carry several renewals (e.g. one per building/circuit), each with its own end date and renewal decision.</div>

        {loading ? <div className="text-[9px] text-gray-400">Loading…</div> : rows.length === 0 ? (
          <div className="text-[9px] text-gray-400 border border-dashed border-gray-200 rounded p-3 text-center">No renewals yet for this line.</div>
        ) : (
          <div className="border border-gray-200 rounded overflow-hidden">
            <table className="w-full text-[9px]">
              <thead><tr className="bg-gray-50 border-b border-gray-200">
                {['Name', 'Type', 'Ends', 'Status', ''].map((h, i) => <th key={i} className="px-2 py-1 text-left text-[8px] uppercase text-gray-500 font-bold">{h}</th>)}
              </tr></thead>
              <tbody>
                {rows.map(r => (
                  <tr key={r.id} className="border-b border-gray-50">
                    <td className="px-2 py-1 font-semibold text-navy">{r.name}</td>
                    <td className="px-2 py-1 text-gray-500">{r.service_type || '—'}</td>
                    <td className="px-2 py-1 whitespace-nowrap">{r.end_date ? formatDate(r.end_date) : '—'}</td>
                    <td className="px-2 py-1"><span className={`text-[7px] font-bold px-1.5 py-0.5 rounded uppercase ${RENEWAL_STATUS_CLS[r.renewal_status] || RENEWAL_STATUS_CLS.pending}`}>{(r.renewal_status || 'pending').replace('_', ' ')}</span></td>
                    <td className="px-2 py-1 text-right"><button onClick={() => del(r.id)} className="text-[8px] text-gray-400 hover:text-red-500">Delete</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {adding ? (
          <div className="border border-gray-200 rounded p-2 space-y-2 bg-gray-50/50">
            <div className="grid grid-cols-2 gap-2">
              <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Name *</label><input className="form-input text-[10px]" value={form.name} onChange={e => setF('name', e.target.value)} placeholder="e.g. Leased line — Building A" /></div>
              <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Type</label><input className="form-input text-[10px]" value={form.service_type} onChange={e => setF('service_type', e.target.value)} placeholder="e.g. Leased Line" /></div>
              <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Start</label><input type="date" className="form-input text-[10px]" value={form.start_date} onChange={e => setF('start_date', e.target.value)} /></div>
              <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">End</label><input type="date" className="form-input text-[10px]" value={form.end_date} onChange={e => setF('end_date', e.target.value)} /></div>
            </div>
            <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Notes</label><textarea rows={2} className="form-textarea text-[10px]" value={form.notes} onChange={e => setF('notes', e.target.value)} placeholder="Supplier, circuit ref, cost…" /></div>
            <div className="flex justify-end gap-2">
              <button onClick={() => setAdding(false)} className="px-2 py-1 text-[9px] border border-gray-200 rounded bg-white">Cancel</button>
              <button onClick={add} disabled={saving || !form.name.trim()} className="px-3 py-1 text-[9px] font-semibold bg-navy text-white rounded disabled:opacity-40">{saving ? 'Saving…' : 'Add renewal'}</button>
            </div>
          </div>
        ) : (
          <button onClick={() => setAdding(true)} className="text-[9px] font-semibold text-navy hover:underline">+ Add renewal</button>
        )}
      </div>
    </Modal>
  );
}

function HardwareStatusOverrideModal({ line, onChange, onConfirm, onClose }) {
  const meta = HW_STATUS_META[line.newStatus] || HW_STATUS_META.not_ordered;
  const [saving, setSaving] = useState(false);
  const needsReason = HW_STATUS_META[line.newStatus]?.needsReason;
  const canConfirm = !needsReason || line.reason.trim().length > 0;

  const handleConfirm = async () => {
    if (!canConfirm) return;
    setSaving(true);
    await onConfirm();
    setSaving(false);
  };

  return (
    <Modal title="Change Hardware Status" onClose={onClose} footer={
      <>
        <button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
        <button onClick={handleConfirm} disabled={saving || !canConfirm} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40">
          {saving ? 'Saving…' : 'Confirm'}
        </button>
      </>
    }>
      <div className="space-y-3">
        <div className="p-2 bg-amber-50 border border-amber-200 rounded text-[8px] text-amber-800">
          Status changes are logged with your name and the reason provided. Use inventory allocation for full traceability.
        </div>
        <div>
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">New Status</label>
          <select
            value={line.newStatus}
            onChange={e => onChange('newStatus', e.target.value)}
            className="form-select text-[10px]"
          >
            {Object.entries(HW_STATUS_META).map(([val, m]) => (
              <option key={val} value={val}>{m.label}</option>
            ))}
          </select>
          {meta.hint && <div className="mt-1 text-[7.5px] text-blue-600 italic">{meta.hint}</div>}
        </div>
        {needsReason && (
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Reason / Reference <span className="text-red-500">*</span></label>
            <input
              autoFocus
              className="form-input"
              placeholder={line.newStatus === 'ordered' ? 'e.g. PO-0042 raised on 24 Apr' : 'Explain why you are setting this status…'}
              value={line.reason}
              onChange={e => onChange('reason', e.target.value)}
            />
          </div>
        )}
      </div>
    </Modal>
  );
}

// ── PO PASS-THROUGH MODAL ─────────────────────────────────────────────────────
function AddPOPassThroughModal({ onClose, onSave }) {
  const [pos, setPos] = useState([]);
  const [selectedPo, setSelectedPo] = useState(null);
  const [poSearch, setPoSearch] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [markupType, setMarkupType] = useState('pct'); // 'pct' | 'flat'
  const [markupValue, setMarkupValue] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get('purchase-orders', { limit: 500 })
      .then(r => {
        const all = Array.isArray(r) ? r : (r?.results || r?.pos || []);
        setPos(all.filter(p => !['delivered', 'closed', 'cancelled'].includes(p.status)));
      })
      .catch(() => {});
  }, []);

  const filteredPos = poSearch.trim()
    ? pos.filter(p =>
        p.po_number?.toLowerCase().includes(poSearch.toLowerCase()) ||
        p.supplier_name?.toLowerCase().includes(poSearch.toLowerCase()) ||
        p.sow_reference?.toLowerCase().includes(poSearch.toLowerCase())
      ).slice(0, 8)
    : pos.slice(0, 8);

  const poTotal = selectedPo?.subtotal || 0;
  const markup = parseFloat(markupValue) || 0;
  const sellAmount = markupType === 'pct'
    ? poTotal * (1 + markup / 100)
    : poTotal + markup;
  const passThroughPct = markupType === 'pct'
    ? markup
    : poTotal > 0 ? ((markup / poTotal) * 100) : 0;

  const handleSave = async () => {
    if (!selectedPo || markupValue === '') return;
    setSaving(true);
    try {
      await onSave({
        item_description: `Pass-through: ${selectedPo.po_number}${selectedPo.supplier_name ? ` (${selectedPo.supplier_name})` : ''}`,
        quantity: 1,
        unit_cost: poTotal,
        unit_sell: sellAmount,
        category: 'hardware & services',
        source_po_id: selectedPo.id,
        pass_through_pct: passThroughPct || null,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Add PO Pass-through" onClose={onClose} footer={
      <><button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
        <button onClick={handleSave} disabled={!selectedPo || markupValue === '' || saving} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40">Add to Hardware / Services</button>
      </>
    }>
      <div className="space-y-3">
        <p className="text-[9px] text-gray-500">Select an open purchase order to pass through to this SOW. Set a markup to cover any handling margin.</p>

        {/* PO selector */}
        <div className="relative">
          <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Purchase Order</label>
          {selectedPo ? (
            <div className="flex items-center gap-2 px-2 py-1.5 bg-blue-50 border border-blue-200 rounded text-[10px]">
              <span className="font-mono font-bold text-navy">{selectedPo.po_number}</span>
              {selectedPo.supplier_name && <span className="text-gray-500">{selectedPo.supplier_name}</span>}
              <span className="ml-auto font-semibold text-navy">{formatMoney(selectedPo.subtotal || 0, 2, selectedPo.currency)}</span>
              <button type="button" onClick={() => { setSelectedPo(null); setPoSearch(''); }} className="text-gray-400 hover:text-red-500 text-xs leading-none ml-1">✕</button>
            </div>
          ) : (
            <>
              <input className="form-input" placeholder="Search by PO number, supplier, or SOW…"
                value={poSearch}
                onChange={e => { setPoSearch(e.target.value); setShowDropdown(true); }}
                onFocus={() => setShowDropdown(true)}
                onBlur={() => setTimeout(() => setShowDropdown(false), 150)} />
              {showDropdown && filteredPos.length > 0 && (
                <div className="absolute z-50 left-0 right-0 top-full mt-0.5 bg-white border border-gray-200 rounded shadow-lg max-h-44 overflow-y-auto">
                  {filteredPos.map(p => {
                    // SOW pricing is in sterling. A PO raised in EUR/USD holds
                    // unconverted amounts, so passing it through at face value
                    // would put a foreign figure into a GBP line — block it and
                    // let the cost be entered manually at the agreed rate.
                    const poCur = (p.currency || 'GBP').toUpperCase();
                    const foreign = poCur !== 'GBP';
                    return (
                      <button key={p.id} type="button" disabled={foreign}
                        title={foreign ? `This PO is in ${poCur}. Add the converted GBP cost as a manual line instead.` : undefined}
                        className={`w-full text-left px-3 py-2 text-[10px] flex items-center gap-2 border-b border-gray-50 ${foreign ? 'opacity-50 cursor-not-allowed bg-gray-50' : 'hover:bg-blue-50'}`}
                        onMouseDown={() => { if (foreign) return; setSelectedPo(p); setPoSearch(''); setShowDropdown(false); }}>
                        <span className="font-mono font-bold text-navy whitespace-nowrap">{p.po_number}</span>
                        <span className="text-gray-500 truncate">{p.supplier_name || '—'}</span>
                        {p.sow_reference && <span className="text-gray-400 text-[8px] whitespace-nowrap">{p.sow_reference}</span>}
                        {foreign && <span className="text-[8px] font-bold text-amber-600 whitespace-nowrap">{poCur}, not GBP</span>}
                        <span className="ml-auto font-semibold text-navy whitespace-nowrap">{formatMoney(p.subtotal || 0, 2, poCur)}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </>
          )}
        </div>

        {selectedPo && (
          <>
            {/* Markup */}
            <div>
              <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Pass-through Markup</label>
              <div className="flex gap-2 mb-1.5">
                {[['pct', '% Percentage'], ['flat', '£ Flat amount']].map(([v, lbl]) => (
                  <label key={v} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded border cursor-pointer text-[9px] flex-1 ${markupType === v ? 'border-gold bg-gold/5 font-semibold' : 'border-gray-200 text-gray-500 hover:border-gray-400'}`}>
                    <input type="radio" className="sr-only" checked={markupType === v} onChange={() => { setMarkupType(v); setMarkupValue(''); }} />
                    <span className={`w-2.5 h-2.5 rounded-full border-2 flex-shrink-0 ${markupType === v ? 'border-gold bg-gold' : 'border-gray-300'}`} />
                    {lbl}
                  </label>
                ))}
              </div>
              <input className="form-input" type="number" step="0.01" min="0"
                placeholder={markupType === 'pct' ? 'e.g. 5 for 5%' : 'e.g. 250.00'}
                value={markupValue}
                onChange={e => setMarkupValue(e.target.value)} />
            </div>

            {/* Summary */}
            <div className="bg-gray-50 border border-gray-100 rounded p-3 grid grid-cols-3 gap-3 text-center">
              <div>
                <div className="text-[7px] font-bold text-gray-400 uppercase mb-0.5">PO Cost</div>
                <div className="text-[12px] font-extrabold text-navy">{formatMoney(poTotal)}</div>
              </div>
              <div>
                <div className="text-[7px] font-bold text-gray-400 uppercase mb-0.5">Markup</div>
                <div className="text-[12px] font-extrabold text-amber-600">
                  {markupType === 'pct' ? `${markup}%` : formatMoney(markup)}
                </div>
              </div>
              <div>
                <div className="text-[7px] font-bold text-gray-400 uppercase mb-0.5">Sell Amount</div>
                <div className="text-[12px] font-extrabold text-emerald-700">{formatMoney(sellAmount)}</div>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

function AddHardwareModal({ onClose, onSave }) {
  const [mode, setMode]                 = useState('product'); // 'product' | 'bundle'
  const [products, setProducts]         = useState([]);
  const [categories, setCategories]     = useState([]);
  const [stockMap, setStockMap]         = useState({});
  const [incomingMap, setIncomingMap]   = useState({}); // product_catalog_id → [{source_po_id, source_po_number, expected_delivery, incoming_count}]
  const [catFilter, setCatFilter]       = useState('');
  const [search, setSearch]             = useState('');
  const [selected, setSelected]         = useState(null);
  const [qty, setQty]                   = useState(1);
  const [sellPrice, setSellPrice]       = useState('');
  // allocSource: null (no allocation), 'stock' (use in_stock), or the batch object {source_po_id, source_po_number, expected_delivery}
  const [allocSource, setAllocSource]   = useState(null);
  const [saving, setSaving]             = useState(false);
  // Bundle mode state
  const [bundles, setBundles]           = useState([]);
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [bundleItems, setBundleItems]   = useState([]);
  const [bundleMarkup, setBundleMarkup] = useState(20);
  const [editedCosts, setEditedCosts]   = useState({});   // item.id → number (overrides null/0 unit_cost)
  const [editedQtys, setEditedQtys]     = useState({});   // item.id → number (overrides bundle default qty)
  const [bundleQty, setBundleQty]       = useState(1);
  const [saveToCatalog, setSaveToCatalog] = useState(false);

  useEffect(() => {
    const params = {};
    if (catFilter) params.category = catFilter;
    if (search)    params.search   = search;
    api.get('product-catalog', params).then(data => {
      // Resource-category rows are old rate-card-era seed data, not real
      // hardware — resource pricing comes from rate_card_bands via the
      // Resources tab, never from here.
      const hw = (data || []).filter(p => p.category !== 'Resource');
      setProducts(hw);
      if (!catFilter) setCategories([...new Set(hw.map(p => p.category))].sort());
    });
  }, [catFilter, search]);

  // Load in-stock counts and incoming batches keyed by product_catalog_id
  useEffect(() => {
    api.get('inventory/stock-by-catalog').then(data => {
      const m = {};
      (data || []).forEach(r => { m[r.product_catalog_id] = r.in_stock_count; });
      setStockMap(m);
    }).catch(() => {});
    api.get('inventory/incoming-by-catalog').then(data => {
      const m = {};
      (data || []).forEach(r => {
        if (!m[r.product_catalog_id]) m[r.product_catalog_id] = [];
        m[r.product_catalog_id].push(r);
      });
      setIncomingMap(m);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (mode === 'bundle' && bundles.length === 0) {
      api.get('product-bundles').then(r => setBundles((r || []).filter(b => b.item_count > 0))).catch(() => {});
    }
  }, [mode]);

  const selectBundle = async (b) => {
    setSelectedBundle(b);
    setBundleItems([]);
    setEditedCosts({});
    setEditedQtys({});
    setBundleQty(1);
    setSaveToCatalog(false);
    const full = await api.get(`product-bundles/${b.id}`).catch(() => null);
    setBundleItems(full?.items || []);
  };

  const getCatalogCost = (item) => item.min_price ?? item.unit_cost ?? 0;
  const getCatalogSell = (item) => (item.sell_price != null && item.sell_price > 0) ? item.sell_price : null;

  const getEffectiveCost = (item) =>
    editedCosts[item.id] !== undefined ? editedCosts[item.id] : getCatalogCost(item);

  const getEffectiveQty = (item) =>
    editedQtys[item.id] !== undefined ? editedQtys[item.id] : item.quantity;

  const bundleHasCatalogPrices = bundleItems.length > 0 && bundleItems.every(i => getCatalogSell(i) != null);

  const bundleTotalCost = bundleItems.reduce((s, item) => s + getEffectiveCost(item) * getEffectiveQty(item), 0);
  const bundleTotalSell = bundleHasCatalogPrices
    ? bundleItems.reduce((s, item) => s + getCatalogSell(item) * getEffectiveQty(item), 0)
    : bundleTotalCost * (1 + bundleMarkup / 100);
  const bundleMarginPct = bundleTotalCost > 0 ? ((bundleTotalSell - bundleTotalCost) / bundleTotalCost) * 100 : 0;

  const hasZeroCostItems = bundleItems.some(item => !getCatalogCost(item) && editedCosts[item.id] === undefined);
  const hasEditedCosts   = Object.keys(editedCosts).length > 0;

  const handleSave = async () => {
    if (mode === 'bundle') {
      if (!selectedBundle) return;
      setSaving(true);
      if (saveToCatalog && hasEditedCosts && canAccess('product-catalog')) {
        await Promise.all(
          Object.entries(editedCosts).map(([itemId, cost]) => {
            const item = bundleItems.find(i => i.id === itemId);
            return cost > 0 && item
              ? api.put(`product-catalog/${item.product_catalog_id}`, { unit_cost: cost })
              : Promise.resolve();
          })
        );
      }
      await onSave({
        item_description: selectedBundle.name,
        bundle_id: selectedBundle.id,
        unit_cost: parseFloat(bundleTotalCost.toFixed(2)),
        unit_sell: parseFloat(bundleTotalSell.toFixed(2)),
        quantity: bundleQty,
        category: 'Bundle',
        bundle_items_json: JSON.stringify(bundleItems.map(item => ({
          product_catalog_id: item.product_catalog_id,
          quantity: getEffectiveQty(item),
        }))),
      });
      setSaving(false);
      return;
    }
    if (!selected) return;
    setSaving(true);
    const cost = Math.round(selectedCost * 100) / 100;
    const payload = {
      item_description: selected.description,
      part_number: selected.code,
      quantity: qty,
      unit_cost: cost,
      unit_sell: Math.round((parseFloat(sellPrice) || cost) * 100) / 100,
      category: selected.category,
      product_catalog_id: selected.id,
    };
    if (allocSource === 'stock') {
      payload.use_inventory = true;
    } else if (allocSource?.source_po_id) {
      payload.allocate_from_po_id = allocSource.source_po_id;
    }
    await onSave(payload);
    setSaving(false);
  };

  const stockCount      = selected ? (stockMap[selected.id] || 0) : 0;
  const incomingBatches = selected ? (incomingMap[selected.id] || []) : [];
  const hasAnyStock     = stockCount > 0 || incomingBatches.length > 0;
  // min_price (real current supplier cost) over the stale unit_cost seed
  // field — same preference used everywhere else this is computed.
  const selectedCost    = selected ? (selected.min_price ?? selected.unit_cost ?? 0) : 0;

  const selectProduct = (p) => {
    setSelected(p);
    const cost = p.min_price ?? p.unit_cost ?? 0;
    // Prefer the catalogue's own agreed sell price over a generic 20%
    // markup guess — a real sell_price already reflects what's actually
    // been quoted for this item.
    setSellPrice(p.sell_price != null && p.sell_price > 0 ? Number(p.sell_price).toFixed(2) : (cost * 1.2).toFixed(2));
    setAllocSource(null);
  };

  return (
    <Modal title="Add Hardware Item" onClose={onClose} wide footer={
      <><button onClick={onClose} className="px-3 py-1 text-[10px] border border-gray-200 rounded bg-white">Cancel</button>
        <button onClick={handleSave} disabled={(mode === 'product' ? !selected : !selectedBundle) || saving} className="px-3 py-1 text-[10px] font-semibold bg-navy text-white rounded disabled:opacity-40">{saving ? 'Adding...' : 'Add to SOW'}</button></>
    }>
      {/* Mode toggle */}
      <div className="flex gap-1 mb-3 border-b border-gray-100 pb-3">
        {[['product','Product'], ['bundle','⊛ Bundle']].map(([m, label]) => (
          <button key={m} type="button"
            onClick={() => setMode(m)}
            className={`px-3 py-1 text-[9px] font-semibold rounded border transition-colors ${mode === m ? 'bg-navy text-white border-navy' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
          >{label}</button>
        ))}
      </div>

      {mode === 'bundle' ? (
        <div className="grid grid-cols-2 gap-4">
          {/* Left: bundle list */}
          <div>
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Select Bundle</label>
            <div className="border border-gray-200 rounded max-h-[280px] overflow-y-auto">
              {bundles.length === 0 && <div className="text-center py-6 text-[9px] text-gray-400">No bundles defined — create some in Product Catalog → ⊛ Bundles</div>}
              {bundles.map(b => (
                <div key={b.id} onClick={() => selectBundle(b)}
                  className={`px-2 py-2 border-b border-gray-100 cursor-pointer hover:bg-violet-50 ${selectedBundle?.id === b.id ? 'bg-violet-100 border-l-2 border-l-violet-500' : ''}`}>
                  <div className="text-[9px] font-semibold text-gray-800">{b.name}</div>
                  {b.description && <div className="text-[8px] text-gray-400">{b.description}</div>}
                  <div className="text-[7.5px] text-gray-400 mt-0.5">{b.item_count} item{b.item_count !== 1 ? 's' : ''}</div>
                </div>
              ))}
            </div>
          </div>
          {/* Right: bundle details + pricing */}
          <div>
            {selectedBundle ? (
              <div className="space-y-3">
                <div className="bg-violet-50 border border-violet-200 rounded p-2">
                  <div className="text-[9px] font-bold text-violet-800 mb-1">{selectedBundle.name}</div>
                  {bundleItems.length === 0 ? (
                    <div className="text-[8px] text-gray-400">Loading…</div>
                  ) : (
                    <>
                    {hasZeroCostItems && (
                      <div className="flex items-start gap-1 bg-amber-50 border border-amber-200 rounded px-2 py-1 mb-2 text-[7.5px] text-amber-800">
                        <span className="mt-0.5">⚠</span>
                        <span>Some items have no cost set. Enter prices below — they'll be used for this SOW line.</span>
                      </div>
                    )}
                    <table className="w-full text-[8px] mb-2">
                      <thead><tr>
                        <th className="text-left text-[7px] uppercase text-gray-400 font-bold pb-0.5">Item</th>
                        <th className="text-center text-[7px] uppercase text-gray-400 font-bold pb-0.5 w-10">Qty/bundle</th>
                        <th className="text-right text-[7px] uppercase text-gray-400 font-bold pb-0.5 w-16">Buy</th>
                        <th className="text-right text-[7px] uppercase text-gray-400 font-bold pb-0.5 w-16">SOW Sell</th>
                      </tr></thead>
                      <tbody>
                        {bundleItems.map(item => {
                          const catalogCost = getCatalogCost(item);
                          const catalogSell = getCatalogSell(item);
                          const noCost = !catalogCost && editedCosts[item.id] === undefined;
                          const effectiveQty = getEffectiveQty(item);
                          const priceUpdated = item.price_last_modified
                            ? new Date(item.price_last_modified).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'2-digit' })
                            : null;
                          return (
                            <tr key={item.id} className={`border-b border-violet-100 last:border-0 ${item.is_optional ? 'opacity-70' : ''}`}>
                              <td className="py-1 pr-1">
                                <div className="font-mono text-[7.5px] text-navy font-bold">{item.code}</div>
                                <div className="text-[8px] truncate max-w-[120px]">{item.description}</div>
                                {item.manufacturer && <div className="text-[7px] text-gray-400">{item.manufacturer}</div>}
                                <div className="flex items-center gap-1 mt-0.5 flex-wrap">
                                  {priceUpdated && <span className="text-[6.5px] text-gray-400">updated {priceUpdated}</span>}
                                  {!!item.is_optional && <span className="text-[6.5px] text-gray-400 italic">optional</span>}
                                </div>
                              </td>
                              <td className="text-center py-1">
                                <input
                                  type="number"
                                  min="1"
                                  step="1"
                                  value={effectiveQty}
                                  onChange={e => {
                                    const v = Math.max(1, parseInt(e.target.value) || 1);
                                    setEditedQtys(prev => ({ ...prev, [item.id]: v }));
                                  }}
                                  className="w-10 text-center text-[8px] border border-gray-200 rounded px-1 py-0.5 focus:outline-none focus:border-navy"
                                />
                              </td>
                              <td className="text-right py-1">
                                {noCost ? (
                                  <input
                                    type="number"
                                    min="0"
                                    step="0.01"
                                    placeholder="0.00"
                                    value={editedCosts[item.id] !== undefined ? editedCosts[item.id] : ''}
                                    onChange={e => {
                                      const v = e.target.value === '' ? undefined : parseFloat(e.target.value) || 0;
                                      setEditedCosts(prev => {
                                        const next = { ...prev };
                                        if (v === undefined) delete next[item.id];
                                        else next[item.id] = v;
                                        return next;
                                      });
                                    }}
                                    className="w-14 text-right text-[8px] border border-amber-300 bg-amber-50 rounded px-1 py-0.5 focus:outline-none focus:border-amber-500"
                                  />
                                ) : (
                                  <span className="text-gray-600">{`£${catalogCost.toFixed(2)}`}</span>
                                )}
                              </td>
                              <td className="text-right py-1 font-semibold">
                                {catalogSell != null
                                  ? <span className="text-navy">{`£${(catalogSell * effectiveQty).toFixed(2)}`}</span>
                                  : <span className="text-gray-400 text-[7px]">no price</span>}
                              </td>
                            </tr>
                          );
                        })}
                        <tr className="border-t border-violet-200 font-bold">
                          <td colSpan={2} className="text-right pt-1 text-violet-700 text-[7px]">Totals</td>
                          <td className="text-right pt-1 text-gray-600">£{bundleTotalCost.toFixed(2)}</td>
                          <td className="text-right pt-1 text-navy">
                            {bundleHasCatalogPrices ? `£${bundleTotalSell.toFixed(2)}` : '—'}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                    {/* Writing a cost back to the catalogue is the Product
                        Catalog page's to do, so only its holders are offered it. */}
                    {hasEditedCosts && canAccess('product-catalog') && (
                      <label className="flex items-center gap-1.5 text-[8px] text-gray-700 cursor-pointer select-none">
                        <input type="checkbox" checked={saveToCatalog} onChange={e => setSaveToCatalog(e.target.checked)} className="rounded" />
                        <span>Save entered prices to product catalog for future use</span>
                      </label>
                    )}
                    </>
                  )}
                </div>
                {!bundleHasCatalogPrices && (
                  <div>
                    <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Markup %</label>
                    <input className="form-input" type="number" step="0.1" min="0" value={bundleMarkup} onChange={e => setBundleMarkup(parseFloat(e.target.value) || 0)} />
                  </div>
                )}
                <div className={`rounded p-2 text-[9px] space-y-1 ${bundleTotalSell < bundleTotalCost ? 'bg-red-50 border border-red-200' : 'bg-gray-50'}`}>
                  <div className="flex justify-between"><span className="text-gray-500">Total buy (cost)</span><span className="font-semibold">£{bundleTotalCost.toFixed(2)}</span></div>
                  {bundleHasCatalogPrices ? (
                    <div className="flex justify-between text-[8px] text-gray-400 italic">
                      <span>Sell prices from product catalog</span>
                    </div>
                  ) : (
                    <div className="flex justify-between">
                      <span className="text-gray-500">Markup ({bundleMarkup}%)</span>
                      <span className={`font-semibold ${bundleTotalSell < bundleTotalCost ? 'text-red-600' : 'text-gold'}`}>{bundleTotalSell >= bundleTotalCost ? '+' : ''}£{(bundleTotalSell - bundleTotalCost).toFixed(2)}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-t border-gray-200 pt-1">
                    <span className="font-bold">SOW sell price</span>
                    <span className={`font-extrabold ${bundleTotalSell < bundleTotalCost ? 'text-red-600' : 'text-navy'}`}>£{bundleTotalSell.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-[8px]">
                    <span className="text-gray-500">Margin</span>
                    <span className={`font-semibold ${bundleMarginPct < 0 ? 'text-red-600' : 'text-emerald-600'}`}>{bundleMarginPct.toFixed(1)}%</span>
                  </div>
                  {bundleTotalSell < bundleTotalCost && (
                    <div className="text-[7.5px] text-red-600 font-semibold pt-0.5">⚠ Selling below cost — adjust prices to avoid a loss</div>
                  )}
                </div>
                <div>
                  <label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Number of bundles</label>
                  <input className="form-input" type="number" min="1" value={bundleQty} onChange={e => setBundleQty(Math.max(1, parseInt(e.target.value) || 1))} />
                  {bundleQty > 1 && (
                    <div className="text-[7.5px] text-gray-500 mt-0.5">Total: £{(bundleTotalSell * bundleQty).toFixed(2)}</div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-gray-50 border border-gray-200 rounded p-3 text-[9px] text-gray-400 text-center h-full flex items-center justify-center">Select a bundle from the list</div>
            )}
          </div>
        </div>
      ) : (
      <div className="grid grid-cols-3 gap-3">
        <div className="col-span-2">
          {/* Prominent search field */}
          <div className="mb-2">
            <label className="block text-[8px] font-bold text-gray-500 uppercase mb-1">Search Product Catalogue</label>
            <input
              className="form-input w-full text-[11px] py-1.5"
              placeholder="🔍 Type to search by name, part code or category..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              autoFocus
            />
          </div>
          <div className="flex gap-2 mb-2">
            <label className="text-[8px] font-bold text-gray-500 uppercase self-center">Filter:</label>
            <select className="form-select flex-1" value={catFilter} onChange={e => setCatFilter(e.target.value)}>
              <option value="">All categories</option>
              {categories.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
            <span className="text-[8px] text-gray-400 self-center">{products.length} items</span>
          </div>
          <div className="border border-gray-200 rounded max-h-[260px] overflow-y-auto">
            {products.map(p => {
              const inStock   = stockMap[p.id] || 0;
              const incoming  = incomingMap[p.id] || [];
              const totalIncoming = incoming.reduce((s, b) => s + b.incoming_count, 0);
              return (
                <div key={p.id} onClick={() => selectProduct(p)} className={`flex items-center justify-between px-2 py-1.5 border-b border-gray-100 cursor-pointer hover:bg-gold/5 ${selected?.id === p.id ? 'bg-gold/10 border-l-2 border-l-gold' : ''}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-[9px] font-mono text-navy">{p.code}</div>
                    <div className="text-[10px] font-medium truncate">{p.description}</div>
                    <div className="text-[8px] text-gray-400">{p.category}</div>
                  </div>
                  <div className="text-right ml-2 flex-shrink-0 flex flex-col items-end gap-0.5">
                    <div className="text-[10px] font-bold">
                      {p.min_price != null
                        ? (p.min_price === p.max_price ? `£${Number(p.min_price).toFixed(2)}` : `£${Number(p.min_price).toFixed(2)}–£${Number(p.max_price).toFixed(2)}`)
                        : (p.unit_cost != null ? `£${Number(p.unit_cost).toFixed(2)}` : '—')}
                    </div>
                    {inStock > 0
                      ? <span className="text-[7px] font-bold px-1 py-0.5 bg-emerald-100 text-emerald-700 rounded">✓ {inStock} in stock</span>
                      : totalIncoming === 0
                        ? <span className="text-[7px] font-bold px-1 py-0.5 bg-red-100 text-red-600 rounded">✗ no stock</span>
                        : null}
                    {totalIncoming > 0 && <span className="text-[7px] font-bold px-1 py-0.5 bg-amber-100 text-amber-700 rounded">◷ {totalIncoming} incoming</span>}
                  </div>
                </div>
              );
            })}
            {products.length === 0 && <div className="text-center py-6 text-[10px] text-gray-400">No products found — try a different search term</div>}
          </div>
        </div>
        <div>
          <div className="text-[8px] font-bold text-gray-500 uppercase mb-2">Selected Item</div>
          {selected ? (
            <div className="bg-gold/5 border border-gold/20 rounded p-2 mb-3">
              <div className="text-[8px] font-mono text-navy mb-1">{selected.code}</div>
              <div className="text-[9px] font-semibold mb-1">{selected.description}</div>
              <div className="text-[8px] text-gray-500 mb-2">Cost: £{Number(selectedCost).toFixed(2)}</div>

              {/* The PMO's supply note; this panel draws its own stock below */}
              <div className="mb-2">
                <ProductSupplyNote productId={selected.id} showStock={false} canWrite={canAccess('product-notes:write')} />
              </div>

              {/* Allocation source selector — always shown */}
              <div className="border-t border-gold/20 pt-1.5">
                <div className="text-[8px] font-bold text-gray-500 uppercase mb-1">Stock Allocation</div>

                {/* OUT OF STOCK banner */}
                {!hasAnyStock && (
                  <div className="mb-1.5 p-1.5 bg-red-50 border border-red-200 rounded flex items-start gap-1.5">
                    <span className="text-red-500 text-[11px] mt-0.5 leading-none">⚠</span>
                    <div>
                      <div className="text-[8px] font-bold text-red-700">No stock available</div>
                      <div className="text-[7.5px] text-red-600">This item has no stock in hand or on order. It will be flagged for the PMO to raise a purchase order.</div>
                    </div>
                  </div>
                )}

                {/* No allocation / needs ordering option */}
                <label className="flex items-start gap-1.5 mb-1 cursor-pointer p-1 rounded hover:bg-white/50">
                  <input type="radio" name="allocSource" checked={allocSource === null} onChange={() => setAllocSource(null)} className="mt-0.5 w-3 h-3" />
                  <span className="text-[8px] text-gray-500">
                    {hasAnyStock ? "Don't allocate — add to SOW only" : '⚠ Add without stock — will need ordering'}
                  </span>
                </label>

                {/* In-stock option */}
                {stockCount > 0 && (
                  <label className="flex items-start gap-1.5 mb-1 cursor-pointer p-1 rounded hover:bg-emerald-50">
                    <input type="radio" name="allocSource" checked={allocSource === 'stock'} onChange={() => setAllocSource('stock')} className="mt-0.5 w-3 h-3" />
                    <div>
                      <span className="text-[8px] font-semibold text-emerald-700">✓ {stockCount} available in stock — allocate now</span>
                      {allocSource === 'stock' && qty > stockCount && (
                        <div className="text-[7px] text-amber-600 mt-0.5">⚠ Only {stockCount} available — {qty - stockCount} unit{qty - stockCount !== 1 ? 's' : ''} will not be allocated</div>
                      )}
                    </div>
                  </label>
                )}

                {/* Incoming batch options */}
                {incomingBatches.map(batch => (
                  <label key={batch.source_po_id} className="flex items-start gap-1.5 mb-1 cursor-pointer p-1 rounded hover:bg-amber-50">
                    <input
                      type="radio"
                      name="allocSource"
                      checked={allocSource?.source_po_id === batch.source_po_id}
                      onChange={() => setAllocSource(batch)}
                      className="mt-0.5 w-3 h-3"
                    />
                    <div>
                      <div className="text-[8px] font-semibold text-amber-700">◷ {batch.incoming_count} on order — pre-allocate</div>
                      <div className="text-[7.5px] text-gray-400 italic">Not yet received · reserved when PO arrives</div>
                      <div className="text-[7px] text-gray-500 font-mono">{batch.source_po_number}</div>
                      {batch.expected_delivery && <div className="text-[7px] text-amber-600">ETA: {batch.expected_delivery}</div>}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          ) : <div className="bg-gray-50 border border-gray-200 rounded p-2 mb-3 text-[9px] text-gray-400 text-center">Select a product from the list</div>}

          <div className="grid gap-2">
            <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Quantity</label><input className="form-input" type="number" min="1" value={qty} onChange={e => setQty(parseInt(e.target.value) || 1)} /></div>
            <div><label className="block text-[8px] font-bold text-gray-500 uppercase mb-0.5">Sell Price (£)</label><input className="form-input" type="number" step="0.01" value={sellPrice} onChange={e => setSellPrice(e.target.value)} /></div>
            {selected && sellPrice && (
              <div className="bg-gray-50 rounded p-2">
                <div className="text-[8px] text-gray-500">Total: <span className="font-bold text-navy">£{(qty * parseFloat(sellPrice || 0)).toFixed(2)}</span></div>
                <div className="text-[8px] text-gray-500">Margin: <span className="font-bold text-emerald-600">{(((parseFloat(sellPrice) - selectedCost) / parseFloat(sellPrice)) * 100).toFixed(1)}%</span></div>
              </div>
            )}
          </div>
        </div>
      </div>
      )}
    </Modal>
  );
}

// ── Hardware Items Modal ──────────────────────────────────────────────────────
// Shows all individual inventory items allocated to a hardware line (qty > 1).
function HardwareItemsModal({ line, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('inventory', { sow_hardware_line_id: line.id, limit: 200 })
      .then(res => { setItems(res.items || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [line.id]);

  const STATUS_LABELS = { incoming: 'Pre-order', in_stock: 'In Stock', allocated: 'Allocated', deployed: 'Deployed', decommissioned: 'Decommissioned' };
  const STATUS_COLORS = {
    incoming:       'bg-amber-100 text-amber-700',
    in_stock:       'bg-emerald-100 text-emerald-700',
    allocated:      'bg-blue-100 text-blue-700',
    deployed:       'bg-navy/10 text-navy',
    decommissioned: 'bg-gray-100 text-gray-500',
  };

  return (
    <Modal title={`Allocated Items — ${line.item_description}`} onClose={onClose} wide>
      <div className="text-[9px] text-gray-500 mb-3">
        {line.quantity} unit{line.quantity !== 1 ? 's' : ''} allocated to this line · full stock traceability
      </div>
      {loading ? (
        <div className="py-6 text-center text-[10px] text-gray-400">Loading…</div>
      ) : items.length === 0 ? (
        <div className="py-6 text-center text-[10px] text-gray-400">No inventory records found for this line.</div>
      ) : (
        <table className="w-full text-[9px]">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              <th className="px-2 py-1.5 text-left font-bold text-gray-500 uppercase text-[8px]">Asset Tag</th>
              <th className="px-2 py-1.5 text-left font-bold text-gray-500 uppercase text-[8px]">Serial Number</th>
              <th className="px-2 py-1.5 text-left font-bold text-gray-500 uppercase text-[8px]">Status</th>
              <th className="px-2 py-1.5 text-left font-bold text-gray-500 uppercase text-[8px]">Location</th>
              <th className="px-2 py-1.5 text-left font-bold text-gray-500 uppercase text-[8px]">ETA / Delivery</th>
              <th className="px-2 py-1.5 text-right font-bold text-gray-500 uppercase text-[8px]">Purchase Price</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <tr key={item.id} className={`border-b border-gray-100 ${idx % 2 === 0 ? '' : 'bg-gray-50/50'}`}>
                <td className="px-2 py-1.5 font-mono text-navy font-semibold">{item.asset_tag || '—'}</td>
                <td className="px-2 py-1.5 font-mono text-gray-600">{item.serial_number || <span className="text-gray-300 italic">not set</span>}</td>
                <td className="px-2 py-1.5">
                  <span className={`px-1.5 py-0.5 rounded text-[7px] font-bold uppercase tracking-wide ${STATUS_COLORS[item.status] || 'bg-gray-100 text-gray-500'}`}>
                    {STATUS_LABELS[item.status] || item.status}
                  </span>
                </td>
                <td className="px-2 py-1.5 text-gray-600">{item.location_name || '—'}</td>
                <td className="px-2 py-1.5 text-gray-600">
                  {item.po_expected_delivery
                    ? <span className="text-amber-700 font-semibold">{item.po_expected_delivery}</span>
                    : item.status === 'incoming' ? <span className="text-gray-400 italic">TBC</span> : '—'}
                </td>
                <td className="px-2 py-1.5 text-right text-gray-700">
                  {item.purchase_price != null ? `£${Number(item.purchase_price).toFixed(2)}` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
          {items.length > 0 && (
            <tfoot>
              <tr className="bg-gray-50 border-t border-gray-200">
                <td colSpan="5" className="px-2 py-1.5 text-[8px] font-bold text-gray-500 text-right">Total Purchase Value</td>
                <td className="px-2 py-1.5 text-right font-extrabold text-navy text-[9px]">
                  {items.every(i => i.purchase_price == null) ? '—' : `£${items.reduce((s, i) => s + (Number(i.purchase_price) || 0), 0).toFixed(2)}`}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      )}
    </Modal>
  );
}
